// @flow
// CODEGEN FILE - DO NOT EDIT
// All manual edits in this file will be destroyed.
//
// To create this file, run yarn gen:stripe_clui from the api-codegen folder.

import type {Commands} from 'docs/components/contrib/StripeClui/helpers/commands/resource';

export const tree: Commands = {
  root: {
    type: 'command',
    commands: {
      stripe: {
        type: 'command',
        params: {
          data: {
            description: 'Data for the API request.',
            shorthand: 'd',
            repeatable: true,
            type: 'param',
          },
          help: {
            description: 'Get help on the current commands.',
            shorthand: 'h',
            type: 'param',
          },
          'stripe-account': {
            description: 'Set a header identifying the connected account.',
            type: 'param',
          },
          'stripe-version': {
            description: 'Set the Stripe API version to use for your request.',
            type: 'param',
          },
        },
        commands: {
          listen: {
            type: 'command',
            description:
              "The listen command watches and forwards webhook events from Stripe to your local machine by connecting directly to Stripe's API. You can test the latest API version, filter events, or even load your saved webhook endpoints from your Stripe account.",
            params: {
              latest: {
                type: 'param',
                shorthand: 'l',
                no_value: true,
                description:
                  "Receive events formatted with the latest API version (default: your account's default API version)",
              },
            },
            args: [],
            path: '/v1/stripecli/sessions',
            method: 'post',
            run: true,
          },
          trigger: {
            type: 'command',
            description:
              'Trigger specific webhook events to be sent. Webhooks events created through the trigger command will also create all necessary side-effect events that are needed to create the triggered event as well as the corresponding API objects.',
            params: {},
            args: ['event'],
            path: 'NA',
            method: 'NA',
            run: true,
          },
          accounts: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves the details of an account.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates a connected account by setting the values of the parameters passed. Any parameters not provided are left unchanged. Most parameters can be changed only for Custom accounts. (These are marked Custom Only below.) Parameters marked Custom and Express are not supported for Standard accounts.  To update your own account, use the Dashboard. Refer to our Connect documentation to learn more about updating accounts.',
                params: {
                  'account-token': {
                    type: 'param',
                    description:
                      'An account token, used to securely provide details to the account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'business-profile': {
                    type: 'param',
                    description: 'Business information about the account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'business-type': {
                    type: 'param',
                    description: 'The business type.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'company',
                        'government_entity',
                        'individual',
                        'non_profit',
                      ],
                    },
                  },
                  capabilities: {
                    type: 'param',
                    description:
                      'Each key of the dictionary represents a capability, and each capability maps to its settings (e.g. whether it has been requested or not). Each capability will be inactive until you have provided its specific requirements and Stripe has verified them. An account may have some of its requested capabilities be active and some be inactive.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  company: {
                    type: 'param',
                    description:
                      'Information about the company or business. This field is available for any `business_type`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'default-currency': {
                    type: 'param',
                    description:
                      "Three-letter ISO currency code representing the default currency for the account. This must be a currency that Stripe supports in the account's country.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  documents: {
                    type: 'param',
                    description:
                      'Documents that may be submitted to satisfy various informational requests.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      'The email address of the account holder. This is only to make the account easier to identify to you. Stripe only emails Custom accounts with your consent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'external-account': {
                    type: 'param',
                    description:
                      'A card or bank account to attach to the account for receiving payouts (you won’t be able to use it for top-ups). You can provide either a token, like the ones returned by Stripe.js, or a dictionary, as documented in the `external-account` parameter for bank account creation. By default, providing an external account sets it as the new default external account for its currency, and deletes the old default if one exists. To add additional external accounts without replacing the existing default for the currency, use the bank account or card creation API.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  individual: {
                    type: 'param',
                    description:
                      'Information about the person represented by the account. This field is null unless `business_type` is set to `individual`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  settings: {
                    type: 'param',
                    description:
                      'Options for customizing how the account functions within Stripe.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tos-acceptance': {
                    type: 'param',
                    description:
                      "Details on the account's acceptance of the Stripe Services Agreement.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'With Connect, you can delete accounts you manage.  Accounts created using test-mode keys can be deleted at any time. Standard accounts created using live-mode keys cannot be deleted. Custom or Express accounts created using live-mode keys can only be deleted once all balances are zero.  If you want to delete your own account, use the account information tab in your account settings instead.',
                params: {},
                args: ['account'],
                path: '/v1/accounts/{account}',
                method: 'delete',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of accounts connected to your platform via Connect. If you’re not a platform, the list is empty.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/accounts',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'With Connect, you can create Stripe accounts for your users. To do this, you’ll first need to register your platform.',
                params: {
                  'account-token': {
                    type: 'param',
                    description:
                      'An account token, used to securely provide details to the account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'business-profile': {
                    type: 'param',
                    description: 'Business information about the account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'business-type': {
                    type: 'param',
                    description: 'The business type.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'company',
                        'government_entity',
                        'individual',
                        'non_profit',
                      ],
                    },
                  },
                  capabilities: {
                    type: 'param',
                    description:
                      'Each key of the dictionary represents a capability, and each capability maps to its settings (e.g. whether it has been requested or not). Each capability will be inactive until you have provided its specific requirements and Stripe has verified them. An account may have some of its requested capabilities be active and some be inactive.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  company: {
                    type: 'param',
                    description:
                      'Information about the company or business. This field is available for any `business_type`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  country: {
                    type: 'param',
                    description:
                      "The country in which the account holder resides, or in which the business is legally established. This should be an ISO 3166-1 alpha-2 country code. For example, if you are in the United States and the business for which you're creating an account is legally represented in Canada, you would use `CA` as the country for the account being created.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-currency': {
                    type: 'param',
                    description:
                      "Three-letter ISO currency code representing the default currency for the account. This must be a currency that Stripe supports in the account's country.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  documents: {
                    type: 'param',
                    description:
                      'Documents that may be submitted to satisfy various informational requests.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      'The email address of the account holder. This is only to make the account easier to identify to you. Stripe only emails Custom accounts with your consent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'external-account': {
                    type: 'param',
                    description:
                      'A card or bank account to attach to the account for receiving payouts (you won’t be able to use it for top-ups). You can provide either a token, like the ones returned by Stripe.js, or a dictionary, as documented in the `external-account` parameter for bank account creation. By default, providing an external account sets it as the new default external account for its currency, and deletes the old default if one exists. To add additional external accounts without replacing the existing default for the currency, use the bank account or card creation API.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  individual: {
                    type: 'param',
                    description:
                      'Information about the person represented by the account. This field is null unless `business_type` is set to `individual`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  settings: {
                    type: 'param',
                    description:
                      'Options for customizing how the account functions within Stripe.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tos-acceptance': {
                    type: 'param',
                    description:
                      "Details on the account's acceptance of the Stripe Services Agreement.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The type of Stripe account to create. May be one of `custom`, `express` or `standard`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['custom', 'express', 'standard'],
                    },
                  },
                },
                args: [],
                path: '/v1/accounts',
                method: 'post',
                run: true,
              },
              reject: {
                type: 'command',
                description:
                  'With Connect, you may flag accounts as suspicious.  Test-mode Custom and Express accounts can be rejected at any time. Accounts created using live-mode keys may only be rejected once all balances are zero.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  reason: {
                    type: 'param',
                    description:
                      'The reason for rejecting the account. Can be `fraud`, `terms_of_service`, or `other`.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/reject',
                method: 'post',
                run: true,
              },
              capabilities: {
                type: 'command',
                description:
                  'Returns a list of capabilities associated with the account. The capabilities are returned sorted by creation date, with the most recent capability appearing first.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/capabilities',
                method: 'get',
                run: true,
              },
            },
          },
          account_links: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates an AccountLink object that includes a single-use Stripe URL that the platform can redirect their user to in order to take them through the Connect Onboarding flow.',
                params: {
                  account: {
                    type: 'param',
                    description:
                      'The identifier of the account to create an account link for.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  collect: {
                    type: 'param',
                    description:
                      'Which information the platform needs to collect from the user. One of `currently_due` or `eventually_due`. Default is `currently_due`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['currently_due', 'eventually_due'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'refresh-url': {
                    type: 'param',
                    description:
                      "The URL the user will be redirected to if the account link is expired, has been previously-visited, or is otherwise invalid. The URL you specify should attempt to generate a new account link with the same parameters used to create the original account link, then redirect the user to the new account link's URL so they can continue with Connect Onboarding. If a new account link cannot be generated or the redirect fails you should display a useful error to the user.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      'The URL that the user will be redirected to upon leaving or completing the linked flow.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The type of account link the user is requesting. Possible values are `account_onboarding` or `account_update`.',
                    required: true,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'account_onboarding',
                        'account_update',
                        'custom_account_update',
                        'custom_account_verification',
                      ],
                    },
                  },
                },
                args: [],
                path: '/v1/account_links',
                method: 'post',
                run: true,
              },
            },
          },
          apple_pay_domains: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'List apple pay domains.',
                params: {
                  'domain-name': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/apple_pay/domains',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Create an apple pay domain.',
                params: {
                  'domain-name': {
                    type: 'param',
                    description: '',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/apple_pay/domains',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieve an apple pay domain.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['domain'],
                path: '/v1/apple_pay/domains/{domain}',
                method: 'get',
                run: true,
              },
              delete: {
                type: 'command',
                description: 'Delete an apple pay domain.',
                params: {},
                args: ['domain'],
                path: '/v1/apple_pay/domains/{domain}',
                method: 'delete',
                run: true,
              },
            },
          },
          application_fees: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of application fees you’ve previously collected. The application fees are returned in sorted order, with the most recent fees appearing first.',
                params: {
                  charge: {
                    type: 'param',
                    description:
                      'Only return application fees for the charge specified by this charge ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/application_fees',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an application fee that your account has collected. The same information is returned when refunding the application fee.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/application_fees/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          balance: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the current account balance, based on the authentication that was used to make the request.  For a sample request, see Accounting for negative balances.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/balance',
                method: 'get',
                run: true,
              },
            },
          },
          balance_transactions: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of transactions that have contributed to the Stripe account balance (e.g., charges, transfers, and so forth). The transactions are returned in sorted order, with the most recent transactions appearing first.  Note that this endpoint was previously called “Balance history” and used the path /v1/balance/history.',
                params: {
                  'available-on': {
                    type: 'param',
                    description:
                      'This parameter is deprecated and we recommend listing by created and filtering in memory instead.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Only return transactions in a certain currency. Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  payout: {
                    type: 'param',
                    description:
                      'For automatic Stripe payouts only, only returns transactions that were paid out on the specified payout ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  source: {
                    type: 'param',
                    description: 'Only returns the original transaction.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'Only returns transactions of the given type. One of: `adjustment`, `advance`, `advance_funding`, `anticipation_repayment`, `application_fee`, `application_fee_refund`, `charge`, `connect_collection_transfer`, `contribution`, `issuing_authorization_hold`, `issuing_authorization_release`, `issuing_dispute`, `issuing_transaction`, `payment`, `payment_failure_refund`, `payment_refund`, `payout`, `payout_cancel`, `payout_failure`, `refund`, `refund_failure`, `reserve_transaction`, `reserved_funds`, `stripe_fee`, `stripe_fx_fee`, `tax_fee`, `topup`, `topup_reversal`, `transfer`, `transfer_cancel`, `transfer_failure`, or `transfer_refund`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/balance_transactions',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the balance transaction with the given ID.  Note that this endpoint previously used the path /v1/balance/history/:id.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/balance_transactions/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          bank_accounts: {
            type: 'command',
            commands: {
              update: {
                type: 'command',
                description:
                  'Updates the metadata, account holder name, account holder type of a bank account belonging to a Custom account, and optionally sets it as the default for its currency. Other bank account details are not editable by design.  You can re-enable a disabled bank account by performing an update call without providing any arguments or changes.',
                params: {
                  'account-holder-name': {
                    type: 'param',
                    description:
                      'The name of the person or business that owns the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'account-holder-type': {
                    type: 'param',
                    description:
                      'The type of entity that holds the account. This can be either `individual` or `company`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['company', 'individual'],
                      },
                    },
                  },
                  'account-type': {
                    type: 'param',
                    description:
                      'The bank account type. This can only be `checking` or `savings` in most countries. In Japan, this can only be `futsu` or `toza`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['checking', 'futsu', 'savings', 'toza'],
                    },
                  },
                  'address-city': {
                    type: 'param',
                    description: 'City/District/Suburb/Town/Village.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-country': {
                    type: 'param',
                    description:
                      'Billing address country, if provided when creating card.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line1': {
                    type: 'param',
                    description:
                      'Address line 1 (Street address/PO Box/Company name).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line2': {
                    type: 'param',
                    description:
                      'Address line 2 (Apartment/Suite/Unit/Building).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-state': {
                    type: 'param',
                    description: 'State/County/Province/Region.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-zip': {
                    type: 'param',
                    description: 'ZIP or postal code.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-for-currency': {
                    type: 'param',
                    description:
                      'When set to true, this becomes the default external account for its currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'exp-month': {
                    type: 'param',
                    description:
                      'Two digit number representing the card’s expiration month.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'exp-year': {
                    type: 'param',
                    description:
                      'Four digit number representing the card’s expiration year.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description: 'Cardholder name.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Delete a specified external account for a given account.',
                params: {},
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'delete',
                run: true,
              },
              verify: {
                type: 'command',
                description:
                  'Verify a specified bank account for a given customer.',
                params: {
                  amounts: {
                    type: 'param',
                    description:
                      'Two positive integers, in *cents*, equal to the values of the microdeposits sent to the bank account.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer', 'id'],
                path: '/v1/customers/{customer}/sources/{id}/verify',
                method: 'post',
                run: true,
              },
            },
          },
          billing_portal: {
            type: 'command',
            commands: {
              configurations: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of configurations that describe the functionality of the customer portal.',
                    params: {
                      active: {
                        type: 'param',
                        description:
                          'Only return configurations that are active or inactive (e.g., pass `true` to only list active configurations).',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'boolean',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'is-default': {
                        type: 'param',
                        description:
                          'Only return the default or non-default configurations (e.g., pass `true` to only list the default configuration).',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'boolean',
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/billing_portal/configurations',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a configuration that describes the functionality and behavior of a PortalSession',
                    params: {
                      'business-profile': {
                        type: 'param',
                        description:
                          'The business information shown to customers in the portal.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'default-return-url': {
                        type: 'param',
                        description:
                          "The default URL to redirect customers to when they click on the portal's link to return to your website. This can be overriden when creating the session.",
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      features: {
                        type: 'param',
                        description:
                          'Information about the features available in the portal.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: [],
                    path: '/v1/billing_portal/configurations',
                    method: 'post',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates a configuration that describes the functionality of the customer portal.',
                    params: {
                      active: {
                        type: 'param',
                        description:
                          'Whether the configuration is active and can be used to create portal sessions.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'boolean',
                        },
                      },
                      'business-profile': {
                        type: 'param',
                        description:
                          'The business information shown to customers in the portal.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'default-return-url': {
                        type: 'param',
                        description:
                          "The default URL to redirect customers to when they click on the portal's link to return to your website. This can be overriden when creating the session.",
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      features: {
                        type: 'param',
                        description:
                          'Information about the features available in the portal.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['configuration'],
                    path: '/v1/billing_portal/configurations/{configuration}',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves a configuration that describes the functionality of the customer portal.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['configuration'],
                    path: '/v1/billing_portal/configurations/{configuration}',
                    method: 'get',
                    run: true,
                  },
                },
              },
              sessions: {
                type: 'command',
                commands: {
                  create: {
                    type: 'command',
                    description: 'Creates a session of the customer portal.',
                    params: {
                      configuration: {
                        type: 'param',
                        description:
                          'The ID of an existing configuration to use for this session, describing its functionality and features. If not specified, the session uses the default configuration.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      customer: {
                        type: 'param',
                        description: 'The ID of an existing customer.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      locale: {
                        type: 'param',
                        description:
                          'The IETF language tag of the locale Customer Portal is displayed in. If blank or auto, the customer’s `preferred_locales` or browser’s locale is used.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'auto',
                            'bg',
                            'cs',
                            'da',
                            'de',
                            'el',
                            'en',
                            'en-AU',
                            'en-CA',
                            'en-GB',
                            'en-IE',
                            'en-IN',
                            'en-NZ',
                            'en-SG',
                            'es',
                            'es-419',
                            'et',
                            'fi',
                            'fil',
                            'fr',
                            'fr-CA',
                            'hr',
                            'hu',
                            'id',
                            'it',
                            'ja',
                            'ko',
                            'lt',
                            'lv',
                            'ms',
                            'mt',
                            'nb',
                            'nl',
                            'pl',
                            'pt',
                            'pt-BR',
                            'ro',
                            'ru',
                            'sk',
                            'sl',
                            'sv',
                            'th',
                            'tr',
                            'vi',
                            'zh',
                            'zh-HK',
                            'zh-TW',
                          ],
                        },
                      },
                      'on-behalf-of': {
                        type: 'param',
                        description:
                          "The `on-behalf-of` account to use for this session. When specified, only subscriptions and invoices with this `on-behalf-of` account appear in the portal. For more information, see the docs. Use the Accounts API to modify the `on-behalf-of` account's branding settings, which the portal displays.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'return-url': {
                        type: 'param',
                        description:
                          "The default URL to redirect customers to when they click on the portal's link to return to your website.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/billing_portal/sessions',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          capabilities: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of capabilities associated with the account. The capabilities are returned sorted by creation date, with the most recent capability appearing first.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/capabilities',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves information about the specified Account Capability.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account', 'capability'],
                path: '/v1/accounts/{account}/capabilities/{capability}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing Account Capability.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  requested: {
                    type: 'param',
                    description:
                      'Passing true requests the capability for the account, if it is not already requested. A requested capability may not immediately become active. Any requirements to activate the capability are returned in the `requirements` arrays.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['account', 'capability'],
                path: '/v1/accounts/{account}/capabilities/{capability}',
                method: 'post',
                run: true,
              },
            },
          },
          cards: {
            type: 'command',
            commands: {
              update: {
                type: 'command',
                description:
                  'Updates the metadata, account holder name, account holder type of a bank account belonging to a Custom account, and optionally sets it as the default for its currency. Other bank account details are not editable by design.  You can re-enable a disabled bank account by performing an update call without providing any arguments or changes.',
                params: {
                  'account-holder-name': {
                    type: 'param',
                    description:
                      'The name of the person or business that owns the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'account-holder-type': {
                    type: 'param',
                    description:
                      'The type of entity that holds the account. This can be either `individual` or `company`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['company', 'individual'],
                      },
                    },
                  },
                  'account-type': {
                    type: 'param',
                    description:
                      'The bank account type. This can only be `checking` or `savings` in most countries. In Japan, this can only be `futsu` or `toza`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['checking', 'futsu', 'savings', 'toza'],
                    },
                  },
                  'address-city': {
                    type: 'param',
                    description: 'City/District/Suburb/Town/Village.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-country': {
                    type: 'param',
                    description:
                      'Billing address country, if provided when creating card.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line1': {
                    type: 'param',
                    description:
                      'Address line 1 (Street address/PO Box/Company name).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line2': {
                    type: 'param',
                    description:
                      'Address line 2 (Apartment/Suite/Unit/Building).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-state': {
                    type: 'param',
                    description: 'State/County/Province/Region.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-zip': {
                    type: 'param',
                    description: 'ZIP or postal code.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-for-currency': {
                    type: 'param',
                    description:
                      'When set to true, this becomes the default external account for its currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'exp-month': {
                    type: 'param',
                    description:
                      'Two digit number representing the card’s expiration month.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'exp-year': {
                    type: 'param',
                    description:
                      'Four digit number representing the card’s expiration year.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description: 'Cardholder name.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Delete a specified external account for a given account.',
                params: {},
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'delete',
                run: true,
              },
            },
          },
          charges: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for charges you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for charges.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/charges/search',
                method: 'get',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of charges you’ve previously created. The charges are returned in sorted order, with the most recent charges appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return charges for the customer specified by this customer ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'payment-intent': {
                    type: 'param',
                    description:
                      'Only return charges that were created by the PaymentIntent specified by this PaymentIntent ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description: 'Only return charges for this transfer group.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/charges',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'To charge a credit card or other payment source, you create a Charge object. If your API key is in test mode, the supplied payment source (e.g., card) won’t actually be charged, although everything else will occur as if in live mode. (Stripe assumes that the charge would have completed successfully).',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount intended to be collected by this payment. A positive integer representing how much to charge in the smallest currency unit (e.g., 100 cents to charge $1.00 or 100 to charge ¥100, a zero-decimal currency). The minimum amount is $0.50 US or equivalent in charge currency. The amount value supports up to eight digits (e.g., a value of 99999999 for a USD charge of $999,999.99).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "A fee in %s that will be applied to the charge and transferred to the application owner's Stripe account. The request must be made with an OAuth key or the `Stripe-Account` header in order to take an application fee. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  capture: {
                    type: 'param',
                    description:
                      'Whether to immediately capture the charge. Defaults to `true`. When `false`, the charge issues an authorization (or pre-authorization), and will need to be captured later. Uncaptured charges expire after a set number of days (7 by default). For more information, see the authorizing charges and settling later documentation.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The ID of an existing customer that will be charged in this request.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to a `Charge` object. It is displayed when in the web interface alongside the charge. Note that if you use Stripe to send automatic email receipts to your customers, your receipt emails will include the `description` of the charge(s) that they are describing.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  destination: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description:
                      'The Stripe account ID for which these funds are intended. Automatically set if you use the `destination` parameter. For details, see Creating Separate Charges and Transfers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      "The email address to which this charge's receipt will be sent. The receipt will not be sent until the charge is paid, and no receipts will be sent for test mode charges. If this charge is for a Customer, the email address specified here will override the customer's email address. If `receipt-email` is specified for a charge in live mode, a receipt will be sent regardless of your email settings.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      'Shipping information for the charge. Helps prevent fraud on charges for physical goods.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  source: {
                    type: 'param',
                    description:
                      'A payment source to be charged. This can be the ID of a card (i.e., credit or debit card), a bank account, a source, a token, or a connected account. For certain sources---namely, cards, bank accounts, and attached sources---you must also pass the ID of the associated customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      'For card charges, use `statement-descriptor_suffix` instead. Otherwise, you can use this value as the complete description of a charge on your customers’ statements. Must contain at least one letter, maximum 22 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor-suffix': {
                    type: 'param',
                    description:
                      'Provides information about the charge that customers see on their statements. Concatenated with the prefix (shortened descriptor) or statement descriptor that’s set on the account to form the complete statement descriptor. Maximum 22 characters for the concatenated descriptor.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'An optional dictionary including the account to automatically transfer to as part of a destination charge. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies this transaction as part of a group. For details, see Grouping transactions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/charges',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of a charge that has previously been created. Supply the unique charge ID that was returned from your previous request, and Stripe will return the corresponding charge information. The same information is returned when creating or refunding the charge.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['charge'],
                path: '/v1/charges/{charge}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified charge by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'The ID of an existing customer that will be associated with this request. This field may only be updated if there is no existing associated customer with this charge.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to a charge object. It is displayed when in the web interface alongside the charge. Note that if you use Stripe to send automatic email receipts to your customers, your receipt emails will include the `description` of the charge(s) that they are describing.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'fraud-details': {
                    type: 'param',
                    description:
                      'A set of key-value pairs you can attach to a charge giving information about its riskiness. If you believe a charge is fraudulent, include a `user_report` key with a value of `fraudulent`. If you believe a charge is safe, include a `user_report` key with a value of `safe`. Stripe will use the information you send to improve our fraud detection algorithms.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      'This is the email address that the receipt for this charge will be sent to. If this field is updated, then a new email receipt will be sent to the updated address.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      'Shipping information for the charge. Helps prevent fraud on charges for physical goods.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies this transaction as part of a group. `transfer-group` may only be provided if it has not been set. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['charge'],
                path: '/v1/charges/{charge}',
                method: 'post',
                run: true,
              },
              capture: {
                type: 'command',
                description:
                  'Capture the payment of an existing, uncaptured, charge. This is the second half of the two-step payment flow, where first you created a charge with the capture option set to false.  Uncaptured payments expire a set number of days after they are created (7 by default). If they are not captured by that point in time, they will be marked as refunded and will no longer be capturable.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'The amount to capture, which must be less than or equal to the original amount. Any additional amount will be automatically refunded.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee': {
                    type: 'param',
                    description: 'An application fee to add on to this charge.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      'An application fee amount to add on to this charge, which must be less than or equal to the original amount.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      "The email address to send this charge's receipt to. This will override the previously-specified email address for this charge, if one was set. Receipts will not be sent in test mode.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      'For card charges, use `statement-descriptor_suffix` instead. Otherwise, you can use this value as the complete description of a charge on your customers’ statements. Must contain at least one letter, maximum 22 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor-suffix': {
                    type: 'param',
                    description:
                      'Provides information about the charge that customers see on their statements. Concatenated with the prefix (shortened descriptor) or statement descriptor that’s set on the account to form the complete statement descriptor. Maximum 22 characters for the concatenated descriptor.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'An optional dictionary including the account to automatically transfer to as part of a destination charge. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies this transaction as part of a group. `transfer-group` may only be provided if it has not been set. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['charge'],
                path: '/v1/charges/{charge}/capture',
                method: 'post',
                run: true,
              },
            },
          },
          checkout: {
            type: 'command',
            commands: {
              sessions: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description: 'Returns a list of Checkout Sessions.',
                    params: {
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'payment-intent': {
                        type: 'param',
                        description:
                          'Only return the Checkout Session for the PaymentIntent specified.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      subscription: {
                        type: 'param',
                        description:
                          'Only return the Checkout Session for the subscription specified.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/checkout/sessions',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description: 'Creates a Session object.',
                    params: {
                      'after-expiration': {
                        type: 'param',
                        description:
                          'Configure actions after a Checkout Session has expired.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'allow-promotion-codes': {
                        type: 'param',
                        description: 'Enables user redeemable promotion codes.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'boolean',
                        },
                      },
                      'automatic-tax': {
                        type: 'param',
                        description:
                          'Settings for automatic tax lookup for this session and resulting payments, invoices, and subscriptions.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'billing-address-collection': {
                        type: 'param',
                        description:
                          "Specify whether Checkout should collect the customer's billing address.",
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['auto', 'required'],
                        },
                      },
                      'cancel-url': {
                        type: 'param',
                        description:
                          'The URL the customer will be directed to if they decide to cancel payment and return to your website.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'client-reference-id': {
                        type: 'param',
                        description:
                          'A unique string to reference the Checkout Session. This can be a customer ID, a cart ID, or similar, and can be used to reconcile the session with your internal systems.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'consent-collection': {
                        type: 'param',
                        description:
                          'Configure fields for the Checkout Session to gather active consent from customers.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      customer: {
                        type: 'param',
                        description:
                          "ID of an existing Customer, if one exists. In `payment` mode, the customer’s most recent card payment method will be used to prefill the email, name, card details, and billing address on the Checkout page. In `subscription` mode, the customer’s default payment method will be used if it’s a card, and otherwise the most recent card will be used. A valid billing address, billing name and billing email are required on the payment method for Checkout to prefill the customer's card details.  If the Customer already has a valid email set, the email will be prefilled and not editable in Checkout. If the Customer does not have a valid `email`, Checkout will set the email entered during the session on the Customer.  If blank for Checkout Sessions in `payment` or `subscription` mode, Checkout will create a new Customer object based on information provided during the payment flow.  You can set `payment_intent_data.setup_future_usage` to have Checkout automatically attach the payment method to the Customer you pass in for future reuse.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'customer-creation': {
                        type: 'param',
                        description:
                          'Configure whether a Checkout Session creates a Customer during Session confirmation.  When a Customer is not created, you can still retrieve email, address, and other customer data entered in Checkout with customer_details.  Sessions that do not create Customers will instead create Guest Customers in the Dashboard.  Can only be set in `payment` and `setup` mode.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['always', 'if_required'],
                        },
                      },
                      'customer-email': {
                        type: 'param',
                        description:
                          'If provided, this value will be used when the Customer object is created. If not provided, customers will be asked to enter their email address. Use this parameter to prefill customer data if you already have an email on file. To access information about the customer once a session is complete, use the `customer` field.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'customer-update': {
                        type: 'param',
                        description:
                          'Controls what fields on Customer can be updated by the Checkout Session. Can only be provided when `customer` is provided.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      discounts: {
                        type: 'param',
                        description:
                          'The coupon or promotion code to apply to this Session. Currently, only up to one may be specified.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'expires-at': {
                        type: 'param',
                        description:
                          'The Epoch time in seconds at which the Checkout Session will expire. It can be anywhere from 1 to 24 hours after Checkout Session creation. By default, this value is 24 hours from creation.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'DateTime',
                        },
                      },
                      'line-items': {
                        type: 'param',
                        description:
                          'A list of items the customer is purchasing. Use this parameter to pass one-time or recurring Prices.  For `payment` mode, there is a maximum of 100 line items, however it is recommended to consolidate line items if there are more than a few dozen.  For `subscription` mode, there is a maximum of 20 line items with recurring Prices and 20 line items with one-time Prices. Line items with one-time Prices in will be on the initial invoice only.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      locale: {
                        type: 'param',
                        description:
                          "The IETF language tag of the locale Checkout is displayed in. If blank or `auto`, the browser's locale is used.",
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'auto',
                            'bg',
                            'cs',
                            'da',
                            'de',
                            'el',
                            'en',
                            'en-GB',
                            'es',
                            'es-419',
                            'et',
                            'fi',
                            'fil',
                            'fr',
                            'fr-CA',
                            'hr',
                            'hu',
                            'id',
                            'it',
                            'ja',
                            'ko',
                            'lt',
                            'lv',
                            'ms',
                            'mt',
                            'nb',
                            'nl',
                            'pl',
                            'pt',
                            'pt-BR',
                            'ro',
                            'ru',
                            'sk',
                            'sl',
                            'sv',
                            'th',
                            'tr',
                            'vi',
                            'zh',
                            'zh-HK',
                            'zh-TW',
                          ],
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      mode: {
                        type: 'param',
                        description:
                          'The mode of the Checkout Session. Required when using prices or `setup` mode. Pass `subscription` if the Checkout Session includes at least one recurring item.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['payment', 'setup', 'subscription'],
                        },
                      },
                      'payment-intent-data': {
                        type: 'param',
                        description:
                          'A subset of parameters to be passed to PaymentIntent creation for Checkout Sessions in `payment` mode.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'payment-method-options': {
                        type: 'param',
                        description: 'Payment-method-specific configuration.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'payment-method-types': {
                        type: 'param',
                        description:
                          "A list of the types of payment methods (e.g., `card`) this Checkout Session can accept.  Read more about the supported payment methods and their requirements in our payment method details guide.  If multiple payment methods are passed, Checkout will dynamically reorder them to prioritize the most relevant payment methods based on the customer's location and other characteristics.",
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'enum',
                            enum: [
                              'acss_debit',
                              'afterpay_clearpay',
                              'alipay',
                              'au_becs_debit',
                              'bacs_debit',
                              'bancontact',
                              'boleto',
                              'card',
                              'eps',
                              'fpx',
                              'giropay',
                              'grabpay',
                              'ideal',
                              'klarna',
                              'konbini',
                              'oxxo',
                              'p24',
                              'paynow',
                              'sepa_debit',
                              'sofort',
                              'us_bank_account',
                              'wechat_pay',
                            ],
                          },
                        },
                      },
                      'phone-number-collection': {
                        type: 'param',
                        description:
                          'Controls phone number collection settings for the session.  We recommend that you review your privacy policy and check with your legal contacts before using this feature. Learn more about collecting phone numbers with Checkout.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'setup-intent-data': {
                        type: 'param',
                        description:
                          'A subset of parameters to be passed to SetupIntent creation for Checkout Sessions in `setup` mode.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'shipping-address-collection': {
                        type: 'param',
                        description:
                          'When set, provides configuration for Checkout to collect a shipping address from a customer.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'shipping-options': {
                        type: 'param',
                        description:
                          'The shipping rate options to apply to this Session.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'shipping-rates': {
                        type: 'param',
                        description:
                          '[Deprecated] The shipping rate to apply to this Session. Only up to one may be specified.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'submit-type': {
                        type: 'param',
                        description:
                          'Describes the type of transaction being performed by Checkout in order to customize relevant text on the page, such as the submit button. `submit-type` can only be specified on Checkout Sessions in `payment` mode, but not Checkout Sessions in `subscription` or `setup` mode.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['auto', 'book', 'donate', 'pay'],
                        },
                      },
                      'subscription-data': {
                        type: 'param',
                        description:
                          'A subset of parameters to be passed to subscription creation for Checkout Sessions in `subscription` mode.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'success-url': {
                        type: 'param',
                        description:
                          'The URL to which Stripe should send customers when payment or setup is complete. If you’d like to use information from the successful Checkout Session on your page, read the guide on customizing your success page.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'tax-id-collection': {
                        type: 'param',
                        description:
                          'Controls tax ID collection settings for the session.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/checkout/sessions',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a Session object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/checkout/sessions/{session}',
                    method: 'get',
                    run: true,
                  },
                  expire: {
                    type: 'command',
                    description:
                      'A Session can be expired when it is in one of these statuses: open   After it expires, a customer can’t complete a Session and customers loading the Session see a message saying the Session is expired.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/checkout/sessions/{session}/expire',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          country_specs: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Lists all Country Spec objects available in the API.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/country_specs',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Returns a Country Spec for a given Country code.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['country'],
                path: '/v1/country_specs/{country}',
                method: 'get',
                run: true,
              },
            },
          },
          coupons: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your coupons.',
                params: {
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/coupons',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'You can create coupons easily via the coupon management page of the Stripe dashboard. Coupon creation is also accessible via the API if you need to create coupons on the fly.  A coupon has either a percent_off or an amount_off and currency. If you set an amount_off, that amount will be subtracted from any invoice’s subtotal. For example, an invoice with a subtotal of 100 will have a final total of 0 if a coupon with an amount_off of 200 is applied to it and an invoice with a subtotal of 300 will have a final total of 100 if a coupon with an amount_off of 200 is applied to it.',
                params: {
                  'amount-off': {
                    type: 'param',
                    description:
                      'A positive integer representing the amount to subtract from an invoice total (required if `percent_off` is not passed).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'applies-to': {
                    type: 'param',
                    description:
                      'A hash containing directions for what this Coupon will apply discounts to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO code for the currency of the `amount_off` parameter (required if `amount_off` is passed).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  duration: {
                    type: 'param',
                    description:
                      'Specifies how long the discount will be in effect if used on a subscription. Can be `forever`, `once`, or `repeating`. Defaults to `once`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['forever', 'once', 'repeating'],
                    },
                  },
                  'duration-in-months': {
                    type: 'param',
                    description:
                      'Required only if `duration` is `repeating`, in which case it must be a positive integer that specifies the number of months the discount will be in effect.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  id: {
                    type: 'param',
                    description:
                      "Unique string of your choice that will be used to identify this coupon when applying it to a customer. If you don't want to specify a particular code, you can leave the ID blank and we'll generate a random code for you.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'max-redemptions': {
                    type: 'param',
                    description:
                      "A positive integer specifying the number of times the coupon can be redeemed before it's no longer valid. For example, you might have a 50% off coupon that the first 20 readers of your blog can use.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      'Name of the coupon displayed to customers on, for instance invoices, or receipts. By default the `id` is shown if `name` is not set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'percent-off': {
                    type: 'param',
                    description:
                      'A positive float larger than 0, and smaller or equal to 100, that represents the discount the coupon will apply (required if `amount_off` is not passed).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'number',
                    },
                  },
                  'redeem-by': {
                    type: 'param',
                    description:
                      'Unix timestamp specifying the last time at which the coupon can be redeemed. After the redeem-by date, the coupon can no longer be applied to new customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                },
                args: [],
                path: '/v1/coupons',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the coupon with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['coupon'],
                path: '/v1/coupons/{coupon}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the metadata of a coupon. Other coupon details (currency, duration, amount_off) are, by design, not editable.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      'Name of the coupon displayed to customers on, for instance invoices, or receipts. By default the `id` is shown if `name` is not set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['coupon'],
                path: '/v1/coupons/{coupon}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'You can delete coupons via the coupon management page of the Stripe dashboard. However, deleting a coupon does not affect any customers who have already applied the coupon; it means that new customers can’t redeem the coupon. You can also delete coupons via the API.',
                params: {},
                args: ['coupon'],
                path: '/v1/coupons/{coupon}',
                method: 'delete',
                run: true,
              },
            },
          },
          credit_notes: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Issue a credit note to adjust the amount of a finalized invoice. For a status=open invoice, a credit note reduces its amount_due. For a status=paid invoice, a credit note does not affect its amount_due. Instead, it can result in any combination of the following:   Refund: create a new refund (using refund_amount) or link an existing refund (using refund). Customer balance credit: credit the customer’s balance (using credit_amount) which will be automatically applied to their next invoice when it’s finalized. Outside of Stripe credit: record the amount that is or will be credited outside of Stripe (using out_of_band_amount).   For post-payment credit notes the sum of the refund, credit and outside of Stripe amounts must equal the credit note total.  You may issue multiple credit notes for an invoice. Each credit note will increment the invoice’s pre_payment_credit_notes_amount or post_payment_credit_notes_amount depending on its status at the time of credit note creation.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the total amount of the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'credit-amount': {
                    type: 'param',
                    description:
                      "The integer amount in %s representing the amount to credit the customer's balance, which will be automatically applied to their next invoice.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description: 'ID of the invoice.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  lines: {
                    type: 'param',
                    description: 'Line items that make up the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  memo: {
                    type: 'param',
                    description:
                      "The credit note's memo appears on the credit note PDF.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'out-of-band-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount that is credited outside of Stripe.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  reason: {
                    type: 'param',
                    description:
                      'Reason for issuing this credit note, one of `duplicate`, `fraudulent`, `order_change`, or `product_unsatisfactory`',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'duplicate',
                        'fraudulent',
                        'order_change',
                        'product_unsatisfactory',
                      ],
                    },
                  },
                  refund: {
                    type: 'param',
                    description:
                      'ID of an existing refund to link this credit note to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'refund-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount to refund. If set, a refund will be created for the charge associated with the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                },
                args: [],
                path: '/v1/credit_notes',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of credit notes.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'Only return credit notes for the customer specified by this customer ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description:
                      'Only return credit notes for the invoice specified by this invoice ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/credit_notes',
                method: 'get',
                run: true,
              },
              preview: {
                type: 'command',
                description:
                  'Get a preview of a credit note without creating it.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the total amount of the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'credit-amount': {
                    type: 'param',
                    description:
                      "The integer amount in %s representing the amount to credit the customer's balance, which will be automatically applied to their next invoice.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description: 'ID of the invoice.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  lines: {
                    type: 'param',
                    description: 'Line items that make up the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  memo: {
                    type: 'param',
                    description:
                      "The credit note's memo appears on the credit note PDF.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'out-of-band-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount that is credited outside of Stripe.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  reason: {
                    type: 'param',
                    description:
                      'Reason for issuing this credit note, one of `duplicate`, `fraudulent`, `order_change`, or `product_unsatisfactory`',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'duplicate',
                        'fraudulent',
                        'order_change',
                        'product_unsatisfactory',
                      ],
                    },
                  },
                  refund: {
                    type: 'param',
                    description:
                      'ID of an existing refund to link this credit note to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'refund-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount to refund. If set, a refund will be created for the charge associated with the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                },
                args: [],
                path: '/v1/credit_notes/preview',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the credit note object with the given identifier.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/credit_notes/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing credit note.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  memo: {
                    type: 'param',
                    description: 'Credit note memo.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/credit_notes/{id}',
                method: 'post',
                run: true,
              },
              void_credit_note: {
                type: 'command',
                description:
                  'Marks a credit note as void. Learn more about voiding credit notes.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/credit_notes/{id}/void',
                method: 'post',
                run: true,
              },
              preview_lines: {
                type: 'command',
                description:
                  'When retrieving a credit note preview, you’ll get a lines property containing the first handful of those items. This URL you can retrieve the full (paginated) list of line items.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the total amount of the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'credit-amount': {
                    type: 'param',
                    description:
                      "The integer amount in %s representing the amount to credit the customer's balance, which will be automatically applied to their next invoice.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description: 'ID of the invoice.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  lines: {
                    type: 'param',
                    description: 'Line items that make up the credit note.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  memo: {
                    type: 'param',
                    description:
                      "The credit note's memo appears on the credit note PDF.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'out-of-band-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount that is credited outside of Stripe.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  reason: {
                    type: 'param',
                    description:
                      'Reason for issuing this credit note, one of `duplicate`, `fraudulent`, `order_change`, or `product_unsatisfactory`',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'duplicate',
                        'fraudulent',
                        'order_change',
                        'product_unsatisfactory',
                      ],
                    },
                  },
                  refund: {
                    type: 'param',
                    description:
                      'ID of an existing refund to link this credit note to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'refund-amount': {
                    type: 'param',
                    description:
                      'The integer amount in %s representing the amount to refund. If set, a refund will be created for the charge associated with the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/credit_notes/preview/lines',
                method: 'get',
                run: true,
              },
            },
          },
          credit_note_line_items: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'When retrieving a credit note, you’ll get a lines property containing the the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['credit_note'],
                path: '/v1/credit_notes/{credit_note}/lines',
                method: 'get',
                run: true,
              },
            },
          },
          customers: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for customers you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for customers.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/customers/search',
                method: 'get',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of your customers. The customers are returned sorted by creation date, with the most recent customers appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      "A case-sensitive filter on the list based on the customer's `email` field. The value must be a string.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'test-clock': {
                    type: 'param',
                    description:
                      'Provides a list of customers that are associated with the specified test clock. The response will not include customers with test clocks if this parameter is not set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/customers',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new customer object.',
                params: {
                  address: {
                    type: 'param',
                    description: "The customer's address.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  balance: {
                    type: 'param',
                    description:
                      "An integer amount in %s that represents the customer's current balance, which affect the customer's future invoices. A negative amount represents a credit that decreases the amount due on an invoice; a positive amount increases the amount due on an invoice.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string that you can attach to a customer object. It is displayed alongside the customer in the dashboard.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      "Customer's email address. It's displayed alongside the customer in your dashboard and can be useful for searching and tracking. This may be up to *512 characters*.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-prefix': {
                    type: 'param',
                    description:
                      'The prefix for the customer used to generate unique invoice numbers. Must be 3–12 uppercase letters or numbers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'invoice-settings': {
                    type: 'param',
                    description: 'Default invoice settings for this customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description: "The customer's full name or business name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'next-invoice-sequence': {
                    type: 'param',
                    description:
                      "The sequence to be used on the customer's next invoice. Defaults to 1.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  phone: {
                    type: 'param',
                    description: "The customer's phone number.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'preferred-locales': {
                    type: 'param',
                    description:
                      "Customer's preferred languages, ordered by preference.",
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'promotion-code': {
                    type: 'param',
                    description:
                      'The API ID of a promotion code to apply to the customer. The customer will have a discount applied on all recurring payments. Charges you create through the API will not have the discount.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      "The customer's shipping information. Appears on invoices emailed to this customer.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  source: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  tax: {
                    type: 'param',
                    description: 'Tax details about the customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tax-exempt': {
                    type: 'param',
                    description:
                      "The customer's tax exemption. One of `none`, `exempt`, or `reverse`.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['exempt', 'none', 'reverse'],
                      },
                    },
                  },
                  'tax-id-data': {
                    type: 'param',
                    description: "The customer's tax IDs.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'test-clock': {
                    type: 'param',
                    description:
                      'ID of the test clock to attach to the customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/customers',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves a Customer object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified customer by setting the values of the parameters passed. Any parameters not provided will be left unchanged. For example, if you pass the source parameter, that becomes the customer’s active source (e.g., a card) to be used for all charges in the future. When you update a customer to a new valid card source by passing the source parameter: for each of the customer’s current subscriptions, if the subscription bills automatically and is in the past_due state, then the latest open invoice for the subscription with automatic collection enabled will be retried. This retry will not count as an automatic retry, and will not affect the next regularly scheduled payment for the invoice. Changing the default_source for a customer will not trigger this behavior.  This request accepts mostly the same arguments as the customer creation call.',
                params: {
                  address: {
                    type: 'param',
                    description: "The customer's address.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  balance: {
                    type: 'param',
                    description:
                      "An integer amount in %s that represents the customer's current balance, which affect the customer's future invoices. A negative amount represents a credit that decreases the amount due on an invoice; a positive amount increases the amount due on an invoice.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-source': {
                    type: 'param',
                    description:
                      "If you are using payment methods created via the PaymentMethods API, see the invoice_settings.default_payment_method parameter.  Provide the ID of a payment source already attached to this customer to make it this customer's default payment source.  If you want to add a new payment source and make it the default, see the source property.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string that you can attach to a customer object. It is displayed alongside the customer in the dashboard.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      "Customer's email address. It's displayed alongside the customer in your dashboard and can be useful for searching and tracking. This may be up to *512 characters*.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-prefix': {
                    type: 'param',
                    description:
                      'The prefix for the customer used to generate unique invoice numbers. Must be 3–12 uppercase letters or numbers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'invoice-settings': {
                    type: 'param',
                    description: 'Default invoice settings for this customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description: "The customer's full name or business name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'next-invoice-sequence': {
                    type: 'param',
                    description:
                      "The sequence to be used on the customer's next invoice. Defaults to 1.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  phone: {
                    type: 'param',
                    description: "The customer's phone number.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'preferred-locales': {
                    type: 'param',
                    description:
                      "Customer's preferred languages, ordered by preference.",
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'promotion-code': {
                    type: 'param',
                    description:
                      'The API ID of a promotion code to apply to the customer. The customer will have a discount applied on all recurring payments. Charges you create through the API will not have the discount.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      "The customer's shipping information. Appears on invoices emailed to this customer.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  source: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  tax: {
                    type: 'param',
                    description: 'Tax details about the customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tax-exempt': {
                    type: 'param',
                    description:
                      "The customer's tax exemption. One of `none`, `exempt`, or `reverse`.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['exempt', 'none', 'reverse'],
                      },
                    },
                  },
                  'trial-end': {
                    type: 'param',
                    description:
                      "Unix timestamp representing the end of the trial period the customer will get before being charged for the first time. This will always overwrite any trials that might apply via a subscribed plan. If set, trial-end will override the default trial period of the plan the customer is being subscribed to. The special value `now` can be provided to end the customer's trial immediately. Can be at most two years from `billing_cycle_anchor`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Permanently deletes a customer. It cannot be undone. Also immediately cancels any active subscriptions on the customer.',
                params: {},
                args: ['customer'],
                path: '/v1/customers/{customer}',
                method: 'delete',
                run: true,
              },
              list_payment_methods: {
                type: 'command',
                description:
                  'Returns a list of PaymentMethods for a given Customer',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'A required filter on the list, based on the object `type` field.',
                    required: true,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'acss_debit',
                        'afterpay_clearpay',
                        'alipay',
                        'au_becs_debit',
                        'bacs_debit',
                        'bancontact',
                        'boleto',
                        'card',
                        'card_present',
                        'eps',
                        'fpx',
                        'giropay',
                        'grabpay',
                        'ideal',
                        'klarna',
                        'konbini',
                        'oxxo',
                        'p24',
                        'paynow',
                        'sepa_debit',
                        'sofort',
                        'us_bank_account',
                        'wechat_pay',
                      ],
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/payment_methods',
                method: 'get',
                run: true,
              },
              delete_discount: {
                type: 'command',
                description:
                  'Removes the currently applied discount on a customer.',
                params: {},
                args: ['customer'],
                path: '/v1/customers/{customer}/discount',
                method: 'delete',
                run: true,
              },
            },
          },
          customer_balance_transactions: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description:
                  'Retrieves a specific customer balance transaction that updated the customer’s balances.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer', 'transaction'],
                path:
                  '/v1/customers/{customer}/balance_transactions/{transaction}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Most credit balance transaction fields are immutable, but you may update its description and metadata.',
                params: {
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['customer', 'transaction'],
                path:
                  '/v1/customers/{customer}/balance_transactions/{transaction}',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of transactions that updated the customer’s balances.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/balance_transactions',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates an immutable transaction that updates the customer’s credit balance.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      "The integer amount in **%s** to apply to the customer's credit balance.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      "Three-letter ISO currency code, in lowercase. Must be a supported currency. If the customer's `currency` is set, this value must match it. If the customer's `currency` is not set, it will be updated to this value.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/balance_transactions',
                method: 'post',
                run: true,
              },
            },
          },
          disputes: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your disputes.',
                params: {
                  charge: {
                    type: 'param',
                    description:
                      'Only return disputes associated to the charge specified by this charge ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'payment-intent': {
                    type: 'param',
                    description:
                      'Only return disputes associated to the PaymentIntent specified by this PaymentIntent ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/disputes',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the dispute with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['dispute'],
                path: '/v1/disputes/{dispute}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'When you get a dispute, contacting your customer is always the best first step. If that doesn’t work, you can submit evidence to help us resolve the dispute in your favor. You can do this in your dashboard, but if you prefer, you can use the API to submit evidence programmatically.  Depending on your dispute type, different evidence fields will give you a better chance of winning your dispute. To figure out which evidence fields to provide, see our guide to dispute types.',
                params: {
                  evidence: {
                    type: 'param',
                    description:
                      'Evidence to upload, to respond to a dispute. Updating any field in the hash will submit all fields in the hash for review. The combined character count of all fields is limited to 150,000.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  submit: {
                    type: 'param',
                    description:
                      'Whether to immediately submit evidence to the bank. If `false`, evidence is staged on the dispute. Staged evidence is visible in the API and Dashboard, and can be submitted to the bank by making another request with this attribute set to `true` (the default).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['dispute'],
                path: '/v1/disputes/{dispute}',
                method: 'post',
                run: true,
              },
              close: {
                type: 'command',
                description:
                  'Closing the dispute for a charge indicates that you do not have any evidence to submit and are essentially dismissing the dispute, acknowledging it as lost.  The status of the dispute will change from needs_response to lost. Closing a dispute is irreversible.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['dispute'],
                path: '/v1/disputes/{dispute}/close',
                method: 'post',
                run: true,
              },
            },
          },
          ephemeral_keys: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates a short-lived API key for a given resource.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      "The ID of the Customer you'd like to modify using the resulting ephemeral key.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'issuing-card': {
                    type: 'param',
                    description:
                      "The ID of the Issuing Card you'd like to access using the resulting ephemeral key.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/ephemeral_keys',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Invalidates a short-lived API key for a given resource.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['key'],
                path: '/v1/ephemeral_keys/{key}',
                method: 'delete',
                run: true,
              },
            },
          },
          events: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'List events, going back up to 30 days. Each event data is rendered according to Stripe API version at its creation time, specified in event object api_version attribute (not according to your current Stripe API version or Stripe-Version header).',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'delivery-success': {
                    type: 'param',
                    description:
                      'Filter events by whether all webhooks were successfully delivered. If false, events which are still pending or have failed all delivery attempts to a webhook endpoint will be returned.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'A string containing a specific event name, or group of events using * as a wildcard. The list will be filtered to include only events with a matching event property.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  types: {
                    type: 'param',
                    description:
                      'An array of up to 20 strings containing specific event names. The list will be filtered to include only events with a matching event property. You may pass either `type` or `types`, but not both.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/events',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an event. Supply the unique identifier of the event, which you might have received in a webhook.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/events/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          exchange_rates: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of objects that contain the rates at which foreign currencies are converted to one another. Only shows the currencies for which Stripe supports.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is the currency that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with the exchange rate for currency X your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and total number of supported payout currencies, and the default is the max.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is the currency that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with the exchange rate for currency X, your subsequent call can include `starting-after=X` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/exchange_rates',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the exchange rates from the given currency to every supported currency.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['rate_id'],
                path: '/v1/exchange_rates/{rate_id}',
                method: 'get',
                run: true,
              },
            },
          },
          external_accounts: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'List external accounts for an account.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/external_accounts',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Create an external account for a given account.',
                params: {
                  'default-for-currency': {
                    type: 'param',
                    description:
                      'When set to true, or if this is the first external account added in this currency, this account becomes the default external account for its currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'external-account': {
                    type: 'param',
                    description: 'Please refer to full documentation instead.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/external_accounts',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieve a specified external account for a given account.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the metadata, account holder name, account holder type of a bank account belonging to a Custom account, and optionally sets it as the default for its currency. Other bank account details are not editable by design.  You can re-enable a disabled bank account by performing an update call without providing any arguments or changes.',
                params: {
                  'account-holder-name': {
                    type: 'param',
                    description:
                      'The name of the person or business that owns the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'account-holder-type': {
                    type: 'param',
                    description:
                      'The type of entity that holds the account. This can be either `individual` or `company`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['company', 'individual'],
                      },
                    },
                  },
                  'account-type': {
                    type: 'param',
                    description:
                      'The bank account type. This can only be `checking` or `savings` in most countries. In Japan, this can only be `futsu` or `toza`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['checking', 'futsu', 'savings', 'toza'],
                    },
                  },
                  'address-city': {
                    type: 'param',
                    description: 'City/District/Suburb/Town/Village.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-country': {
                    type: 'param',
                    description:
                      'Billing address country, if provided when creating card.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line1': {
                    type: 'param',
                    description:
                      'Address line 1 (Street address/PO Box/Company name).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-line2': {
                    type: 'param',
                    description:
                      'Address line 2 (Apartment/Suite/Unit/Building).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-state': {
                    type: 'param',
                    description: 'State/County/Province/Region.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'address-zip': {
                    type: 'param',
                    description: 'ZIP or postal code.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-for-currency': {
                    type: 'param',
                    description:
                      'When set to true, this becomes the default external account for its currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'exp-month': {
                    type: 'param',
                    description:
                      'Two digit number representing the card’s expiration month.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'exp-year': {
                    type: 'param',
                    description:
                      'Four digit number representing the card’s expiration year.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description: 'Cardholder name.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Delete a specified external account for a given account.',
                params: {},
                args: ['account', 'id'],
                path: '/v1/accounts/{account}/external_accounts/{id}',
                method: 'delete',
                run: true,
              },
            },
          },
          fee_refunds: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Refunds an application fee that has previously been collected but not yet refunded. Funds will be refunded to the Stripe account from which the fee was originally collected.  You can optionally refund only part of an application fee. You can do so multiple times, until the entire fee has been refunded.  Once entirely refunded, an application fee can’t be refunded again. This method will raise an error when called on an already-refunded application fee, or when trying to refund more money than is left on an application fee.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer, in _%s_, representing how much of this fee to refund. Can refund only up to the remaining unrefunded amount of the fee.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/application_fees/{id}/refunds',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'You can see a list of the refunds belonging to a specific application fee. Note that the 10 most recent refunds are always available by default on the application fee object. If you need more than those 10, you can use this API method and the limit and starting_after parameters to page through additional refunds.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/application_fees/{id}/refunds',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'By default, you can see the 10 most recent refunds stored directly on the application fee object, but you can also retrieve details about a specific refund stored on the application fee.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['fee', 'id'],
                path: '/v1/application_fees/{fee}/refunds/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified application fee refund by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  This request only accepts metadata as an argument.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['fee', 'id'],
                path: '/v1/application_fees/{fee}/refunds/{id}',
                method: 'post',
                run: true,
              },
            },
          },
          files: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of the files that your account has access to. The files are returned sorted by creation date, with the most recently created files appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  purpose: {
                    type: 'param',
                    description:
                      'The file purpose to filter queries by. If none is provided, files will not be filtered by purpose.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'account_requirement',
                        'additional_verification',
                        'business_icon',
                        'business_logo',
                        'customer_signature',
                        'dispute_evidence',
                        'document_provider_identity_document',
                        'finance_report_run',
                        'identity_document',
                        'identity_document_downloadable',
                        'pci_document',
                        'selfie',
                        'sigma_scheduled_query',
                        'tax_document_user_upload',
                      ],
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/files',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'To upload a file to Stripe, you’ll need to send a request of type multipart/form-data. The request should contain the file you would like to upload, as well as the parameters for creating a file.  All of Stripe’s officially supported Client libraries should have support for sending multipart/form-data.',
                params: {},
                args: [],
                path: '/v1/files',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing file object. Supply the unique file ID from a file, and Stripe will return the corresponding file object. To access file contents, see the File Upload Guide.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['file'],
                path: '/v1/files/{file}',
                method: 'get',
                run: true,
              },
            },
          },
          file_links: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves the file link with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['link'],
                path: '/v1/file_links/{link}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates an existing file link object. Expired links can no longer be updated.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      'A future timestamp after which the link will no longer be usable, or `now` to expire the link immediately.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['link'],
                path: '/v1/file_links/{link}',
                method: 'post',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new file link object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      'A future timestamp after which the link will no longer be usable.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  file: {
                    type: 'param',
                    description:
                      "The ID of the file. The file's `purpose` must be one of the following: `business_icon`, `business_logo`, `customer_signature`, `dispute_evidence`, `finance_report_run`, `identity_document_downloadable`, `pci_document`, `selfie`, `sigma_scheduled_query`, or `tax_document_user_upload`.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/file_links',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of file links.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  expired: {
                    type: 'param',
                    description:
                      'Filter links by their expiration status. By default, all links are returned.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  file: {
                    type: 'param',
                    description: 'Only return links for the given file.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/file_links',
                method: 'get',
                run: true,
              },
            },
          },
          identity: {
            type: 'command',
            commands: {
              verification_reports: {
                type: 'command',
                commands: {
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an existing VerificationReport',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['report'],
                    path: '/v1/identity/verification_reports/{report}',
                    method: 'get',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'List all verification reports.',
                    params: {
                      created: {
                        type: 'param',
                        description: '',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'Only return VerificationReports of this type',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['document', 'id_number'],
                        },
                      },
                      'verification-session': {
                        type: 'param',
                        description:
                          'Only return VerificationReports created by this VerificationSession ID. It is allowed to provide a VerificationIntent ID.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/identity/verification_reports',
                    method: 'get',
                    run: true,
                  },
                },
              },
              verification_sessions: {
                type: 'command',
                commands: {
                  create: {
                    type: 'command',
                    description:
                      'Creates a VerificationSession object.  After the VerificationSession is created, display a verification modal using the session client_secret or send your users to the session’s url.  If your API key is in test mode, verification checks won’t actually process, though everything else will occur as if in live mode.  Related guide: Verify your users’ identity documents.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      options: {
                        type: 'param',
                        description:
                          'A set of options for the session’s verification checks.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'return-url': {
                        type: 'param',
                        description:
                          'The URL that the user will be redirected to upon completing the verification flow.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'The type of verification check to be performed.',
                        required: true,
                        input_type: {
                          shape: 'enum',
                          enum: ['document', 'id_number'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/identity/verification_sessions',
                    method: 'post',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'Returns a list of VerificationSessions',
                    params: {
                      created: {
                        type: 'param',
                        description: '',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Only return VerificationSessions with this status. Learn more about the lifecycle of sessions.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'canceled',
                            'processing',
                            'requires_input',
                            'verified',
                          ],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/identity/verification_sessions',
                    method: 'get',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves the details of a VerificationSession that was previously created.  When the session status is requires_input, you can use this method to retrieve a valid client_secret or url to allow re-submission.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/identity/verification_sessions/{session}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates a VerificationSession object.  When the session status is requires_input, you can use this method to update the verification check and options.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      options: {
                        type: 'param',
                        description:
                          'A set of options for the session’s verification checks.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'The type of verification check to be performed.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['document', 'id_number'],
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/identity/verification_sessions/{session}',
                    method: 'post',
                    run: true,
                  },
                  cancel: {
                    type: 'command',
                    description:
                      'A VerificationSession object can be canceled when it is in requires_input status.  Once canceled, future submission attempts are disabled. This cannot be undone. Learn more.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/identity/verification_sessions/{session}/cancel',
                    method: 'post',
                    run: true,
                  },
                  redact: {
                    type: 'command',
                    description:
                      'Redact a VerificationSession to remove all collected information from Stripe. This will redact the VerificationSession and all objects related to it, including VerificationReports, Events, request logs, etc.  A VerificationSession object can be redacted when it is in requires_input or verified status. Redacting a VerificationSession in requires_action state will automatically cancel it.  The redaction process may take up to four days. When the redaction process is in progress, the VerificationSession’s redaction.status field will be set to processing; when the process is finished, it will change to redacted and an identity.verification_session.redacted event will be emitted.  Redaction is irreversible. Redacted objects are still accessible in the Stripe API, but all the fields that contain personal data will be replaced by the string [redacted] or a similar placeholder. The metadata field will also be erased. Redacted objects cannot be updated or used for any purpose.  Learn more.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['session'],
                    path: '/v1/identity/verification_sessions/{session}/redact',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          invoices: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for invoices you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for invoices.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/invoices/search',
                method: 'get',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'You can list all invoices, or list the invoices for a specific customer. The invoices are returned sorted by creation date, with the most recently created invoices appearing first.',
                params: {
                  'collection-method': {
                    type: 'param',
                    description:
                      'The collection method of the invoice to retrieve. Either `charge_automatically` or `send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return invoices for the customer specified by this customer ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'due-date': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'The status of the invoice, one of `draft`, `open`, `paid`, `uncollectible`, or `void`. Learn more',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['draft', 'open', 'paid', 'uncollectible', 'void'],
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      'Only return invoices for the subscription specified by this subscription ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/invoices',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'This endpoint creates a draft invoice for a given customer. The draft invoice created pulls in all pending invoice items on that customer, including prorations. The invoice remains a draft until you finalize the invoice, which allows you to pay or send the invoice to your customers.',
                params: {
                  'account-tax-ids': {
                    type: 'param',
                    description:
                      'The account tax IDs associated with the invoice. Only editable when the invoice is a draft.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "A fee in %s that will be applied to the invoice and transferred to the application owner's Stripe account. The request must be made with an OAuth key or the Stripe-Account header in order to take an application fee. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'auto-advance': {
                    type: 'param',
                    description:
                      "Controls whether Stripe will perform automatic collection of the invoice. When `false`, the invoice's state will not automatically advance without an explicit action.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically`, or `send_invoice`. When charging automatically, Stripe will attempt to pay this invoice using the default source attached to the customer. When sending an invoice, Stripe will email this invoice to the customer with payment instructions. Defaults to `charge_automatically`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  'custom-fields': {
                    type: 'param',
                    description:
                      'A list of up to 4 custom fields to be displayed on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  customer: {
                    type: 'param',
                    description: 'The ID of the customer who will be billed.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'days-until-due': {
                    type: 'param',
                    description:
                      'The number of days from when the invoice is created until it is due. Valid only for invoices where `collection_method=send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'default-payment-method': {
                    type: 'param',
                    description:
                      "ID of the default payment method for the invoice. It must belong to the customer associated with the invoice. If not set, defaults to the subscription's default payment method, if any, or to the default payment method in the customer's invoice settings.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-source': {
                    type: 'param',
                    description:
                      "ID of the default payment source for the invoice. It must belong to the customer associated with the invoice and be in a chargeable state. If not set, defaults to the subscription's default source, if any, or to the customer's default source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any line item that does not have `tax_rates` set.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      "An arbitrary string attached to the object. Often useful for displaying to users. Referenced as 'memo' in the Dashboard.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      "The coupons to redeem into discounts for the invoice. If not specified, inherits the discount from the invoice's customer. Pass an empty string to avoid inheriting any discounts.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'due-date': {
                    type: 'param',
                    description:
                      'The date on which payment for this invoice is due. Valid only for invoices where `collection_method=send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  footer: {
                    type: 'param',
                    description: 'Footer to be displayed on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description:
                      'The account (if any) for which the funds of the invoice payment are intended. If set, the invoice will be presented with the branding and support information of the specified account. See the Invoices with Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-settings': {
                    type: 'param',
                    description:
                      'Configuration settings for the PaymentIntent that is generated when the invoice is finalized.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'pending-invoice-items-behavior': {
                    type: 'param',
                    description:
                      'How to handle pending invoice items on invoice creation. One of `include`, `exclude`, or `include_and_require`. `include` will include any pending invoice items, and will create an empty draft invoice if no pending invoice items exist. `include_and_require` will include any pending invoice items, if no pending invoice items exist then the request will fail. `exclude` will always create an empty invoice draft regardless if there are pending invoice items or not. Defaults to `include_and_require` if the parameter is omitted.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['exclude', 'include', 'include_and_require'],
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "Extra information about a charge for the customer's credit card statement. It must contain at least one letter. If not specified and this invoice is part of a subscription, the default `statement-descriptor` will be set to the first subscription item's product's `statement-descriptor`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      "The ID of the subscription to invoice, if any. If not set, the created invoice will include all pending invoice items for the customer. If set, the created invoice will only include pending invoice items for that subscription and pending invoice items not associated with any subscription. The subscription's billing cycle and regular subscription events won't be affected.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      "If specified, the funds from the invoice will be transferred to the destination and the ID of the resulting transfer will be found on the invoice's charge.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/invoices',
                method: 'post',
                run: true,
              },
              upcoming: {
                type: 'command',
                description:
                  'At any time, you can preview the upcoming invoice for a customer. This will show you all the charges that are pending, including subscription renewal charges, invoice item charges, etc. It will also show you any discounts that are applicable to the invoice.  Note that when you are viewing an upcoming invoice, you are simply viewing a preview – the invoice has not yet been created. As such, the upcoming invoice will not show up in invoice listing calls, and you cannot use the API to pay or edit the invoice. If you want to change the amount that your customer will be billed, you can add, remove, or update pending invoice items, or update the customer’s discount.  You can preview the effects of updating a subscription, including a preview of what proration will take place. To ensure that the actual proration is calculated exactly the same as the previewed proration, you should pass a proration_date parameter when doing the actual subscription update. The value passed in should be the same as the subscription_proration_date returned on the upcoming invoice resource. The recommended way to get only the prorations being previewed is to consider only proration line items where period[start] is equal to the subscription_proration_date on the upcoming invoice resource.',
                params: {
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this invoice preview.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description:
                      "The code of the coupon to apply. If `subscription` or `subscription_items` is provided, the invoice returned will preview updating or creating a subscription with that coupon. Otherwise, it will preview applying that coupon to the customer for the next upcoming invoice from among the customer's subscriptions. The invoice can be previewed without a coupon by passing this value as an empty string.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "The identifier of the customer whose upcoming invoice you'd like to retrieve.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'customer-details': {
                    type: 'param',
                    description:
                      'Details about the customer you want to invoice or overrides for an existing customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      "The coupons to redeem into discounts for the invoice preview. If not specified, inherits the discount from the customer or subscription. This only works for coupons directly applied to the invoice. To apply a coupon to a subscription, you must use the `coupon` parameter instead. Pass an empty string to avoid inheriting any discounts. To preview the upcoming invoice for a subscription that hasn't been created, use `coupon` instead.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-items': {
                    type: 'param',
                    description:
                      'List of invoice items to add or update in the upcoming invoice preview.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  schedule: {
                    type: 'param',
                    description:
                      "The identifier of the unstarted schedule whose upcoming invoice you'd like to retrieve. Cannot be used with subscription or subscription fields.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      "The identifier of the subscription for which you'd like to retrieve the upcoming invoice. If not provided, but a `subscription_items` is provided, you will preview creating a subscription with those items. If neither `subscription` nor `subscription_items` is provided, you will retrieve the next upcoming invoice from among the customer's subscriptions.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'subscription-billing-cycle-anchor': {
                    type: 'param',
                    description:
                      "For new subscriptions, a future timestamp to anchor the subscription's billing cycle. This is used to determine the date of the first full invoice, and, for plans with `month` or `year` intervals, the day of the month for subsequent invoices. For existing subscriptions, the value can only be set to `now` or `unchanged`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-cancel-at': {
                    type: 'param',
                    description:
                      'Timestamp indicating when the subscription should be scheduled to cancel. Will prorate if within the current period and prorations have been enabled using `proration_behavior`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'DateTime',
                      },
                    },
                  },
                  'subscription-cancel-at-period-end': {
                    type: 'param',
                    description:
                      'Boolean indicating whether this subscription should cancel at the end of the current period.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'subscription-cancel-now': {
                    type: 'param',
                    description:
                      'This simulates the subscription being canceled or expired immediately.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'subscription-default-tax-rates': {
                    type: 'param',
                    description:
                      'If provided, the invoice returned will preview updating or creating a subscription with these default tax rates. The default tax rates will apply to any line item that does not have `tax_rates` set.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'subscription-items': {
                    type: 'param',
                    description:
                      'A list of up to 20 subscription items, each with an attached price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'subscription-proration-date': {
                    type: 'param',
                    description:
                      "If previewing an update to a subscription, and doing proration, `subscription-proration-date` forces the proration to be calculated as though the update was done at the specified time. The time given must be within the current subscription period, and cannot be before the subscription was on its current plan. If set, `subscription`, and one of `subscription_items`, or `subscription_trial_end` are required. Also, `subscription_proration_behavior` cannot be set to 'none'.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'subscription-start-date': {
                    type: 'param',
                    description:
                      'Date a subscription is intended to start (can be future or past)',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'subscription-trial-end': {
                    type: 'param',
                    description:
                      'If provided, the invoice returned will preview updating or creating a subscription with that trial end. If set, one of `subscription_items` or `subscription` is required.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-trial-from-plan': {
                    type: 'param',
                    description:
                      "Indicates if a plan's `trial_period_days` should be applied to the subscription. Setting `subscription_trial_end` per subscription is preferred, and this defaults to `false`. Setting this flag to `true` together with `subscription_trial_end` is not allowed. See Using trial periods on subscriptions to learn more.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: [],
                path: '/v1/invoices/upcoming',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the invoice with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Draft invoices are fully editable. Once an invoice is finalized, monetary values, as well as collection_method, become uneditable.  If you would like to stop the Stripe Billing engine from automatically finalizing, reattempting payments on, sending reminders for, or automatically reconciling invoices, pass auto_advance=false.',
                params: {
                  'account-tax-ids': {
                    type: 'param',
                    description:
                      'The account tax IDs associated with the invoice. Only editable when the invoice is a draft.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "A fee in %s that will be applied to the invoice and transferred to the application owner's Stripe account. The request must be made with an OAuth key or the Stripe-Account header in order to take an application fee. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'auto-advance': {
                    type: 'param',
                    description:
                      'Controls whether Stripe will perform automatic collection of the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically` or `send_invoice`. This field can be updated only on `draft` invoices.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  'custom-fields': {
                    type: 'param',
                    description:
                      'A list of up to 4 custom fields to be displayed on the invoice. If a value for `custom-fields` is specified, the list specified will replace the existing custom field list on this invoice. Pass an empty string to remove previously-defined fields.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'days-until-due': {
                    type: 'param',
                    description:
                      'The number of days from which the invoice is created until it is due. Only valid for invoices where `collection_method=send_invoice`. This field can only be updated on `draft` invoices.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'default-payment-method': {
                    type: 'param',
                    description:
                      "ID of the default payment method for the invoice. It must belong to the customer associated with the invoice. If not set, defaults to the subscription's default payment method, if any, or to the default payment method in the customer's invoice settings.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-source': {
                    type: 'param',
                    description:
                      "ID of the default payment source for the invoice. It must belong to the customer associated with the invoice and be in a chargeable state. If not set, defaults to the subscription's default source, if any, or to the customer's default source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any line item that does not have `tax_rates` set. Pass an empty string to remove previously-defined tax rates.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      "An arbitrary string attached to the object. Often useful for displaying to users. Referenced as 'memo' in the Dashboard.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      'The discounts that will apply to the invoice. Pass an empty string to remove previously-defined discounts.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'due-date': {
                    type: 'param',
                    description:
                      'The date on which payment for this invoice is due. Only valid for invoices where `collection_method=send_invoice`. This field can only be updated on `draft` invoices.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  footer: {
                    type: 'param',
                    description: 'Footer to be displayed on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description:
                      'The account (if any) for which the funds of the invoice payment are intended. If set, the invoice will be presented with the branding and support information of the specified account. See the Invoices with Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'payment-settings': {
                    type: 'param',
                    description:
                      'Configuration settings for the PaymentIntent that is generated when the invoice is finalized.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "Extra information about a charge for the customer's credit card statement. It must contain at least one letter. If not specified and this invoice is part of a subscription, the default `statement-descriptor` will be set to the first subscription item's product's `statement-descriptor`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      "If specified, the funds from the invoice will be transferred to the destination and the ID of the resulting transfer will be found on the invoice's charge. This will be unset if you POST an empty value.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Permanently deletes a one-off invoice draft. This cannot be undone. Attempts to delete invoices that are no longer in a draft state will fail; once an invoice has been finalized or if an invoice is for a subscription, it must be voided.',
                params: {},
                args: ['invoice'],
                path: '/v1/invoices/{invoice}',
                method: 'delete',
                run: true,
              },
              pay: {
                type: 'command',
                description:
                  'Stripe automatically creates and then attempts to collect payment on invoices for customers on subscriptions according to your subscriptions settings. However, if you’d like to attempt payment on an invoice out of the normal collection schedule or for some other reason, you can do so.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  forgive: {
                    type: 'param',
                    description:
                      "In cases where the source used to pay the invoice has insufficient funds, passing `forgive=true` controls whether a charge should be attempted for the full amount available on the source, up to the amount to fully pay the invoice. This effectively forgives the difference between the amount available on the source and the amount due.   Passing `forgive=false` will fail the charge if the source hasn't been pre-funded with the right amount. An example for this case is with ACH Credit Transfers and wires: if the amount wired is less than the amount due by a small amount, you might want to forgive the difference. Defaults to `false`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Indicates if a customer is on or off-session while an invoice payment is attempted. Defaults to `true` (off-session).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'paid-out-of-band': {
                    type: 'param',
                    description:
                      'Boolean representing whether an invoice is paid outside of Stripe. This will result in no charge being made. Defaults to `false`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'A PaymentMethod to be charged. The PaymentMethod must be the ID of a PaymentMethod belonging to the customer associated with the invoice being paid.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  source: {
                    type: 'param',
                    description:
                      'A payment source to be charged. The source must be the ID of a source belonging to the customer associated with the invoice being paid.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/pay',
                method: 'post',
                run: true,
              },
              finalize_invoice: {
                type: 'command',
                description:
                  'Stripe automatically finalizes drafts before sending and attempting payment on invoices. However, if you’d like to finalize a draft invoice manually, you can do so using this method.',
                params: {
                  'auto-advance': {
                    type: 'param',
                    description:
                      "Controls whether Stripe will perform automatic collection of the invoice. When `false`, the invoice's state will not automatically advance without an explicit action.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/finalize',
                method: 'post',
                run: true,
              },
              upcominglines: {
                type: 'command',
                description:
                  'When retrieving an upcoming invoice, you’ll get a lines property containing the total count of line items and the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this invoice preview.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description:
                      "The code of the coupon to apply. If `subscription` or `subscription_items` is provided, the invoice returned will preview updating or creating a subscription with that coupon. Otherwise, it will preview applying that coupon to the customer for the next upcoming invoice from among the customer's subscriptions. The invoice can be previewed without a coupon by passing this value as an empty string.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "The identifier of the customer whose upcoming invoice you'd like to retrieve.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'customer-details': {
                    type: 'param',
                    description:
                      'Details about the customer you want to invoice or overrides for an existing customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      "The coupons to redeem into discounts for the invoice preview. If not specified, inherits the discount from the customer or subscription. This only works for coupons directly applied to the invoice. To apply a coupon to a subscription, you must use the `coupon` parameter instead. Pass an empty string to avoid inheriting any discounts. To preview the upcoming invoice for a subscription that hasn't been created, use `coupon` instead.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-items': {
                    type: 'param',
                    description:
                      'List of invoice items to add or update in the upcoming invoice preview.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  schedule: {
                    type: 'param',
                    description:
                      "The identifier of the unstarted schedule whose upcoming invoice you'd like to retrieve. Cannot be used with subscription or subscription fields.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      "The identifier of the subscription for which you'd like to retrieve the upcoming invoice. If not provided, but a `subscription_items` is provided, you will preview creating a subscription with those items. If neither `subscription` nor `subscription_items` is provided, you will retrieve the next upcoming invoice from among the customer's subscriptions.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'subscription-billing-cycle-anchor': {
                    type: 'param',
                    description:
                      "For new subscriptions, a future timestamp to anchor the subscription's billing cycle. This is used to determine the date of the first full invoice, and, for plans with `month` or `year` intervals, the day of the month for subsequent invoices. For existing subscriptions, the value can only be set to `now` or `unchanged`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-cancel-at': {
                    type: 'param',
                    description:
                      'Timestamp indicating when the subscription should be scheduled to cancel. Will prorate if within the current period and prorations have been enabled using `proration_behavior`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'DateTime',
                      },
                    },
                  },
                  'subscription-cancel-at-period-end': {
                    type: 'param',
                    description:
                      'Boolean indicating whether this subscription should cancel at the end of the current period.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'subscription-cancel-now': {
                    type: 'param',
                    description:
                      'This simulates the subscription being canceled or expired immediately.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'subscription-default-tax-rates': {
                    type: 'param',
                    description:
                      'If provided, the invoice returned will preview updating or creating a subscription with these default tax rates. The default tax rates will apply to any line item that does not have `tax_rates` set.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'subscription-items': {
                    type: 'param',
                    description:
                      'A list of up to 20 subscription items, each with an attached price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'subscription-proration-date': {
                    type: 'param',
                    description:
                      "If previewing an update to a subscription, and doing proration, `subscription-proration-date` forces the proration to be calculated as though the update was done at the specified time. The time given must be within the current subscription period, and cannot be before the subscription was on its current plan. If set, `subscription`, and one of `subscription_items`, or `subscription_trial_end` are required. Also, `subscription_proration_behavior` cannot be set to 'none'.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'subscription-start-date': {
                    type: 'param',
                    description:
                      'Date a subscription is intended to start (can be future or past)',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'subscription-trial-end': {
                    type: 'param',
                    description:
                      'If provided, the invoice returned will preview updating or creating a subscription with that trial end. If set, one of `subscription_items` or `subscription` is required.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-trial-from-plan': {
                    type: 'param',
                    description:
                      "Indicates if a plan's `trial_period_days` should be applied to the subscription. Setting `subscription_trial_end` per subscription is preferred, and this defaults to `false`. Setting this flag to `true` together with `subscription_trial_end` is not allowed. See Using trial periods on subscriptions to learn more.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: [],
                path: '/v1/invoices/upcoming/lines',
                method: 'get',
                run: true,
              },
              send_invoice: {
                type: 'command',
                description:
                  'Stripe will automatically send invoices to customers according to your subscriptions settings. However, if you’d like to manually send an invoice to your customer out of the normal schedule, you can do so. When sending invoices that have already been paid, there will be no reference to the payment in the email.  Requests made in test-mode result in no emails being sent, despite sending an invoice.sent event.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/send',
                method: 'post',
                run: true,
              },
              mark_uncollectible: {
                type: 'command',
                description:
                  'Marking an invoice as uncollectible is useful for keeping track of bad debts that can be written off for accounting purposes.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/mark_uncollectible',
                method: 'post',
                run: true,
              },
              void_invoice: {
                type: 'command',
                description:
                  'Mark a finalized invoice as void. This cannot be undone. Voiding an invoice is similar to deletion, however it only applies to finalized invoices and maintains a papertrail where the invoice can still be found.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/void',
                method: 'post',
                run: true,
              },
            },
          },
          invoice_items: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of your invoice items. Invoice items are returned sorted by creation date, with the most recently created invoice items appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The identifier of the customer whose invoice items to return. If none is provided, all invoice items will be returned.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description:
                      'Only return invoice items belonging to this invoice. If none is provided, all invoice items will be returned. If specifying an invoice, no customer identifier is needed.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  pending: {
                    type: 'param',
                    description:
                      'Set to `true` to only show pending invoice items, which are not yet attached to any invoices. Set to `false` to only show invoice items already attached to invoices. If unspecified, no filter is applied.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/invoiceitems',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates an item to be added to a draft invoice (up to 250 items per invoice). If no invoice is specified, the item will be on the next invoice created for the customer specified.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'The integer amount in %s of the charge to be applied to the upcoming invoice. Passing in a negative `amount` will reduce the `amount_due` on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The ID of the customer who will be billed when this invoice item is billed.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to the invoice item. The description is displayed in the invoice for easy tracking.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discountable: {
                    type: 'param',
                    description:
                      'Controls whether discounts apply to this invoice item. Defaults to false for prorations or negative invoice items, and true for all other invoice items.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      'The coupons to redeem into discounts for the invoice item or invoice line item.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  invoice: {
                    type: 'param',
                    description:
                      'The ID of an existing invoice to add this invoice item to. When left blank, the invoice item will be added to the next upcoming scheduled invoice. This is useful when adding invoice items in response to an invoice.created webhook. You can only add invoice items to draft invoices and there is a maximum of 250 items per invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  period: {
                    type: 'param',
                    description:
                      'The period associated with this invoice item. When set to different values, the period will be rendered on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  price: {
                    type: 'param',
                    description: 'The ID of the price object.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'price-data': {
                    type: 'param',
                    description:
                      'Data used to generate a new Price object inline.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  quantity: {
                    type: 'param',
                    description:
                      'Non-negative integer. The quantity of units for the invoice item.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      'The ID of a subscription to add this invoice item to. When left blank, the invoice item will be be added to the next upcoming scheduled invoice. When set, scheduled invoices for subscriptions other than the specified subscription will ignore the invoice item. Use this when you want to express that an invoice item has been accrued within the context of a particular subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates which apply to the invoice item. When set, the `default_tax-rates` on the invoice do not apply to this invoice item.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'unit-amount': {
                    type: 'param',
                    description:
                      'The integer unit amount in %s of the charge to be applied to the upcoming invoice. This `unit-amount` will be multiplied by the quantity to get the full amount. Passing in a negative `unit-amount` will reduce the `amount_due` on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'unit-amount-decimal': {
                    type: 'param',
                    description:
                      'Same as `unit_amount`, but accepts a decimal value in %s with at most 12 decimal places. Only one of `unit_amount` and `unit-amount-decimal` can be set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'decimal_string',
                    },
                  },
                },
                args: [],
                path: '/v1/invoiceitems',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the invoice item with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['invoiceitem'],
                path: '/v1/invoiceitems/{invoiceitem}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the amount or description of an invoice item on an upcoming invoice. Updating an invoice item is only possible before the invoice it’s attached to is closed.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      "The integer amount in %s of the charge to be applied to the upcoming invoice. If you want to apply a credit to the customer's account, pass a negative amount.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to the invoice item. The description is displayed in the invoice for easy tracking.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discountable: {
                    type: 'param',
                    description:
                      'Controls whether discounts apply to this invoice item. Defaults to false for prorations or negative invoice items, and true for all other invoice items. Cannot be set to true for prorations.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      'The coupons & existing discounts which apply to the invoice item or invoice line item. Item discounts are applied before invoice discounts. Pass an empty string to remove previously-defined discounts.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  period: {
                    type: 'param',
                    description:
                      'The period associated with this invoice item. When set to different values, the period will be rendered on the invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  price: {
                    type: 'param',
                    description: 'The ID of the price object.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'price-data': {
                    type: 'param',
                    description:
                      'Data used to generate a new Price object inline.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  quantity: {
                    type: 'param',
                    description:
                      'Non-negative integer. The quantity of units for the invoice item.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates which apply to the invoice item. When set, the `default_tax-rates` on the invoice do not apply to this invoice item. Pass an empty string to remove previously-defined tax rates.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'unit-amount': {
                    type: 'param',
                    description:
                      "The integer unit amount in %s of the charge to be applied to the upcoming invoice. This unit-amount will be multiplied by the quantity to get the full amount. If you want to apply a credit to the customer's account, pass a negative unit-amount.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'unit-amount-decimal': {
                    type: 'param',
                    description:
                      'Same as `unit_amount`, but accepts a decimal value in %s with at most 12 decimal places. Only one of `unit_amount` and `unit-amount-decimal` can be set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'decimal_string',
                    },
                  },
                },
                args: ['invoiceitem'],
                path: '/v1/invoiceitems/{invoiceitem}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Deletes an invoice item, removing it from an invoice. Deleting invoice items is only possible when they’re not attached to invoices, or if it’s attached to a draft invoice.',
                params: {},
                args: ['invoiceitem'],
                path: '/v1/invoiceitems/{invoiceitem}',
                method: 'delete',
                run: true,
              },
            },
          },
          issuer_fraud_records: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of issuer fraud records.',
                params: {
                  charge: {
                    type: 'param',
                    description:
                      'Only return issuer fraud records for the charge specified by this charge ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/issuer_fraud_records',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an issuer fraud record that has previously been created.   Please refer to the issuer fraud record object reference for more details.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['issuer_fraud_record'],
                path: '/v1/issuer_fraud_records/{issuer_fraud_record}',
                method: 'get',
                run: true,
              },
            },
          },
          issuing: {
            type: 'command',
            commands: {
              authorizations: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Issuing Authorization objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      card: {
                        type: 'param',
                        description:
                          'Only return authorizations that belong to the given card.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      cardholder: {
                        type: 'param',
                        description:
                          'Only return authorizations that belong to the given cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      created: {
                        type: 'param',
                        description:
                          'Only return authorizations that were created during the given date interval.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Only return authorizations with the given status. One of `pending`, `closed`, or `reversed`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['closed', 'pending', 'reversed'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/authorizations',
                    method: 'get',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an Issuing Authorization object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['authorization'],
                    path: '/v1/issuing/authorizations/{authorization}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates the specified Issuing Authorization object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['authorization'],
                    path: '/v1/issuing/authorizations/{authorization}',
                    method: 'post',
                    run: true,
                  },
                  approve: {
                    type: 'command',
                    description:
                      'Approves a pending Issuing Authorization object. This request should be made within the timeout window of the real-time authorization flow.',
                    params: {
                      amount: {
                        type: 'param',
                        description:
                          "If the authorization's `pending_request.is_amount_controllable` property is `true`, you may provide this value to control how much to hold for the authorization. Must be positive (use `decline` to decline an authorization request).",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['authorization'],
                    path: '/v1/issuing/authorizations/{authorization}/approve',
                    method: 'post',
                    run: true,
                  },
                  decline: {
                    type: 'command',
                    description:
                      'Declines a pending Issuing Authorization object. This request should be made within the timeout window of the real time authorization flow.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['authorization'],
                    path: '/v1/issuing/authorizations/{authorization}/decline',
                    method: 'post',
                    run: true,
                  },
                },
              },
              cards: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Issuing Card objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      cardholder: {
                        type: 'param',
                        description:
                          'Only return cards belonging to the Cardholder with the provided ID.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      created: {
                        type: 'param',
                        description:
                          'Only return cards that were issued during the given date interval.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'exp-month': {
                        type: 'param',
                        description:
                          'Only return cards that have the given expiration month.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'exp-year': {
                        type: 'param',
                        description:
                          'Only return cards that have the given expiration year.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      last4: {
                        type: 'param',
                        description:
                          'Only return cards that have the given last four digits.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Only return cards that have the given status. One of `active`, `inactive`, or `canceled`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'canceled', 'inactive'],
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'Only return cards that have the given type. One of `virtual` or `physical`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['physical', 'virtual'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/cards',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description: 'Creates an Issuing Card object.',
                    params: {
                      cardholder: {
                        type: 'param',
                        description:
                          'The Cardholder object with which the card will be associated.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      currency: {
                        type: 'param',
                        description: 'The currency for the card.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'replacement-for': {
                        type: 'param',
                        description:
                          'The card this is meant to be a replacement for (if any).',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'replacement-reason': {
                        type: 'param',
                        description:
                          'If `replacement_for` is specified, this should indicate why that card is being replaced.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['damaged', 'expired', 'lost', 'stolen'],
                        },
                      },
                      shipping: {
                        type: 'param',
                        description:
                          'The address where the card will be shipped.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'spending-controls': {
                        type: 'param',
                        description:
                          'Rules that control spending for this card. Refer to our documentation for more details.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Whether authorizations can be approved on this card. Defaults to `inactive`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'inactive'],
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'The type of card to issue. Possible values are `physical` or `virtual`.',
                        required: true,
                        input_type: {
                          shape: 'enum',
                          enum: ['physical', 'virtual'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/cards',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an Issuing Card object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['card'],
                    path: '/v1/issuing/cards/{card}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates the specified Issuing Card object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      'cancellation-reason': {
                        type: 'param',
                        description:
                          'Reason why the `status` of this card is `canceled`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['lost', 'stolen'],
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                      pin: {
                        type: 'param',
                        description: 'The desired new PIN for this card.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'spending-controls': {
                        type: 'param',
                        description:
                          'Rules that control spending for this card. Refer to our documentation for more details.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Dictates whether authorizations can be approved on this card. If this card is being canceled because it was lost or stolen, this information should be provided as `cancellation_reason`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'canceled', 'inactive'],
                        },
                      },
                    },
                    args: ['card'],
                    path: '/v1/issuing/cards/{card}',
                    method: 'post',
                    run: true,
                  },
                },
              },
              cardholders: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Issuing Cardholder objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      created: {
                        type: 'param',
                        description:
                          'Only return cardholders that were created during the given date interval.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      email: {
                        type: 'param',
                        description:
                          'Only return cardholders that have the given email address.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'phone-number': {
                        type: 'param',
                        description:
                          'Only return cardholders that have the given phone number.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Only return cardholders that have the given status. One of `active`, `inactive`, or `blocked`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'blocked', 'inactive'],
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'Only return cardholders that have the given type. One of `individual` or `company`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['company', 'individual'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/cardholders',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new Issuing Cardholder object that can be issued cards.',
                    params: {
                      billing: {
                        type: 'param',
                        description: "The cardholder's billing address.",
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      company: {
                        type: 'param',
                        description:
                          'Additional information about a `company` cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      email: {
                        type: 'param',
                        description: "The cardholder's email address.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      individual: {
                        type: 'param',
                        description:
                          'Additional information about an `individual` cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      name: {
                        type: 'param',
                        description:
                          "The cardholder's name. This will be printed on cards issued to them. The maximum length of this field is 24 characters.",
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'phone-number': {
                        type: 'param',
                        description:
                          "The cardholder's phone number. This will be transformed to E.164 if it is not provided in that format already. This is required for all cardholders who will be creating EU cards. See the 3D Secure documentation for more details.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'spending-controls': {
                        type: 'param',
                        description:
                          "Rules that control spending across this cardholder's cards. Refer to our documentation for more details.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          "Specifies whether to permit authorizations on this cardholder's cards. Defaults to `active`.",
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'inactive'],
                        },
                      },
                      type: {
                        type: 'param',
                        description: 'One of `individual` or `company`.',
                        required: true,
                        input_type: {
                          shape: 'enum',
                          enum: ['company', 'individual'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/cardholders',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an Issuing Cardholder object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['cardholder'],
                    path: '/v1/issuing/cardholders/{cardholder}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates the specified Issuing Cardholder object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      billing: {
                        type: 'param',
                        description: "The cardholder's billing address.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      company: {
                        type: 'param',
                        description:
                          'Additional information about a `company` cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      email: {
                        type: 'param',
                        description: "The cardholder's email address.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      individual: {
                        type: 'param',
                        description:
                          'Additional information about an `individual` cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'phone-number': {
                        type: 'param',
                        description:
                          "The cardholder's phone number. This is required for all cardholders who will be creating EU cards. See the 3D Secure documentation for more details.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'spending-controls': {
                        type: 'param',
                        description:
                          "Rules that control spending across this cardholder's cards. Refer to our documentation for more details.",
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          "Specifies whether to permit authorizations on this cardholder's cards.",
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['active', 'inactive'],
                        },
                      },
                    },
                    args: ['cardholder'],
                    path: '/v1/issuing/cardholders/{cardholder}',
                    method: 'post',
                    run: true,
                  },
                },
              },
              disputes: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Issuing Dispute objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      created: {
                        type: 'param',
                        description:
                          'Select Issuing disputes that were created during the given date interval.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'Select Issuing disputes with the given status.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'expired',
                            'lost',
                            'submitted',
                            'unsubmitted',
                            'won',
                          ],
                        },
                      },
                      transaction: {
                        type: 'param',
                        description:
                          'Select the Issuing dispute for the given transaction.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/disputes',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates an Issuing Dispute object. Individual pieces of evidence within the evidence object are optional at this point. Stripe only validates that required evidence is present during submission. Refer to Dispute reasons and evidence for more details about evidence requirements.',
                    params: {
                      evidence: {
                        type: 'param',
                        description: 'Evidence provided for the dispute.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      transaction: {
                        type: 'param',
                        description:
                          'The ID of the issuing transaction to create a dispute for.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/disputes',
                    method: 'post',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates the specified Issuing Dispute object by setting the values of the parameters passed. Any parameters not provided will be left unchanged. Properties on the evidence object can be unset by passing in an empty string.',
                    params: {
                      evidence: {
                        type: 'param',
                        description: 'Evidence provided for the dispute.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['dispute'],
                    path: '/v1/issuing/disputes/{dispute}',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an Issuing Dispute object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['dispute'],
                    path: '/v1/issuing/disputes/{dispute}',
                    method: 'get',
                    run: true,
                  },
                  submit: {
                    type: 'command',
                    description:
                      'Submits an Issuing Dispute to the card network. Stripe validates that all evidence fields required for the dispute’s reason are present. For more details, see Dispute reasons and evidence.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['dispute'],
                    path: '/v1/issuing/disputes/{dispute}/submit',
                    method: 'post',
                    run: true,
                  },
                },
              },
              transactions: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Issuing Transaction objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      card: {
                        type: 'param',
                        description:
                          'Only return transactions that belong to the given card.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      cardholder: {
                        type: 'param',
                        description:
                          'Only return transactions that belong to the given cardholder.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      created: {
                        type: 'param',
                        description:
                          'Only return transactions that were created during the given date interval.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      type: {
                        type: 'param',
                        description:
                          'Only return transactions that have the given type. One of `capture` or `refund`.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['capture', 'refund'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/issuing/transactions',
                    method: 'get',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves an Issuing Transaction object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['transaction'],
                    path: '/v1/issuing/transactions/{transaction}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates the specified Issuing Transaction object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['transaction'],
                    path: '/v1/issuing/transactions/{transaction}',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          line_items: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'When retrieving a Checkout Session, there is an includable line_items property containing the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['session'],
                path: '/v1/checkout/sessions/{session}/line_items',
                method: 'get',
                run: true,
              },
            },
          },
          invoice_line_items: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'When retrieving an invoice, you’ll get a lines property containing the total count of line items and the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['invoice'],
                path: '/v1/invoices/{invoice}/lines',
                method: 'get',
                run: true,
              },
            },
          },
          login_links: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates a single-use login link for an Express account to access their Stripe dashboard.  You may only create login links for Express accounts connected to your platform.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'redirect-url': {
                    type: 'param',
                    description:
                      'Where to redirect the user after they log out of their dashboard.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/login_links',
                method: 'post',
                run: true,
              },
            },
          },
          mandates: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves a Mandate object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['mandate'],
                path: '/v1/mandates/{mandate}',
                method: 'get',
                run: true,
              },
            },
          },
          orders: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description: 'Creates a new order object.',
                params: {
                  coupon: {
                    type: 'param',
                    description:
                      'A coupon code that represents a discount to be applied to this order. Must be one-time duration and in same currency as the order. An order can have multiple coupons.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The ID of an existing customer to use for this order. If provided, the customer email and shipping address will be used to create the order. Subsequently, the customer will also be charged to pay the order. If `email` or `shipping` are also provided, they will override the values retrieved from the customer object.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      'The email address of the customer placing the order.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  items: {
                    type: 'param',
                    description:
                      'List of items constituting the order. An order can have up to 25 items.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      'Shipping address for the order. Required if any of the SKUs are for products that have `shippable` set to true.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/orders',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of your orders. The orders are returned sorted by creation date, with the most recently created orders appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: 'Date this order was created.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description: 'Only return orders for the given customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  ids: {
                    type: 'param',
                    description: 'Only return orders with the given IDs.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'Only return orders that have the given status. One of `created`, `paid`, `fulfilled`, or `refunded`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'status-transitions': {
                    type: 'param',
                    description:
                      'Filter orders based on when they were paid, fulfilled, canceled, or returned.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'upstream-ids': {
                    type: 'param',
                    description:
                      'Only return orders with the given upstream order IDs.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/orders',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing order. Supply the unique order ID from either an order creation request or the order list, and Stripe will return the corresponding order information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/orders/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specific order by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                params: {
                  coupon: {
                    type: 'param',
                    description:
                      'A coupon code that represents a discount to be applied to this order. Must be one-time duration and in same currency as the order. An order can have multiple coupons.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'selected-shipping-method': {
                    type: 'param',
                    description:
                      'The shipping method to select for fulfilling this order. If specified, must be one of the `id`s of a shipping method in the `shipping_methods` array. If specified, will overwrite the existing selected shipping method, updating `items` as necessary.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  shipping: {
                    type: 'param',
                    description:
                      'Tracking information once the order has been fulfilled.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'Current order status. One of `created`, `paid`, `canceled`, `fulfilled`, or `returned`. More detail in the Orders Guide.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'canceled',
                        'created',
                        'fulfilled',
                        'paid',
                        'returned',
                      ],
                    },
                  },
                },
                args: ['id'],
                path: '/v1/orders/{id}',
                method: 'post',
                run: true,
              },
              pay: {
                type: 'command',
                description:
                  'Pay an order by providing a source to create a payment.',
                params: {
                  'application-fee': {
                    type: 'param',
                    description:
                      "A fee in %s that will be applied to the order and transferred to the application owner's Stripe account. The request must be made with an OAuth key or the `Stripe-Account` header in order to take an application fee. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The ID of an existing customer that will be charged for this order. If no customer was attached to the order at creation, either `source` or `customer` is required. Otherwise, the specified customer will be charged instead of the one attached to the order.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      'The email address of the customer placing the order. Required if not previously specified for the order.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  source: {
                    type: 'param',
                    description:
                      "A Token's or a Source's ID, as returned by Elements. If no customer was attached to the order at creation, either `source` or `customer` is required. Otherwise, the specified source will be charged intead of the customer attached to the order.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/orders/{id}/pay',
                method: 'post',
                run: true,
              },
              return_order: {
                type: 'command',
                description:
                  'Return all or part of an order. The order must have a status of paid or fulfilled before it can be returned. Once all items have been returned, the order will become canceled or returned depending on which status the order started in.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  items: {
                    type: 'param',
                    description: 'List of items to return.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/orders/{id}/returns',
                method: 'post',
                run: true,
              },
            },
          },
          order_returns: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of your order returns. The returns are returned sorted by creation date, with the most recently created return appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: 'Date this return was created.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  order: {
                    type: 'param',
                    description: 'The order to retrieve returns for.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/order_returns',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing order return. Supply the unique order ID from either an order return creation request or the order return list, and Stripe will return the corresponding order information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/order_returns/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          payment_intents: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for PaymentIntents you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for payment intents.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_intents/search',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a PaymentIntent object.  After the PaymentIntent is created, attach a payment method and confirm to continue the payment. You can read more about the different payment flows available via the Payment Intents API here.  When confirm=true is used during creation, it is equivalent to creating and confirming the PaymentIntent in the same call. You may use any parameters available in the confirm API when confirm=true is supplied.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount intended to be collected by this PaymentIntent. A positive integer representing how much to charge in the smallest currency unit (e.g., 100 cents to charge $1.00 or 100 to charge ¥100, a zero-decimal currency). The minimum amount is $0.50 US or equivalent in charge currency. The amount value supports up to eight digits (e.g., a value of 99999999 for a USD charge of $999,999.99).',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. The amount of the application fee collected will be capped at the total payment amount. For more information, see the PaymentIntents use case for connected accounts.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'automatic-payment-methods': {
                    type: 'param',
                    description:
                      "When enabled, this PaymentIntent will accept payment methods that you have enabled in the Dashboard and are compatible with this PaymentIntent's other parameters.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'capture-method': {
                    type: 'param',
                    description:
                      "Controls when the funds will be captured from the customer's account.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['automatic', 'manual'],
                    },
                  },
                  confirm: {
                    type: 'param',
                    description:
                      'Set to `true` to attempt to confirm this PaymentIntent immediately. This parameter defaults to `false`. When creating and confirming a PaymentIntent at the same time, parameters available in the confirm API may also be provided.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'confirmation-method': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['automatic', 'manual'],
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "ID of the Customer this PaymentIntent belongs to, if one exists.  Payment methods attached to other Customers cannot be used with this PaymentIntent.  If present in combination with setup_future_usage, this PaymentIntent's payment method will be attached to the Customer after the PaymentIntent has been confirmed and any required actions from the user are complete.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'error-on-requires-action': {
                    type: 'param',
                    description:
                      'Set to `true` to fail the payment attempt if the PaymentIntent transitions into `requires_action`. This parameter is intended for simpler integrations that do not handle customer actions, like saving cards without authentication. This parameter can only be used with `confirm=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  mandate: {
                    type: 'param',
                    description:
                      'ID of the mandate to be used for this payment. This parameter can only be used with `confirm=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'mandate-data': {
                    type: 'param',
                    description:
                      'This hash contains details about the Mandate to create. This parameter can only be used with `confirm=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Set to `true` to indicate that the customer is not in your checkout flow during this payment attempt, and therefore is unable to authenticate. This parameter is intended for scenarios where you collect card details and charge them later. This parameter can only be used with `confirm=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description:
                      'The Stripe account ID for which these funds are intended. For details, see the PaymentIntents use case for connected accounts.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or compatible Source object) to attach to this PaymentIntent.  If neither the `payment-method` parameter nor the `source` parameter are provided with `confirm=true`, `source` will be automatically populated with `customer.default_source` to improve the migration experience for users of the Charges API. We recommend that you explicitly provide the `payment-method` going forward.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-data': {
                    type: 'param',
                    description:
                      'If provided, this hash will be used to create a PaymentMethod. The new PaymentMethod will appear in the payment_method property on the PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types (e.g. card) that this PaymentIntent is allowed to use. If this is not provided, defaults to ["card"].',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      'Email address that the receipt for the resulting payment will be sent to. If `receipt-email` is specified for a payment in live mode, a receipt will be sent regardless of your email settings.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      "The URL to redirect your customer back to after they authenticate or cancel their payment on the payment method's app or site. If you'd prefer to redirect to a mobile application, you can alternatively supply an application URI scheme. This parameter can only be used with `confirm=true`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'setup-future-usage': {
                    type: 'param',
                    description:
                      "Indicates that you intend to make future payments with this PaymentIntent's payment method.  Providing this parameter will attach the payment method to the PaymentIntent's Customer, if present, after the PaymentIntent is confirmed and any required actions from the user are complete. If no Customer was provided, the payment method can still be attached to a Customer after the transaction completes.  When processing card payments, Stripe also uses `setup-future-usage` to dynamically optimize your payment flow and comply with regional legislation and network rules, such as SCA.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['off_session', 'on_session'],
                    },
                  },
                  shipping: {
                    type: 'param',
                    description: 'Shipping information for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      'For non-card charges, you can use this value as the complete description that appears on your customers’ statements. Must contain at least one letter, maximum 22 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor-suffix': {
                    type: 'param',
                    description:
                      'Provides information about a card payment that customers see on their statements. Concatenated with the prefix (shortened descriptor) or statement descriptor that’s set on the account to form the complete statement descriptor. Maximum 22 characters for the concatenated descriptor.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The parameters used to automatically create a Transfer when the payment succeeds. For more information, see the PaymentIntents use case for connected accounts.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies the resulting payment as part of a group. See the PaymentIntents use case for connected accounts for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'use-stripe-sdk': {
                    type: 'param',
                    description:
                      'Set to `true` only when using manual confirmation and the iOS or Android SDKs to handle additional authentication steps.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_intents',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of PaymentIntents.',
                params: {
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return PaymentIntents for the customer specified by this customer ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_intents',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of a PaymentIntent that has previously been created.   Client-side retrieval using a publishable key is allowed when the client_secret is provided in the query string.   When retrieved with a publishable key, only a subset of properties will be returned. Please refer to the payment intent object reference for more details.',
                params: {
                  'client-secret': {
                    type: 'param',
                    description:
                      'The client secret of the PaymentIntent. Required if a publishable key is used to retrieve the source.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates properties on a PaymentIntent object without confirming.  Depending on which properties you update, you may need to confirm the PaymentIntent again. For example, updating the payment_method will always require you to confirm the PaymentIntent again. If you prefer to update and confirm at the same time, we recommend updating properties via the confirm API instead.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount intended to be collected by this PaymentIntent. A positive integer representing how much to charge in the smallest currency unit (e.g., 100 cents to charge $1.00 or 100 to charge ¥100, a zero-decimal currency). The minimum amount is $0.50 US or equivalent in charge currency. The amount value supports up to eight digits (e.g., a value of 99999999 for a USD charge of $999,999.99).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. The amount of the application fee collected will be capped at the total payment amount. For more information, see the PaymentIntents use case for connected accounts.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "ID of the Customer this PaymentIntent belongs to, if one exists.  Payment methods attached to other Customers cannot be used with this PaymentIntent.  If present in combination with setup_future_usage, this PaymentIntent's payment method will be attached to the Customer after the PaymentIntent has been confirmed and any required actions from the user are complete.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or compatible Source object) to attach to this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-data': {
                    type: 'param',
                    description:
                      'If provided, this hash will be used to create a PaymentMethod. The new PaymentMethod will appear in the payment_method property on the PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types (e.g. card) that this PaymentIntent is allowed to use.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      'Email address that the receipt for the resulting payment will be sent to. If `receipt-email` is specified for a payment in live mode, a receipt will be sent regardless of your email settings.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'setup-future-usage': {
                    type: 'param',
                    description:
                      "Indicates that you intend to make future payments with this PaymentIntent's payment method.  Providing this parameter will attach the payment method to the PaymentIntent's Customer, if present, after the PaymentIntent is confirmed and any required actions from the user are complete. If no Customer was provided, the payment method can still be attached to a Customer after the transaction completes.  When processing card payments, Stripe also uses `setup-future-usage` to dynamically optimize your payment flow and comply with regional legislation and network rules, such as SCA.  If `setup-future-usage` is already set and you are performing a request using a publishable key, you may only update the value from `on_session` to `off_session`.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['off_session', 'on_session'],
                      },
                    },
                  },
                  shipping: {
                    type: 'param',
                    description: 'Shipping information for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      'For non-card charges, you can use this value as the complete description that appears on your customers’ statements. Must contain at least one letter, maximum 22 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor-suffix': {
                    type: 'param',
                    description:
                      'Provides information about a card payment that customers see on their statements. Concatenated with the prefix (shortened descriptor) or statement descriptor that’s set on the account to form the complete statement descriptor. Maximum 22 characters for the concatenated descriptor.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The parameters used to automatically create a Transfer when the payment succeeds. For more information, see the PaymentIntents use case for connected accounts.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies the resulting payment as part of a group. `transfer-group` may only be provided if it has not been set. See the PaymentIntents use case for connected accounts for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}',
                method: 'post',
                run: true,
              },
              confirm: {
                type: 'command',
                description:
                  'Confirm that your customer intends to pay with current or provided payment method. Upon confirmation, the PaymentIntent will attempt to initiate a payment.  If the selected payment method requires additional authentication steps, the PaymentIntent will transition to the requires_action status and suggest additional actions via next_action. If payment fails, the PaymentIntent will transition to the requires_payment_method status. If payment succeeds, the PaymentIntent will transition to the succeeded status (or requires_capture, if capture_method is set to manual).  If the confirmation_method is automatic, payment may be attempted using our client SDKs and the PaymentIntent’s client_secret. After next_actions are handled by the client, no additional confirmation is required to complete the payment.  If the confirmation_method is manual, all payment attempts must be initiated using a secret key. If any actions are required for the payment, the PaymentIntent will return to the requires_confirmation state after those actions are completed. Your server needs to then explicitly re-confirm the PaymentIntent to initiate the next payment attempt. Read the expanded documentation to learn more about manual confirmation.',
                params: {
                  'error-on-requires-action': {
                    type: 'param',
                    description:
                      'Set to `true` to fail the payment attempt if the PaymentIntent transitions into `requires_action`. This parameter is intended for simpler integrations that do not handle customer actions, like saving cards without authentication.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  mandate: {
                    type: 'param',
                    description:
                      'ID of the mandate to be used for this payment.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'mandate-data': {
                    type: 'param',
                    description:
                      'This hash contains details about the Mandate to create',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Set to `true` to indicate that the customer is not in your checkout flow during this payment attempt, and therefore is unable to authenticate. This parameter is intended for scenarios where you collect card details and charge them later.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or compatible Source object) to attach to this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-data': {
                    type: 'param',
                    description:
                      'If provided, this hash will be used to create a PaymentMethod. The new PaymentMethod will appear in the payment_method property on the PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'receipt-email': {
                    type: 'param',
                    description:
                      'Email address that the receipt for the resulting payment will be sent to. If `receipt-email` is specified for a payment in live mode, a receipt will be sent regardless of your email settings.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      "The URL to redirect your customer back to after they authenticate or cancel their payment on the payment method's app or site. If you'd prefer to redirect to a mobile application, you can alternatively supply an application URI scheme. This parameter is only used for cards and other redirect-based payment methods.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'setup-future-usage': {
                    type: 'param',
                    description:
                      "Indicates that you intend to make future payments with this PaymentIntent's payment method.  Providing this parameter will attach the payment method to the PaymentIntent's Customer, if present, after the PaymentIntent is confirmed and any required actions from the user are complete. If no Customer was provided, the payment method can still be attached to a Customer after the transaction completes.  When processing card payments, Stripe also uses `setup-future-usage` to dynamically optimize your payment flow and comply with regional legislation and network rules, such as SCA.  If `setup-future-usage` is already set and you are performing a request using a publishable key, you may only update the value from `on_session` to `off_session`.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'enum',
                        enum: ['off_session', 'on_session'],
                      },
                    },
                  },
                  shipping: {
                    type: 'param',
                    description: 'Shipping information for this PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'use-stripe-sdk': {
                    type: 'param',
                    description:
                      'Set to `true` only when using manual confirmation and the iOS or Android SDKs to handle additional authentication steps.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}/confirm',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'A PaymentIntent object can be canceled when it is in one of these statuses: requires_payment_method, requires_capture, requires_confirmation, requires_action, or processing.   Once canceled, no additional charges will be made by the PaymentIntent and any operations on the PaymentIntent will fail with an error. For PaymentIntents with status=’requires_capture’, the remaining amount_capturable will automatically be refunded.   You cannot cancel the PaymentIntent for a Checkout Session. Expire the Checkout Session instead',
                params: {
                  'cancellation-reason': {
                    type: 'param',
                    description:
                      'Reason for canceling this PaymentIntent. Possible values are `duplicate`, `fraudulent`, `requested_by_customer`, or `abandoned`',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'abandoned',
                        'duplicate',
                        'fraudulent',
                        'requested_by_customer',
                      ],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}/cancel',
                method: 'post',
                run: true,
              },
              capture: {
                type: 'command',
                description:
                  'Capture the funds of an existing uncaptured PaymentIntent when its status is requires_capture.  Uncaptured PaymentIntents will be canceled a set number of days after they are created (7 by default).  Learn more about separate authorization and capture.',
                params: {
                  'amount-to-capture': {
                    type: 'param',
                    description:
                      'The amount to capture from the PaymentIntent, which must be less than or equal to the original amount. Any additional amount will be automatically refunded. Defaults to the full `amount_capturable` if not provided.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. The amount of the application fee collected will be capped at the total payment amount. For more information, see the PaymentIntents use case for connected accounts.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      'For non-card charges, you can use this value as the complete description that appears on your customers’ statements. Must contain at least one letter, maximum 22 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor-suffix': {
                    type: 'param',
                    description:
                      'Provides information about a card payment that customers see on their statements. Concatenated with the prefix (shortened descriptor) or statement descriptor that’s set on the account to form the complete statement descriptor. Maximum 22 characters for the concatenated descriptor.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The parameters used to automatically create a Transfer when the payment is captured. For more information, see the PaymentIntents use case for connected accounts.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}/capture',
                method: 'post',
                run: true,
              },
              verify_microdeposits: {
                type: 'command',
                description:
                  'Verifies microdeposits on a PaymentIntent object.',
                params: {
                  amounts: {
                    type: 'param',
                    description:
                      'Two positive integers, in *cents*, equal to the values of the microdeposits sent to the bank account.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  'descriptor-code': {
                    type: 'param',
                    description:
                      'A six-character code starting with SM present in the microdeposit sent to the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}/verify_microdeposits',
                method: 'post',
                run: true,
              },
              apply_customer_balance: {
                type: 'command',
                description:
                  'Manually reconcile the remaining amount for a customer_balance PaymentIntent.  This can be used when the cash balance for a customer in manual reconciliation mode received funds.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount intended to be applied to this PaymentIntent from the customer’s cash balance.  A positive integer representing how much to charge in the smallest currency unit (e.g., 100 cents to charge $1.00 or 100 to charge ¥100, a zero-decimal currency).  The maximum amount is the amount of the PaymentIntent.  When omitted, the amount defaults to the remaining amount requested on the PaymentIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/payment_intents/{intent}/apply_customer_balance',
                method: 'post',
                run: true,
              },
            },
          },
          payment_links: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your payment links.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return payment links that are active or inactive (e.g., pass `false` to list all inactive payment links).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_links',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a payment link.',
                params: {
                  'after-completion': {
                    type: 'param',
                    description: 'Behavior after the purchase is complete.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'allow-promotion-codes': {
                    type: 'param',
                    description: 'Enables user redeemable promotion codes.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. Can only be applied when there are no line items with recurring prices.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'application-fee-percent': {
                    type: 'param',
                    description:
                      "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice subtotal that will be transferred to the application owner's Stripe account. There must be at least 1 line item with a recurring price to use this field.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'number',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description: 'Configuration for automatic tax collection.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'billing-address-collection': {
                    type: 'param',
                    description:
                      "Configuration for collecting the customer's billing address.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['auto', 'required'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'line-items': {
                    type: 'param',
                    description:
                      'The line items representing what is being sold. Each line item represents an item being sold. Up to 20 line items are supported.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`. Metadata associated with this Payment Link will automatically be copied to checkout sessions created by this payment link.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description: 'The account on behalf of which to charge.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types that customers can use. Only `card` is supported. If no value is passed, Stripe will dynamically show relevant payment methods from your payment method settings (20+ payment methods supported).',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'literal',
                        literal: 'card',
                      },
                    },
                  },
                  'phone-number-collection': {
                    type: 'param',
                    description:
                      'Controls phone number collection settings during checkout.  We recommend that you review your privacy policy and check with your legal contacts.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'shipping-address-collection': {
                    type: 'param',
                    description:
                      "Configuration for collecting the customer's shipping address.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'subscription-data': {
                    type: 'param',
                    description:
                      'When creating a subscription, the specified configuration data will be used. There must be at least one line item with a recurring price to use `subscription-data`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The account (if any) the payments will be attributed to for tax reporting, and where funds from each payment will be transferred to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_links',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieve a payment link.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payment_link'],
                path: '/v1/payment_links/{payment_link}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates a payment link.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      "Whether the payment link's `url` is active. If `false`, customers visiting the URL will be shown a page saying that the link has been deactivated.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'after-completion': {
                    type: 'param',
                    description: 'Behavior after the purchase is complete.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'allow-promotion-codes': {
                    type: 'param',
                    description: 'Enables user redeemable promotion codes.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description: 'Configuration for automatic tax collection.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'billing-address-collection': {
                    type: 'param',
                    description:
                      "Configuration for collecting the customer's billing address.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['auto', 'required'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'line-items': {
                    type: 'param',
                    description:
                      'The line items representing what is being sold. Each line item represents an item being sold. Up to 20 line items are supported.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`. Metadata associated with this Payment Link will automatically be copied to checkout sessions created by this payment link.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types that customers can use. Only `card` is supported. Pass an empty string to enable automatic payment methods that use your payment method settings.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'literal',
                          literal: 'card',
                        },
                      },
                    },
                  },
                  'shipping-address-collection': {
                    type: 'param',
                    description:
                      "Configuration for collecting the customer's shipping address.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                },
                args: ['payment_link'],
                path: '/v1/payment_links/{payment_link}',
                method: 'post',
                run: true,
              },
              list_line_items: {
                type: 'command',
                description:
                  'When retrieving a payment link, there is an includable line_items property containing the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['payment_link'],
                path: '/v1/payment_links/{payment_link}/line_items',
                method: 'get',
                run: true,
              },
            },
          },
          payment_methods: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates a PaymentMethod object. Read the Stripe.js reference to learn how to create PaymentMethods via Stripe.js.  Instead of creating a PaymentMethod directly, we recommend using the PaymentIntents API to accept a payment immediately or the SetupIntent API to collect payment method details ahead of a future payment.',
                params: {
                  'acss-debit': {
                    type: 'param',
                    description:
                      'If this is an `acss-debit` PaymentMethod, this hash contains details about the ACSS Debit payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'afterpay-clearpay': {
                    type: 'param',
                    description:
                      'If this is an `AfterpayClearpay` PaymentMethod, this hash contains details about the AfterpayClearpay payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  alipay: {
                    type: 'param',
                    description:
                      'If this is an `Alipay` PaymentMethod, this hash contains details about the Alipay payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'au-becs-debit': {
                    type: 'param',
                    description:
                      'If this is an `au-becs-debit` PaymentMethod, this hash contains details about the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'bacs-debit': {
                    type: 'param',
                    description:
                      'If this is a `bacs-debit` PaymentMethod, this hash contains details about the Bacs Direct Debit bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  bancontact: {
                    type: 'param',
                    description:
                      'If this is a `bancontact` PaymentMethod, this hash contains details about the Bancontact payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'billing-details': {
                    type: 'param',
                    description:
                      'Billing information associated with the PaymentMethod that may be used or required by particular types of payment methods.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  boleto: {
                    type: 'param',
                    description:
                      'If this is a `boleto` PaymentMethod, this hash contains details about the Boleto payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  card: {
                    type: 'param',
                    description:
                      'If this is a `card` PaymentMethod, this hash contains the user\'s card details. For backwards compatibility, you can alternatively provide a Stripe token (e.g., for Apple Pay, Amex Express Checkout, or legacy Checkout) into the card hash with format `card: {token: "tok_visa"}`. When providing a card number, you must meet the requirements for PCI compliance. We strongly recommend using Stripe.js instead of interacting with this API directly.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The `Customer` to whom the original PaymentMethod is attached.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  eps: {
                    type: 'param',
                    description:
                      'If this is an `eps` PaymentMethod, this hash contains details about the EPS payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  fpx: {
                    type: 'param',
                    description:
                      'If this is an `fpx` PaymentMethod, this hash contains details about the FPX payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  giropay: {
                    type: 'param',
                    description:
                      'If this is a `giropay` PaymentMethod, this hash contains details about the Giropay payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  grabpay: {
                    type: 'param',
                    description:
                      'If this is a `grabpay` PaymentMethod, this hash contains details about the GrabPay payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  ideal: {
                    type: 'param',
                    description:
                      'If this is an `ideal` PaymentMethod, this hash contains details about the iDEAL payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'interac-present': {
                    type: 'param',
                    description:
                      'If this is an `interac-present` PaymentMethod, this hash contains details about the Interac Present payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  klarna: {
                    type: 'param',
                    description:
                      'If this is a `klarna` PaymentMethod, this hash contains details about the Klarna payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  konbini: {
                    type: 'param',
                    description:
                      'If this is a `konbini` PaymentMethod, this hash contains details about the Konbini payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  oxxo: {
                    type: 'param',
                    description:
                      'If this is an `oxxo` PaymentMethod, this hash contains details about the OXXO payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  p24: {
                    type: 'param',
                    description:
                      'If this is a `p24` PaymentMethod, this hash contains details about the P24 payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description: 'The PaymentMethod to share.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  paynow: {
                    type: 'param',
                    description:
                      'If this is a `paynow` PaymentMethod, this hash contains details about the PayNow payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'sepa-debit': {
                    type: 'param',
                    description:
                      'If this is a `sepa-debit` PaymentMethod, this hash contains details about the SEPA debit bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  sofort: {
                    type: 'param',
                    description:
                      'If this is a `sofort` PaymentMethod, this hash contains details about the SOFORT payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The type of the PaymentMethod. An additional hash is included on the PaymentMethod with a name matching this value. It contains additional information specific to the PaymentMethod type.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'acss_debit',
                        'afterpay_clearpay',
                        'alipay',
                        'au_becs_debit',
                        'bacs_debit',
                        'bancontact',
                        'boleto',
                        'card',
                        'eps',
                        'fpx',
                        'giropay',
                        'grabpay',
                        'ideal',
                        'klarna',
                        'konbini',
                        'oxxo',
                        'p24',
                        'paynow',
                        'sepa_debit',
                        'sofort',
                        'us_bank_account',
                        'wechat_pay',
                      ],
                    },
                  },
                  'us-bank-account': {
                    type: 'param',
                    description:
                      'If this is an `us-bank-account` PaymentMethod, this hash contains details about the US bank account payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'wechat-pay': {
                    type: 'param',
                    description:
                      'If this is an `wechat-pay` PaymentMethod, this hash contains details about the wechat-pay payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/payment_methods',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of PaymentMethods. For listing a customer’s payment methods, you should use List a Customer’s PaymentMethods',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'The ID of the customer whose PaymentMethods will be retrieved. If not provided, the response list will be empty.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'A required filter on the list, based on the object `type` field.',
                    required: true,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'acss_debit',
                        'afterpay_clearpay',
                        'alipay',
                        'au_becs_debit',
                        'bacs_debit',
                        'bancontact',
                        'boleto',
                        'card',
                        'card_present',
                        'eps',
                        'fpx',
                        'giropay',
                        'grabpay',
                        'ideal',
                        'klarna',
                        'konbini',
                        'oxxo',
                        'p24',
                        'paynow',
                        'sepa_debit',
                        'sofort',
                        'us_bank_account',
                        'wechat_pay',
                      ],
                    },
                  },
                },
                args: [],
                path: '/v1/payment_methods',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves a PaymentMethod object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payment_method'],
                path: '/v1/payment_methods/{payment_method}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates a PaymentMethod object. A PaymentMethod must be attached a customer to be updated.',
                params: {
                  'acss-debit': {
                    type: 'param',
                    description:
                      'This is a legacy parameter that will be removed in the future. It is a hash that does not accept any keys.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'au-becs-debit': {
                    type: 'param',
                    description:
                      'This is a legacy parameter that will be removed in the future. It is a hash that does not accept any keys.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'bacs-debit': {
                    type: 'param',
                    description:
                      'This is a legacy parameter that will be removed in the future. It is a hash that does not accept any keys.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'billing-details': {
                    type: 'param',
                    description:
                      'Billing information associated with the PaymentMethod that may be used or required by particular types of payment methods.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  card: {
                    type: 'param',
                    description:
                      "If this is a `card` PaymentMethod, this hash contains the user's card details.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'sepa-debit': {
                    type: 'param',
                    description:
                      'This is a legacy parameter that will be removed in the future. It is a hash that does not accept any keys.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'us-bank-account': {
                    type: 'param',
                    description:
                      'If this is an `us-bank-account` PaymentMethod, this hash contains details about the US bank account payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['payment_method'],
                path: '/v1/payment_methods/{payment_method}',
                method: 'post',
                run: true,
              },
              attach: {
                type: 'command',
                description:
                  'Attaches a PaymentMethod object to a Customer.  To attach a new PaymentMethod to a customer for future payments, we recommend you use a SetupIntent or a PaymentIntent with setup_future_usage. These approaches will perform any necessary steps to ensure that the PaymentMethod can be used in a future payment. Using the /v1/payment_methods/:id/attach endpoint does not ensure that future payments can be made with the attached PaymentMethod. See Optimizing cards for future payments for more information about setting up future payments.  To use this PaymentMethod as the default for invoice or subscription payments, set invoice_settings.default_payment_method, on the Customer to the PaymentMethod’s ID.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'The ID of the customer to which to attach the PaymentMethod.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payment_method'],
                path: '/v1/payment_methods/{payment_method}/attach',
                method: 'post',
                run: true,
              },
              detach: {
                type: 'command',
                description:
                  'Detaches a PaymentMethod object from a Customer. After a PaymentMethod is detached, it can no longer be used for a payment or re-attached to a Customer.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payment_method'],
                path: '/v1/payment_methods/{payment_method}/detach',
                method: 'post',
                run: true,
              },
            },
          },
          payment_sources: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'List sources for a specified customer.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  object: {
                    type: 'param',
                    description:
                      'Filter sources according to a particular object type.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/sources',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'When you create a new credit card, you must specify a customer or recipient on which to create it.  If the card’s owner has no default card, then the new card will become the default. However, if the owner already has a default, then it will not change. To change the default, you should update the customer to have a new default_source.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  source: {
                    type: 'param',
                    description: 'Please refer to full documentation instead.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/sources',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieve a specified source for a given customer.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer', 'id'],
                path: '/v1/customers/{customer}/sources/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          payouts: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing payout. Supply the unique payout ID from either a payout creation request or the payout list, and Stripe will return the corresponding payout information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payout'],
                path: '/v1/payouts/{payout}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified payout by setting the values of the parameters passed. Any parameters not provided will be left unchanged. This request accepts only the metadata as arguments.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['payout'],
                path: '/v1/payouts/{payout}',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of existing payouts sent to third-party bank accounts or that Stripe has sent you. The payouts are returned in sorted order, with the most recently created payouts appearing first.',
                params: {
                  'arrival-date': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  destination: {
                    type: 'param',
                    description:
                      'The ID of an external account - only return payouts sent to this external account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'Only return payouts that have the given status: `pending`, `paid`, `failed`, or `canceled`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/payouts',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'To send funds to your own bank account, you create a new payout object. Your Stripe balance must be able to cover the payout amount, or you’ll receive an “Insufficient Funds” error.  If your API key is in test mode, money won’t actually be sent, though everything else will occur as if in live mode.  If you are creating a manual payout on a Stripe account that uses multiple payment source types, you’ll need to specify the source type balance that the payout should draw from. The balance object details available and pending amounts by source type.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer in cents representing how much to payout.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  destination: {
                    type: 'param',
                    description:
                      'The ID of a bank account or a card to send the payout to. If no destination is supplied, the default external account for the specified currency will be used.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  method: {
                    type: 'param',
                    description:
                      'The method used to send this payout, which can be `standard` or `instant`. `instant` is only supported for payouts to debit cards. (See Instant payouts for marketplaces for more information.)',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['instant', 'standard'],
                    },
                  },
                  'source-type': {
                    type: 'param',
                    description:
                      'The balance type of your Stripe balance to draw this payout from. Balances for different payment sources are kept separately. You can find the amounts with the balances API. One of `bank_account`, `card`, or `fpx`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['bank_account', 'card', 'fpx'],
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "A string to be displayed on the recipient's bank or card statement. This may be at most 22 characters. Attempting to use a `statement-descriptor` longer than 22 characters will return an error. Note: Most banks will truncate this information and/or display it inconsistently. Some may not display it at all.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/payouts',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'A previously created payout can be canceled if it has not yet been paid out. Funds will be refunded to your available balance. You may not cancel automatic Stripe payouts.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payout'],
                path: '/v1/payouts/{payout}/cancel',
                method: 'post',
                run: true,
              },
              reverse: {
                type: 'command',
                description:
                  'Reverses a payout by debiting the destination bank account. Only payouts for connected accounts to US bank accounts may be reversed at this time. If the payout is in the pending status, /v1/payouts/:id/cancel should be used instead.  By requesting a reversal via /v1/payouts/:id/reverse, you confirm that the authorized signatory of the selected bank account has authorized the debit on the bank account and that no other authorization is required.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['payout'],
                path: '/v1/payouts/{payout}/reverse',
                method: 'post',
                run: true,
              },
            },
          },
          persons: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of people associated with the account’s legal entity. The people are returned sorted by creation date, with the most recent people appearing first.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  relationship: {
                    type: 'param',
                    description:
                      "Filters on the list of people returned based on the person's relationship to the account's company.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/persons',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new person.',
                params: {
                  address: {
                    type: 'param',
                    description: "The person's address.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'address-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's address (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'address-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's address (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  dob: {
                    type: 'param',
                    description: "The person's date of birth.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  documents: {
                    type: 'param',
                    description:
                      'Documents that may be submitted to satisfy various informational requests.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  email: {
                    type: 'param',
                    description: "The person's email address.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'first-name': {
                    type: 'param',
                    description: "The person's first name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'first-name-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's first name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'first-name-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's first name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'full-name-aliases': {
                    type: 'param',
                    description:
                      'A list of alternate names or aliases that the person is known by.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  gender: {
                    type: 'param',
                    description:
                      'The person\'s gender (International regulations require either "male" or "female").',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'id-number': {
                    type: 'param',
                    description:
                      "The person's ID number, as appropriate for their country. For example, a social security number in the U.S., social insurance number in Canada, etc. Instead of the number itself, you can also provide a PII token provided by Stripe.js.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name': {
                    type: 'param',
                    description: "The person's last name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's last name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's last name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'maiden-name': {
                    type: 'param',
                    description: "The person's maiden name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  nationality: {
                    type: 'param',
                    description:
                      'The country where the person is a national. Two-letter country code (ISO 3166-1 alpha-2), or "XX" if unavailable.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'person-token': {
                    type: 'param',
                    description:
                      'A person token, used to securely provide details to the person.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  phone: {
                    type: 'param',
                    description: "The person's phone number.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'political-exposure': {
                    type: 'param',
                    description:
                      'Indicates if the person or any of their representatives, family members, or other closely related persons, declares that they hold or have held an important public job or function, in any jurisdiction.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  relationship: {
                    type: 'param',
                    description:
                      "The relationship that this person has with the account's legal entity.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ssn-last-4': {
                    type: 'param',
                    description:
                      "The last four digits of the person's Social Security number (U.S. only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  verification: {
                    type: 'param',
                    description: "The person's verification status.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['account'],
                path: '/v1/accounts/{account}/persons',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves an existing person.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['account', 'person'],
                path: '/v1/accounts/{account}/persons/{person}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing person.',
                params: {
                  address: {
                    type: 'param',
                    description: "The person's address.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'address-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's address (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'address-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's address (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  dob: {
                    type: 'param',
                    description: "The person's date of birth.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  documents: {
                    type: 'param',
                    description:
                      'Documents that may be submitted to satisfy various informational requests.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  email: {
                    type: 'param',
                    description: "The person's email address.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'first-name': {
                    type: 'param',
                    description: "The person's first name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'first-name-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's first name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'first-name-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's first name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'full-name-aliases': {
                    type: 'param',
                    description:
                      'A list of alternate names or aliases that the person is known by.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  gender: {
                    type: 'param',
                    description:
                      'The person\'s gender (International regulations require either "male" or "female").',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'id-number': {
                    type: 'param',
                    description:
                      "The person's ID number, as appropriate for their country. For example, a social security number in the U.S., social insurance number in Canada, etc. Instead of the number itself, you can also provide a PII token provided by Stripe.js.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name': {
                    type: 'param',
                    description: "The person's last name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name-kana': {
                    type: 'param',
                    description:
                      "The Kana variation of the person's last name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'last-name-kanji': {
                    type: 'param',
                    description:
                      "The Kanji variation of the person's last name (Japan only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'maiden-name': {
                    type: 'param',
                    description: "The person's maiden name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  nationality: {
                    type: 'param',
                    description:
                      'The country where the person is a national. Two-letter country code (ISO 3166-1 alpha-2), or "XX" if unavailable.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'person-token': {
                    type: 'param',
                    description:
                      'A person token, used to securely provide details to the person.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  phone: {
                    type: 'param',
                    description: "The person's phone number.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'political-exposure': {
                    type: 'param',
                    description:
                      'Indicates if the person or any of their representatives, family members, or other closely related persons, declares that they hold or have held an important public job or function, in any jurisdiction.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  relationship: {
                    type: 'param',
                    description:
                      "The relationship that this person has with the account's legal entity.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ssn-last-4': {
                    type: 'param',
                    description:
                      "The last four digits of the person's Social Security number (U.S. only).",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  verification: {
                    type: 'param',
                    description: "The person's verification status.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['account', 'person'],
                path: '/v1/accounts/{account}/persons/{person}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Deletes an existing person’s relationship to the account’s legal entity. Any person with a relationship for an account can be deleted through the API, except if the person is the account_opener. If your integration is using the executive parameter, you cannot delete the only verified executive on file.',
                params: {},
                args: ['account', 'person'],
                path: '/v1/accounts/{account}/persons/{person}',
                method: 'delete',
                run: true,
              },
            },
          },
          plans: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your plans.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return plans that are active or inactive (e.g., pass `false` to list all inactive plans).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  product: {
                    type: 'param',
                    description: 'Only return plans for the given product.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/plans',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'You can now model subscriptions more flexibly using the Prices API. It replaces the Plans API and is backwards compatible to simplify your migration.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the plan is currently available for new subscriptions. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'aggregate-usage': {
                    type: 'param',
                    description:
                      'Specifies a usage aggregation strategy for plans of `usage_type=metered`. Allowed values are `sum` for summing up all usage during a period, `last_during_period` for using the last usage record reported within a period, `last_ever` for using the last usage record ever (across period bounds) or `max` which uses the usage record with the maximum reported usage during a period. Defaults to `sum`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['last_during_period', 'last_ever', 'max', 'sum'],
                    },
                  },
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer in %s (or 0 for a free plan) representing how much to charge on a recurring basis.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'amount-decimal': {
                    type: 'param',
                    description:
                      'Same as `amount`, but accepts a decimal value with at most 12 decimal places. Only one of `amount` and `amount-decimal` can be set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'decimal_string',
                    },
                  },
                  'billing-scheme': {
                    type: 'param',
                    description:
                      'Describes how to compute the price per period. Either `per_unit` or `tiered`. `per_unit` indicates that the fixed amount (specified in `amount`) will be charged per unit in `quantity` (for plans with `usage_type=licensed`), or per unit of total usage (for plans with `usage_type=metered`). `tiered` indicates that the unit pricing will be computed using a tiering strategy as defined using the `tiers` and `tiers_mode` attributes.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['per_unit', 'tiered'],
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  id: {
                    type: 'param',
                    description:
                      'An identifier randomly generated by Stripe. Used to identify this plan when subscribing a customer. You can optionally override this ID, but the ID must be unique across all plans in your Stripe account. You can, however, use the same plan ID in both live and test modes.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  interval: {
                    type: 'param',
                    description:
                      'Specifies billing frequency. Either `day`, `week`, `month` or `year`.',
                    required: true,
                    input_type: {
                      shape: 'enum',
                      enum: ['day', 'month', 'week', 'year'],
                    },
                  },
                  'interval-count': {
                    type: 'param',
                    description:
                      'The number of intervals between subscription billings. For example, `interval=month` and `interval-count=3` bills every 3 months. Maximum of one year interval allowed (1 year, 12 months, or 52 weeks).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  nickname: {
                    type: 'param',
                    description:
                      'A brief description of the plan, hidden from customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  product: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  tiers: {
                    type: 'param',
                    description:
                      'Each element represents a pricing tier. This parameter requires `billing_scheme` to be set to `tiered`. See also the documentation for `billing_scheme`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tiers-mode': {
                    type: 'param',
                    description:
                      'Defines if the tiering price should be `graduated` or `volume` based. In `volume`-based tiering, the maximum quantity within a period determines the per unit price, in `graduated` tiering pricing can successively change as the quantity grows.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['graduated', 'volume'],
                    },
                  },
                  'transform-usage': {
                    type: 'param',
                    description:
                      'Apply a transformation to the reported usage or set quantity before computing the billed price. Cannot be combined with `tiers`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'trial-period-days': {
                    type: 'param',
                    description:
                      'Default number of trial days when subscribing a customer to this plan using `trial_from_plan=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'usage-type': {
                    type: 'param',
                    description:
                      'Configures how the quantity per period should be determined. Can be either `metered` or `licensed`. `licensed` automatically bills the `quantity` set when adding it to a subscription. `metered` aggregates the total usage based on usage records. Defaults to `licensed`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['licensed', 'metered'],
                    },
                  },
                },
                args: [],
                path: '/v1/plans',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the plan with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['plan'],
                path: '/v1/plans/{plan}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified plan by setting the values of the parameters passed. Any parameters not provided are left unchanged. By design, you cannot change a plan’s ID, amount, currency, or billing cycle.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the plan is currently available for new subscriptions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  nickname: {
                    type: 'param',
                    description:
                      'A brief description of the plan, hidden from customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  product: {
                    type: 'param',
                    description:
                      'The product the plan belongs to. This cannot be changed once it has been used in a subscription or subscription schedule.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'trial-period-days': {
                    type: 'param',
                    description:
                      'Default number of trial days when subscribing a customer to this plan using `trial_from_plan=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                },
                args: ['plan'],
                path: '/v1/plans/{plan}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Deleting plans means new subscribers can’t be added. Existing subscribers aren’t affected.',
                params: {},
                args: ['plan'],
                path: '/v1/plans/{plan}',
                method: 'delete',
                run: true,
              },
            },
          },
          prices: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for prices you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for prices.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/prices/search',
                method: 'get',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of your prices.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return prices that are active or inactive (e.g., pass `false` to list all inactive prices).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  currency: {
                    type: 'param',
                    description: 'Only return prices for the given currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'lookup-keys': {
                    type: 'param',
                    description:
                      'Only return the price with these lookup-keys, if any exist.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  product: {
                    type: 'param',
                    description: 'Only return prices for the given product.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  recurring: {
                    type: 'param',
                    description:
                      'Only return prices with these recurring fields.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'Only return prices of type `recurring` or `one_time`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['one_time', 'recurring'],
                    },
                  },
                },
                args: [],
                path: '/v1/prices',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a new price for an existing product. The price can be recurring or one-time.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the price can be used for new purchases. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'billing-scheme': {
                    type: 'param',
                    description:
                      'Describes how to compute the price per period. Either `per_unit` or `tiered`. `per_unit` indicates that the fixed amount (specified in `unit_amount` or `unit_amount_decimal`) will be charged per unit in `quantity` (for prices with `usage_type=licensed`), or per unit of total usage (for prices with `usage_type=metered`). `tiered` indicates that the unit pricing will be computed using a tiering strategy as defined using the `tiers` and `tiers_mode` attributes.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['per_unit', 'tiered'],
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'lookup-key': {
                    type: 'param',
                    description:
                      'A lookup key used to retrieve prices dynamically from a static string. This may be up to 200 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  nickname: {
                    type: 'param',
                    description:
                      'A brief description of the price, hidden from customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  product: {
                    type: 'param',
                    description:
                      'The ID of the product that this price will belong to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'product-data': {
                    type: 'param',
                    description:
                      'These fields can be used to create a new product that this price will belong to.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  recurring: {
                    type: 'param',
                    description:
                      'The recurring components of a price such as `interval` and `usage_type`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tax-behavior': {
                    type: 'param',
                    description:
                      'Specifies whether the price is considered inclusive of taxes or exclusive of taxes. One of `inclusive`, `exclusive`, or `unspecified`. Once specified as either `inclusive` or `exclusive`, it cannot be changed.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['exclusive', 'inclusive', 'unspecified'],
                    },
                  },
                  tiers: {
                    type: 'param',
                    description:
                      'Each element represents a pricing tier. This parameter requires `billing_scheme` to be set to `tiered`. See also the documentation for `billing_scheme`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'tiers-mode': {
                    type: 'param',
                    description:
                      'Defines if the tiering price should be `graduated` or `volume` based. In `volume`-based tiering, the maximum quantity within a period determines the per unit price, in `graduated` tiering pricing can successively change as the quantity grows.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['graduated', 'volume'],
                    },
                  },
                  'transfer-lookup-key': {
                    type: 'param',
                    description:
                      'If set to true, will atomically remove the lookup key from the existing price, and assign it to this price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'transform-quantity': {
                    type: 'param',
                    description:
                      'Apply a transformation to the reported usage or set quantity before computing the billed price. Cannot be combined with `tiers`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'unit-amount': {
                    type: 'param',
                    description:
                      'A positive integer in %s (or 0 for a free price) representing how much to charge.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'unit-amount-decimal': {
                    type: 'param',
                    description:
                      'Same as `unit_amount`, but accepts a decimal value in %s with at most 12 decimal places. Only one of `unit_amount` and `unit-amount-decimal` can be set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'decimal_string',
                    },
                  },
                },
                args: [],
                path: '/v1/prices',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the price with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['price'],
                path: '/v1/prices/{price}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified price by setting the values of the parameters passed. Any parameters not provided are left unchanged.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the price can be used for new purchases. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'lookup-key': {
                    type: 'param',
                    description:
                      'A lookup key used to retrieve prices dynamically from a static string. This may be up to 200 characters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  nickname: {
                    type: 'param',
                    description:
                      'A brief description of the price, hidden from customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  recurring: {
                    type: 'param',
                    description:
                      'The recurring components of a price such as `interval` and `usage_type`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'tax-behavior': {
                    type: 'param',
                    description:
                      'Specifies whether the price is considered inclusive of taxes or exclusive of taxes. One of `inclusive`, `exclusive`, or `unspecified`. Once specified as either `inclusive` or `exclusive`, it cannot be changed.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['exclusive', 'inclusive', 'unspecified'],
                    },
                  },
                  'transfer-lookup-key': {
                    type: 'param',
                    description:
                      'If set to true, will atomically remove the lookup key from the existing price, and assign it to this price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['price'],
                path: '/v1/prices/{price}',
                method: 'post',
                run: true,
              },
            },
          },
          products: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for products you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for products.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/products/search',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new product object.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the product is currently available for purchase. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  attributes: {
                    type: 'param',
                    description:
                      'A list of up to 5 alphanumeric attributes. Should only be set if type=`good`.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  caption: {
                    type: 'param',
                    description:
                      'A short one-line description of the product, meant to be displayable to the customer. May only be set if type=`good`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'deactivate-on': {
                    type: 'param',
                    description:
                      'An array of Connect application names or identifiers that should not be able to order the SKUs for this product. May only be set if type=`good`.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      "The product's description, meant to be displayable to the customer. Use this field to optionally store a long form explanation of the product being sold for your own rendering purposes.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  id: {
                    type: 'param',
                    description:
                      'An identifier will be randomly generated by Stripe. You can optionally override this ID, but the ID must be unique across all products in your Stripe account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  images: {
                    type: 'param',
                    description:
                      'A list of up to 8 URLs of images for this product, meant to be displayable to the customer.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      "The product's name, meant to be displayable to the customer.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'package-dimensions': {
                    type: 'param',
                    description:
                      'The dimensions of this product for shipping purposes.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  shippable: {
                    type: 'param',
                    description:
                      'Whether this product is shipped (i.e., physical goods).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "An arbitrary string to be displayed on your customer's credit card or bank statement. While most banks display this information consistently, some may display it incorrectly or not at all.  This may be up to 22 characters. The statement description may not include ``, `\\`, `\"`, `'` characters, and will appear on your customer's statement in capital letters. Non-ASCII characters are automatically stripped.  It must contain at least one letter.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-code': {
                    type: 'param',
                    description: 'A tax code ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The type of the product. Defaults to `service` if not explicitly specified, enabling use of this product with Subscriptions and Plans. Set this parameter to `good` to use this product with Orders and SKUs. On API versions before `2018-02-05`, this field defaults to `good` for compatibility reasons.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['good', 'service'],
                    },
                  },
                  'unit-label': {
                    type: 'param',
                    description:
                      'A label that represents units of this product in Stripe and on customers’ receipts and invoices. When set, this will be included in associated invoice line item descriptions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  url: {
                    type: 'param',
                    description:
                      'A URL of a publicly-accessible webpage for this product.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/products',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of your products. The products are returned sorted by creation date, with the most recently created products appearing first.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return products that are active or inactive (e.g., pass `false` to list all inactive products).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'Only return products that were created during the given date interval.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  ids: {
                    type: 'param',
                    description: 'Only return products with the given IDs.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  shippable: {
                    type: 'param',
                    description:
                      'Only return products that can be shipped (i.e., physical, not digital products).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description: 'Only return products of this type.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['good', 'service'],
                    },
                  },
                  url: {
                    type: 'param',
                    description: 'Only return products with the given url.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/products',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing product. Supply the unique product ID from either a product creation request or the product list, and Stripe will return the corresponding product information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/products/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specific product by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the product is available for purchase.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  attributes: {
                    type: 'param',
                    description:
                      'A list of up to 5 alphanumeric attributes that each SKU can provide values for (e.g., `["color", "size"]`). If a value for `attributes` is specified, the list specified will replace the existing attributes list on this product. Any attributes not present after the update will be deleted from the SKUs for this product.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  caption: {
                    type: 'param',
                    description:
                      'A short one-line description of the product, meant to be displayable to the customer. May only be set if `type=good`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'deactivate-on': {
                    type: 'param',
                    description:
                      'An array of Connect application names or identifiers that should not be able to order the SKUs for this product. May only be set if `type=good`.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      "The product's description, meant to be displayable to the customer. Use this field to optionally store a long form explanation of the product being sold for your own rendering purposes.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  images: {
                    type: 'param',
                    description:
                      'A list of up to 8 URLs of images for this product, meant to be displayable to the customer.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      "The product's name, meant to be displayable to the customer.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'package-dimensions': {
                    type: 'param',
                    description:
                      'The dimensions of this product for shipping purposes.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  shippable: {
                    type: 'param',
                    description:
                      'Whether this product is shipped (i.e., physical goods).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "An arbitrary string to be displayed on your customer's credit card or bank statement. While most banks display this information consistently, some may display it incorrectly or not at all.  This may be up to 22 characters. The statement description may not include ``, `\\`, `\"`, `'` characters, and will appear on your customer's statement in capital letters. Non-ASCII characters are automatically stripped.  It must contain at least one letter. May only be set if `type=service`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-code': {
                    type: 'param',
                    description: 'A tax code ID.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'unit-label': {
                    type: 'param',
                    description:
                      'A label that represents units of this product in Stripe and on customers’ receipts and invoices. When set, this will be included in associated invoice line item descriptions. May only be set if `type=service`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  url: {
                    type: 'param',
                    description:
                      'A URL of a publicly-accessible webpage for this product.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/products/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Delete a product. Deleting a product is only possible if it has no prices associated with it. Additionally, deleting a product with type=good is only possible if it has no SKUs associated with it.',
                params: {},
                args: ['id'],
                path: '/v1/products/{id}',
                method: 'delete',
                run: true,
              },
            },
          },
          promotion_codes: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the promotion code with the given ID. In order to retrieve a promotion code by the customer-facing code use list with the desired code.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['promotion_code'],
                path: '/v1/promotion_codes/{promotion_code}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified promotion code by setting the values of the parameters passed. Most fields are, by design, not editable.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the promotion code is currently active. A promotion code can only be reactivated when the coupon is still valid and the promotion code is otherwise redeemable.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['promotion_code'],
                path: '/v1/promotion_codes/{promotion_code}',
                method: 'post',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'A promotion code points to a coupon. You can optionally restrict the code to a specific customer, redemption limit, and expiration date.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the promotion code is currently active.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  code: {
                    type: 'param',
                    description:
                      'The customer-facing code. Regardless of case, this code must be unique across all active promotion codes for a specific customer. If left blank, we will generate one automatically.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description: 'The coupon for this promotion code.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The customer that this promotion code can be used by. If not set, the promotion code can be used by all customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      "The timestamp at which this promotion code will expire. If the coupon has specified a `redeems_by`, then this value cannot be after the coupon's `redeems_by`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'max-redemptions': {
                    type: 'param',
                    description:
                      "A positive integer specifying the number of times the promotion code can be redeemed. If the coupon has specified a `max-redemptions`, then this value cannot be greater than the coupon's `max-redemptions`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  restrictions: {
                    type: 'param',
                    description:
                      'Settings that restrict the redemption of the promotion code.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/promotion_codes',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of your promotion codes.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Filter promotion codes by whether they are active.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  code: {
                    type: 'param',
                    description:
                      'Only return promotion codes that have this case-insensitive code.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  coupon: {
                    type: 'param',
                    description: 'Only return promotion codes for this coupon.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return promotion codes that are restricted to this customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/promotion_codes',
                method: 'get',
                run: true,
              },
            },
          },
          quotes: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves the quote with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'A quote models prices and services for a customer.',
                params: {
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. There cannot be any line items with recurring prices when using this field.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  'application-fee-percent': {
                    type: 'param',
                    description:
                      "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice subtotal that will be transferred to the application owner's Stripe account. There must be at least 1 line item with a recurring price to use this field.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'number',
                      },
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this quote and resulting invoices and subscriptions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically`, or `send_invoice`. When charging automatically, Stripe will attempt to pay invoices at the end of the subscription cycle or at invoice finalization using the default payment method attached to the subscription or customer. When sending an invoice, Stripe will email your customer an invoice with payment instructions. Defaults to `charge_automatically`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The customer for which this quote belongs to. A customer is required before finalizing the quote. Once specified, it cannot be changed.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any line item that does not have `tax_rates` set.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'A description that will be displayed on the quote PDF.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      'The discounts applied to the quote. You can only set up to one discount.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      'A future timestamp on which the quote will be canceled if in `open` or `draft` status. Measured in seconds since the Unix epoch.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  footer: {
                    type: 'param',
                    description:
                      'A footer that will be displayed on the quote PDF.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  header: {
                    type: 'param',
                    description:
                      'A header that will be displayed on the quote PDF.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'invoice-settings': {
                    type: 'param',
                    description:
                      'All invoices will be billed using the specified settings.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'line-items': {
                    type: 'param',
                    description:
                      'A list of line items the customer is being quoted for. Each line item includes information about the product, the quantity, and the resulting cost.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description: 'The account on behalf of which to charge.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'subscription-data': {
                    type: 'param',
                    description:
                      'When creating a subscription or subscription schedule, the specified configuration data will be used. There must be at least one line item with a recurring price for a subscription or subscription schedule to be created. A subscription schedule is created if `subscription-data[effective_date]` is present and in the future, otherwise a subscription is created.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The data with which to automatically create a Transfer for each of the invoices.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}',
                method: 'post',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'A quote models prices and services for a customer. Default options for header, description, footer, and expires_at can be set in the dashboard via the quote template.',
                params: {
                  'application-fee-amount': {
                    type: 'param',
                    description:
                      "The amount of the application fee (if any) that will be requested to be applied to the payment and transferred to the application owner's Stripe account. There cannot be any line items with recurring prices when using this field.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  'application-fee-percent': {
                    type: 'param',
                    description:
                      "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice subtotal that will be transferred to the application owner's Stripe account. There must be at least 1 line item with a recurring price to use this field.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'number',
                      },
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Settings for automatic tax lookup for this quote and resulting invoices and subscriptions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically`, or `send_invoice`. When charging automatically, Stripe will attempt to pay invoices at the end of the subscription cycle or at invoice finalization using the default payment method attached to the subscription or customer. When sending an invoice, Stripe will email your customer an invoice with payment instructions. Defaults to `charge_automatically`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The customer for which this quote belongs to. A customer is required before finalizing the quote. Once specified, it cannot be changed.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any line item that does not have `tax_rates` set.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'A description that will be displayed on the quote PDF. If no value is passed, the default description configured in your quote template settings will be used.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  discounts: {
                    type: 'param',
                    description:
                      'The discounts applied to the quote. You can only set up to one discount.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      'A future timestamp on which the quote will be canceled if in `open` or `draft` status. Measured in seconds since the Unix epoch. If no value is passed, the default expiration date configured in your quote template settings will be used.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  footer: {
                    type: 'param',
                    description:
                      'A footer that will be displayed on the quote PDF. If no value is passed, the default footer configured in your quote template settings will be used.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'from-quote': {
                    type: 'param',
                    description:
                      'Clone an existing quote. The new quote will be created in `status=draft`. When using this parameter, you cannot specify any other parameters except for `expires_at`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  header: {
                    type: 'param',
                    description:
                      'A header that will be displayed on the quote PDF. If no value is passed, the default header configured in your quote template settings will be used.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'invoice-settings': {
                    type: 'param',
                    description:
                      'All invoices will be billed using the specified settings.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'line-items': {
                    type: 'param',
                    description:
                      'A list of line items the customer is being quoted for. Each line item includes information about the product, the quantity, and the resulting cost.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description: 'The account on behalf of which to charge.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'subscription-data': {
                    type: 'param',
                    description:
                      'When creating a subscription or subscription schedule, the specified configuration data will be used. There must be at least one line item with a recurring price for a subscription or subscription schedule to be created. A subscription schedule is created if `subscription-data[effective_date]` is present and in the future, otherwise a subscription is created.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'test-clock': {
                    type: 'param',
                    description: 'ID of the test clock to attach to the quote.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      'The data with which to automatically create a Transfer for each of the invoices.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/quotes',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of your quotes.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'The ID of the customer whose quotes will be retrieved.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description: 'The status of the quote.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['accepted', 'canceled', 'draft', 'open'],
                    },
                  },
                  'test-clock': {
                    type: 'param',
                    description:
                      'Provides a list of quotes that are associated with the specified test clock. The response will not include quotes with test clocks if this and the customer parameter is not set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/quotes',
                method: 'get',
                run: true,
              },
              cancel: {
                type: 'command',
                description: 'Cancels the quote.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/cancel',
                method: 'post',
                run: true,
              },
              finalize_quote: {
                type: 'command',
                description: 'Finalizes the quote.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'expires-at': {
                    type: 'param',
                    description:
                      'A future timestamp on which the quote will be canceled if in `open` or `draft` status. Measured in seconds since the Unix epoch.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/finalize',
                method: 'post',
                run: true,
              },
              accept: {
                type: 'command',
                description: 'Accepts the specified quote.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/accept',
                method: 'post',
                run: true,
              },
              list_line_items: {
                type: 'command',
                description:
                  'When retrieving a quote, there is an includable line_items property containing the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/line_items',
                method: 'get',
                run: true,
              },
              list_computed_upfront_line_items: {
                type: 'command',
                description:
                  'When retrieving a quote, there is an includable computed.upfront.line_items property containing the first handful of those items. There is also a URL where you can retrieve the full (paginated) list of upfront line items.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/computed_upfront_line_items',
                method: 'get',
                run: true,
              },
              pdf: {
                type: 'command',
                description: 'Download the PDF for a finalized quote',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['quote'],
                path: '/v1/quotes/{quote}/pdf',
                method: 'get',
                run: true,
              },
            },
          },
          radar: {
            type: 'command',
            commands: {
              early_fraud_warnings: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description: 'Returns a list of early fraud warnings.',
                    params: {
                      charge: {
                        type: 'param',
                        description:
                          'Only return early fraud warnings for the charge specified by this charge ID.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'payment-intent': {
                        type: 'param',
                        description:
                          'Only return early fraud warnings for charges that were created by the PaymentIntent specified by this PaymentIntent ID.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/radar/early_fraud_warnings',
                    method: 'get',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves the details of an early fraud warning that has previously been created.   Please refer to the early fraud warning object reference for more details.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['early_fraud_warning'],
                    path:
                      '/v1/radar/early_fraud_warnings/{early_fraud_warning}',
                    method: 'get',
                    run: true,
                  },
                },
              },
              value_lists: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of ValueList objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      alias: {
                        type: 'param',
                        description:
                          'The alias used to reference the value list when writing rules.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      contains: {
                        type: 'param',
                        description:
                          'A value contained within a value list - returns all value lists containing this value.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      created: {
                        type: 'param',
                        description: '',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/radar/value_lists',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new ValueList object, which can then be referenced in rules.',
                    params: {
                      alias: {
                        type: 'param',
                        description:
                          'The name of the value list for use in rules.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'item-type': {
                        type: 'param',
                        description:
                          'Type of the items in the value list. One of `card_fingerprint`, `card_bin`, `email`, `ip_address`, `country`, `string`, `case_sensitive_string`, or `customer_id`. Use `string` if the item type is unknown or mixed.',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'card_bin',
                            'card_fingerprint',
                            'case_sensitive_string',
                            'country',
                            'customer_id',
                            'email',
                            'ip_address',
                            'string',
                          ],
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      name: {
                        type: 'param',
                        description:
                          'The human-readable name of the value list.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/radar/value_lists',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a ValueList object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['value_list'],
                    path: '/v1/radar/value_lists/{value_list}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates a ValueList object by setting the values of the parameters passed. Any parameters not provided will be left unchanged. Note that item_type is immutable.',
                    params: {
                      alias: {
                        type: 'param',
                        description:
                          'The name of the value list for use in rules.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'map',
                          keyType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      name: {
                        type: 'param',
                        description:
                          'The human-readable name of the value list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: ['value_list'],
                    path: '/v1/radar/value_lists/{value_list}',
                    method: 'post',
                    run: true,
                  },
                  delete: {
                    type: 'command',
                    description:
                      'Deletes a ValueList object, also deleting any items contained within the value list. To be deleted, a value list must not be referenced in any rules.',
                    params: {},
                    args: ['value_list'],
                    path: '/v1/radar/value_lists/{value_list}',
                    method: 'delete',
                    run: true,
                  },
                },
              },
              value_list_items: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of ValueListItem objects. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                    params: {
                      created: {
                        type: 'param',
                        description: '',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      value: {
                        type: 'param',
                        description:
                          'Return items belonging to the parent list whose value matches the specified value (using an "is like" match).',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'value-list': {
                        type: 'param',
                        description:
                          'Identifier for the parent value list this item belongs to.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/radar/value_list_items',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new ValueListItem object, which is added to the specified parent value list.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      value: {
                        type: 'param',
                        description:
                          'The value of the item (whose type must match the type of the parent value list).',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'value-list': {
                        type: 'param',
                        description:
                          'The identifier of the value list which the created item will be added to.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/radar/value_list_items',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a ValueListItem object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['item'],
                    path: '/v1/radar/value_list_items/{item}',
                    method: 'get',
                    run: true,
                  },
                  delete: {
                    type: 'command',
                    description:
                      'Deletes a ValueListItem object, removing it from its parent value list.',
                    params: {},
                    args: ['item'],
                    path: '/v1/radar/value_list_items/{item}',
                    method: 'delete',
                    run: true,
                  },
                },
              },
            },
          },
          recipients: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of your recipients. The recipients are returned sorted by creation date, with the most recently created recipients appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['corporation', 'individual'],
                    },
                  },
                  verified: {
                    type: 'param',
                    description:
                      'Only return recipients that are verified or unverified.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: [],
                path: '/v1/recipients',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a new Recipient object and verifies the recipient’s identity. Also verifies the recipient’s bank account information or debit card, if either is provided.',
                params: {
                  'bank-account': {
                    type: 'param',
                    description:
                      "A bank account to attach to the recipient. You can provide either a token, like the ones returned by Stripe.js, or a dictionary containing a user's bank account details, with the options described below.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  card: {
                    type: 'param',
                    description:
                      "A U.S. Visa or MasterCard debit card (_not_ prepaid) to attach to the recipient. If the debit card is not valid, recipient creation will fail. You can provide either a token, like the ones returned by Stripe.js, or a dictionary containing a user's debit card details, with the options described below. Although not all information is required, the extra info helps prevent fraud.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to a `Recipient` object. It is displayed alongside the recipient in the web interface.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      "The recipient's email address. It is displayed alongside the recipient in the web interface, and can be useful for searching and tracking.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      "The recipient's full, legal name. For type `individual`, should be in the format `First Last`, `First Middle Last`, or `First M Last` (no prefixes or suffixes). For `corporation`, the full, incorporated name.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-id': {
                    type: 'param',
                    description:
                      "The recipient's tax ID, as a string. For type `individual`, the full SSN; for type `corporation`, the full EIN.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'Type of the recipient: either `individual` or `corporation`.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/recipients',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing recipient. You need only supply the unique recipient identifier that was returned upon recipient creation.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/recipients/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified recipient by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  If you update the name or tax ID, the identity verification will automatically be rerun. If you update the bank account, the bank account validation will automatically be rerun.',
                params: {
                  'bank-account': {
                    type: 'param',
                    description:
                      "A bank account to attach to the recipient. You can provide either a token, like the ones returned by Stripe.js, or a dictionary containing a user's bank account details, with the options described below.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  card: {
                    type: 'param',
                    description:
                      "A U.S. Visa or MasterCard debit card (not prepaid) to attach to the recipient. You can provide either a token, like the ones returned by Stripe.js, or a dictionary containing a user's debit card details, with the options described below. Passing `card` will create a new card, make it the new recipient default card, and delete the old recipient default (if one exists). If you want to add additional debit cards instead of replacing the existing default, use the card creation API. Whenever you attach a card to a recipient, Stripe will automatically validate the debit card.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-card': {
                    type: 'param',
                    description:
                      "ID of the card to set as the recipient's new default for payouts.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to a `Recipient` object. It is displayed alongside the recipient in the web interface.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  email: {
                    type: 'param',
                    description:
                      "The recipient's email address. It is displayed alongside the recipient in the web interface, and can be useful for searching and tracking.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  name: {
                    type: 'param',
                    description:
                      "The recipient's full, legal name. For type `individual`, should be in the format `First Last`, `First Middle Last`, or `First M Last` (no prefixes or suffixes). For `corporation`, the full, incorporated name.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-id': {
                    type: 'param',
                    description:
                      "The recipient's tax ID, as a string. For type `individual`, the full SSN; for type `corporation`, the full EIN.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/recipients/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Permanently deletes a recipient. It cannot be undone.',
                params: {},
                args: ['id'],
                path: '/v1/recipients/{id}',
                method: 'delete',
                run: true,
              },
            },
          },
          refunds: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of all refunds you’ve previously created. The refunds are returned in sorted order, with the most recent refunds appearing first. For convenience, the 10 most recent refunds are always available by default on the charge object.',
                params: {
                  charge: {
                    type: 'param',
                    description:
                      'Only return refunds for the charge specified by this charge ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'payment-intent': {
                    type: 'param',
                    description:
                      'Only return refunds for the PaymentIntent specified by this ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/refunds',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the details of an existing refund.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['refund'],
                path: '/v1/refunds/{refund}',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Create a refund.',
                params: {
                  amount: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  charge: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'payment-intent': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  reason: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'duplicate',
                        'fraudulent',
                        'requested_by_customer',
                      ],
                    },
                  },
                  'refund-application-fee': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'reverse-transfer': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: [],
                path: '/v1/refunds',
                method: 'post',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified refund by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  This request only accepts metadata as an argument.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['refund'],
                path: '/v1/refunds/{refund}',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'Cancels a refund with a status of requires_action.  Refunds in other states cannot be canceled, and only refunds for payment methods that require customer action will enter the requires_action state.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['refund'],
                path: '/v1/refunds/{refund}/cancel',
                method: 'post',
                run: true,
              },
            },
          },
          reporting: {
            type: 'command',
            commands: {
              report_runs: {
                type: 'command',
                commands: {
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves the details of an existing Report Run.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['report_run'],
                    path: '/v1/reporting/report_runs/{report_run}',
                    method: 'get',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new object and begin running the report. (Certain report types require a live-mode API key.)',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      parameters: {
                        type: 'param',
                        description:
                          'Parameters specifying how the report should be run. Different Report Types have different required and optional parameters, listed in the API Access to Reports documentation.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'report-type': {
                        type: 'param',
                        description:
                          'The ID of the report type to run, such as `"balance.summary.1"`.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/reporting/report_runs',
                    method: 'post',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description:
                      'Returns a list of Report Runs, with the most recent appearing first.',
                    params: {
                      created: {
                        type: 'param',
                        description: '',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/reporting/report_runs',
                    method: 'get',
                    run: true,
                  },
                },
              },
              report_types: {
                type: 'command',
                commands: {
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves the details of a Report Type. (Certain report types require a live-mode API key.)',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['report_type'],
                    path: '/v1/reporting/report_types/{report_type}',
                    method: 'get',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'Returns a full list of Report Types.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: [],
                    path: '/v1/reporting/report_types',
                    method: 'get',
                    run: true,
                  },
                },
              },
            },
          },
          reviews: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of Review objects that have open set to true. The objects are sorted in descending order by creation date, with the most recently created object appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/reviews',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves a Review object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['review'],
                path: '/v1/reviews/{review}',
                method: 'get',
                run: true,
              },
              approve: {
                type: 'command',
                description:
                  'Approves a Review object, closing it and removing it from the list of reviews.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['review'],
                path: '/v1/reviews/{review}/approve',
                method: 'post',
                run: true,
              },
            },
          },
          sigma: {
            type: 'command',
            commands: {
              scheduled_query_runs: {
                type: 'command',
                commands: {
                  list: {
                    type: 'command',
                    description: 'Returns a list of scheduled query runs.',
                    params: {
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/sigma/scheduled_query_runs',
                    method: 'get',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description:
                      'Retrieves the details of an scheduled query run.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['scheduled_query_run'],
                    path:
                      '/v1/sigma/scheduled_query_runs/{scheduled_query_run}',
                    method: 'get',
                    run: true,
                  },
                },
              },
            },
          },
          setup_attempts: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of SetupAttempts associated with a provided SetupIntent.',
                params: {
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'setup-intent': {
                    type: 'param',
                    description:
                      'Only return SetupAttempts created by the SetupIntent specified by this ID.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/setup_attempts',
                method: 'get',
                run: true,
              },
            },
          },
          setup_intents: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates a SetupIntent object.  After the SetupIntent is created, attach a payment method and confirm to collect any required permissions to charge the payment method later.',
                params: {
                  confirm: {
                    type: 'param',
                    description:
                      'Set to `true` to attempt to confirm this SetupIntent immediately. This parameter defaults to `false`. If the payment method attached is a card, a return_url may be provided in case additional authentication is required.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "ID of the Customer this SetupIntent belongs to, if one exists.  If present, the SetupIntent's payment method will be attached to the Customer on successful setup. Payment methods attached to other Customers cannot be used with this SetupIntent.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'mandate-data': {
                    type: 'param',
                    description:
                      'This hash contains details about the Mandate to create. This parameter can only be used with `confirm=true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'on-behalf-of': {
                    type: 'param',
                    description:
                      'The Stripe account ID for which this SetupIntent is created.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or saved Source object) to attach to this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types (e.g. card) that this SetupIntent is allowed to use. If this is not provided, defaults to ["card"].',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      "The URL to redirect your customer back to after they authenticate or cancel their payment on the payment method's app or site. If you'd prefer to redirect to a mobile application, you can alternatively supply an application URI scheme. This parameter can only be used with `confirm=true`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'single-use': {
                    type: 'param',
                    description:
                      'If this hash is populated, this SetupIntent will generate a single-use Mandate on success.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  usage: {
                    type: 'param',
                    description:
                      'Indicates how the payment method is intended to be used in the future. If not provided, this value defaults to `off_session`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['off_session', 'on_session'],
                    },
                  },
                },
                args: [],
                path: '/v1/setup_intents',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of SetupIntents.',
                params: {
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return SetupIntents for the customer specified by this customer ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'Only return SetupIntents associated with the specified payment method.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/setup_intents',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of a SetupIntent that has previously been created.   Client-side retrieval using a publishable key is allowed when the client_secret is provided in the query string.   When retrieved with a publishable key, only a subset of properties will be returned. Please refer to the SetupIntent object reference for more details.',
                params: {
                  'client-secret': {
                    type: 'param',
                    description:
                      'The client secret of the SetupIntent. Required if a publishable key is used to retrieve the SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/setup_intents/{intent}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates a SetupIntent object.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      "ID of the Customer this SetupIntent belongs to, if one exists.  If present, the SetupIntent's payment method will be attached to the Customer on successful setup. Payment methods attached to other Customers cannot be used with this SetupIntent.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or saved Source object) to attach to this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method-types': {
                    type: 'param',
                    description:
                      'The list of payment method types (e.g. card) that this SetupIntent is allowed to set up. If this is not provided, defaults to ["card"].',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/setup_intents/{intent}',
                method: 'post',
                run: true,
              },
              confirm: {
                type: 'command',
                description:
                  'Confirm that your customer intends to set up the current or provided payment method. For example, you would confirm a SetupIntent when a customer hits the “Save” button on a payment method management page on your website.  If the selected payment method does not require any additional steps from the customer, the SetupIntent will transition to the succeeded status.  Otherwise, it will transition to the requires_action status and suggest additional actions via next_action. If setup fails, the SetupIntent will transition to the requires_payment_method status.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'mandate-data': {
                    type: 'param',
                    description:
                      'This hash contains details about the Mandate to create',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'payment-method': {
                    type: 'param',
                    description:
                      'ID of the payment method (a PaymentMethod, Card, or saved Source object) to attach to this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'payment-method-options': {
                    type: 'param',
                    description:
                      'Payment-method-specific configuration for this SetupIntent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      "The URL to redirect your customer back to after they authenticate on the payment method's app or site. If you'd prefer to redirect to a mobile application, you can alternatively supply an application URI scheme. This parameter is only used for cards and other redirect-based payment methods.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/setup_intents/{intent}/confirm',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'A SetupIntent object can be canceled when it is in one of these statuses: requires_payment_method, requires_confirmation, or requires_action.   Once canceled, setup is abandoned and any operations on the SetupIntent will fail with an error.',
                params: {
                  'cancellation-reason': {
                    type: 'param',
                    description:
                      'Reason for canceling this SetupIntent. Possible values are `abandoned`, `requested_by_customer`, or `duplicate`',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['abandoned', 'duplicate', 'requested_by_customer'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/setup_intents/{intent}/cancel',
                method: 'post',
                run: true,
              },
              verify_microdeposits: {
                type: 'command',
                description: 'Verifies microdeposits on a SetupIntent object.',
                params: {
                  amounts: {
                    type: 'param',
                    description:
                      'Two positive integers, in *cents*, equal to the values of the microdeposits sent to the bank account.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'integer',
                      },
                    },
                  },
                  'descriptor-code': {
                    type: 'param',
                    description:
                      'A six-character code starting with SM present in the microdeposit sent to the bank account.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['intent'],
                path: '/v1/setup_intents/{intent}/verify_microdeposits',
                method: 'post',
                run: true,
              },
            },
          },
          shipping_rates: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your shipping rates.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return shipping rates that are active or inactive.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Only return shipping rates for the given currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/shipping_rates',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new shipping rate object.',
                params: {
                  'delivery-estimate': {
                    type: 'param',
                    description:
                      'The estimated range for how long shipping will take, meant to be displayable to the customer. This will appear on CheckoutSessions.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'display-name': {
                    type: 'param',
                    description:
                      'The name of the shipping rate, meant to be displayable to the customer. This will appear on CheckoutSessions.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'fixed-amount': {
                    type: 'param',
                    description:
                      'Describes a fixed amount to charge for shipping. Must be present if type is `fixed-amount`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'tax-behavior': {
                    type: 'param',
                    description:
                      'Specifies whether the rate is considered inclusive of taxes or exclusive of taxes. One of `inclusive`, `exclusive`, or `unspecified`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['exclusive', 'inclusive', 'unspecified'],
                    },
                  },
                  'tax-code': {
                    type: 'param',
                    description:
                      'A tax code ID. The Shipping tax code is `txcd_92010001`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The type of calculation to use on the shipping rate. Can only be `fixed_amount` for now.',
                    required: false,
                    input_type: {
                      shape: 'literal',
                      literal: 'fixed_amount',
                    },
                  },
                },
                args: [],
                path: '/v1/shipping_rates',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Returns the shipping rate object with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['shipping_rate_token'],
                path: '/v1/shipping_rates/{shipping_rate_token}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing shipping rate object.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the shipping rate can be used for new purchases. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['shipping_rate_token'],
                path: '/v1/shipping_rates/{shipping_rate_token}',
                method: 'post',
                run: true,
              },
            },
          },
          skus: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing SKU. Supply the unique SKU identifier from either a SKU creation request or from the product, and Stripe will return the corresponding SKU information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/skus/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specific SKU by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  Note that a SKU’s attributes are not editable. Instead, you would need to deactivate the existing SKU and create a new one with the new attribute values.',
                params: {
                  active: {
                    type: 'param',
                    description: 'Whether this SKU is available for purchase.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  attributes: {
                    type: 'param',
                    description:
                      'A dictionary of attributes and values for the attributes defined by the product. When specified, `attributes` will partially update the existing attributes dictionary on the product, with the postcondition that a value must be present for each attribute key on the product.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  image: {
                    type: 'param',
                    description:
                      'The URL of an image for this SKU, meant to be displayable to the customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  inventory: {
                    type: 'param',
                    description: "Description of the SKU's inventory.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'package-dimensions': {
                    type: 'param',
                    description:
                      'The dimensions of this SKU for shipping purposes.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  price: {
                    type: 'param',
                    description:
                      'The cost of the item as a positive integer in the smallest currency unit (that is, 100 cents to charge $1.00, or 100 to charge ¥100, Japanese Yen being a zero-decimal currency).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  product: {
                    type: 'param',
                    description:
                      "The ID of the product that this SKU should belong to. The product must exist, have the same set of attribute names as the SKU's current product, and be of type `good`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/skus/{id}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Delete a SKU. Deleting a SKU is only possible until it has been used in an order.',
                params: {},
                args: ['id'],
                path: '/v1/skus/{id}',
                method: 'delete',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of your SKUs. The SKUs are returned sorted by creation date, with the most recently created SKUs appearing first.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Only return SKUs that are active or inactive (e.g., pass `false` to list all inactive products).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  attributes: {
                    type: 'param',
                    description:
                      'Only return SKUs that have the specified key-value pairs in this partially constructed dictionary. Can be specified only if `product` is also supplied. For instance, if the associated product has attributes `["color", "size"]`, passing in `attributes[color]=red` returns all the SKUs for this product that have `color` set to `red`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  ids: {
                    type: 'param',
                    description: 'Only return SKUs with the given IDs.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'in-stock': {
                    type: 'param',
                    description:
                      'Only return SKUs that are either in stock or out of stock (e.g., pass `false` to list all SKUs that are out of stock). If no value is provided, all SKUs are returned.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  product: {
                    type: 'param',
                    description:
                      'The ID of the product whose SKUs will be retrieved. Must be a product with type `good`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/skus',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new SKU associated with a product.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Whether the SKU is available for purchase. Default to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  attributes: {
                    type: 'param',
                    description:
                      'A dictionary of attributes and values for the attributes defined by the product. If, for example, a product\'s attributes are `["size", "gender"]`, a valid SKU has the following dictionary of attributes: `{"size": "Medium", "gender": "Unisex"}`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  id: {
                    type: 'param',
                    description:
                      'The identifier for the SKU. Must be unique. If not provided, an identifier will be randomly generated.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  image: {
                    type: 'param',
                    description:
                      'The URL of an image for this SKU, meant to be displayable to the customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  inventory: {
                    type: 'param',
                    description: "Description of the SKU's inventory.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'package-dimensions': {
                    type: 'param',
                    description:
                      'The dimensions of this SKU for shipping purposes.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  price: {
                    type: 'param',
                    description:
                      'The cost of the item as a nonnegative integer in the smallest currency unit (that is, 100 cents to charge $1.00, or 100 to charge ¥100, Japanese Yen being a zero-decimal currency).',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  product: {
                    type: 'param',
                    description:
                      'The ID of the product this SKU is associated with. Must be a product with type `good`.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/skus',
                method: 'post',
                run: true,
              },
            },
          },
          sources: {
            type: 'command',
            commands: {
              detach: {
                type: 'command',
                description: 'Delete a specified source for a given customer.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer', 'id'],
                path: '/v1/customers/{customer}/sources/{id}',
                method: 'delete',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves an existing source object. Supply the unique source ID from a source creation request and Stripe will return the corresponding up-to-date source object information.',
                params: {
                  'client-secret': {
                    type: 'param',
                    description:
                      'The client secret of the source. Required if a publishable key is used to retrieve the source.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['source'],
                path: '/v1/sources/{source}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified source by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  This request accepts the metadata and owner as arguments. It is also possible to update type specific information for selected payment methods. Please refer to our payment method guides for more detail.',
                params: {
                  amount: {
                    type: 'param',
                    description: 'Amount associated with the source.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  mandate: {
                    type: 'param',
                    description:
                      'Information about a mandate possibility attached to a source object (generally for bank debits) as well as its acceptance status.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  owner: {
                    type: 'param',
                    description:
                      'Information about the owner of the payment instrument that may be used or required by particular source types.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'source-order': {
                    type: 'param',
                    description:
                      'Information about the items and shipping associated with the source. Required for transactional credit (for example Klarna) sources before you can charge it.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['source'],
                path: '/v1/sources/{source}',
                method: 'post',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new source object.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount associated with the source. This is the amount for which the source will be chargeable once ready. Required for `single_use` sources. Not supported for `receiver` type sources, where charge amount may not be specified until funds land.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO code for the currency associated with the source. This is the currency for which the source will be chargeable once ready.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The `Customer` to whom the original source is attached to. Must be set when the original source is not a `Source` (e.g., `Card`).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  flow: {
                    type: 'param',
                    description:
                      'The authentication `flow` of the source to create. `flow` is one of `redirect`, `receiver`, `code_verification`, `none`. It is generally inferred unless a type supports multiple flows.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'code_verification',
                        'none',
                        'receiver',
                        'redirect',
                      ],
                    },
                  },
                  mandate: {
                    type: 'param',
                    description:
                      'Information about a mandate possibility attached to a source object (generally for bank debits) as well as its acceptance status.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'original-source': {
                    type: 'param',
                    description: 'The source to share.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  owner: {
                    type: 'param',
                    description:
                      'Information about the owner of the payment instrument that may be used or required by particular source types.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  receiver: {
                    type: 'param',
                    description:
                      'Optional parameters for the receiver flow. Can be set only if the source is a receiver (`flow` is `receiver`).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  redirect: {
                    type: 'param',
                    description:
                      'Parameters required for the redirect flow. Required if the source is authenticated by a redirect (`flow` is `redirect`).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'source-order': {
                    type: 'param',
                    description:
                      'Information about the items and shipping associated with the source. Required for transactional credit (for example Klarna) sources before you can charge it.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "An arbitrary string to be displayed on your customer's statement. As an example, if your website is `RunClub` and the item you're charging for is a race ticket, you may want to specify a `statement-descriptor` of `RunClub 5K race ticket.` While many payment types will display this information, some may not display it at all.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  token: {
                    type: 'param',
                    description:
                      'An optional token used to create the source. When passed, token properties will override source parameters.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'The `type` of the source to create. Required unless `customer` and `original_source` are specified (see the Cloning card Sources guide)',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  usage: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['reusable', 'single_use'],
                    },
                  },
                },
                args: [],
                path: '/v1/sources',
                method: 'post',
                run: true,
              },
              verify: {
                type: 'command',
                description: 'Verify a given source.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  values: {
                    type: 'param',
                    description: 'The values needed to verify the source.',
                    required: true,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['source'],
                path: '/v1/sources/{source}/verify',
                method: 'post',
                run: true,
              },
              source_transactions: {
                type: 'command',
                description: 'List source transactions for a given source.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['source'],
                path: '/v1/sources/{source}/source_transactions',
                method: 'get',
                run: true,
              },
            },
          },
          subscriptions: {
            type: 'command',
            commands: {
              search: {
                type: 'command',
                description:
                  'Search for subscriptions you’ve previously created using Stripe’s Search Query Language. Don’t use search in read-after-write flows where strict consistency is necessary. Under normal operating conditions, data is searchable in less than a minute. Occasionally, propagation of new or updated data can be up to an hour behind during outages. Search functionality is not available to merchants in India.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  page: {
                    type: 'param',
                    description:
                      "A cursor for pagination across multiple pages of results. Don't include this parameter on the first call. Use the next_page value returned in a previous response to request subsequent results.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  query: {
                    type: 'param',
                    description:
                      'The search query string. See search query language and the list of supported query fields for subscriptions.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/subscriptions/search',
                method: 'get',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'By default, returns a list of subscriptions that have not been canceled. In order to list canceled subscriptions, specify status=canceled.',
                params: {
                  'collection-method': {
                    type: 'param',
                    description:
                      'The collection method of the subscriptions to retrieve. Either `charge_automatically` or `send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'current-period-end': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'current-period-start': {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The ID of the customer whose subscriptions will be retrieved.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  plan: {
                    type: 'param',
                    description:
                      'The ID of the plan whose subscriptions will be retrieved.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  price: {
                    type: 'param',
                    description:
                      'Filter for subscriptions that contain this recurring price ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'The status of the subscriptions to retrieve. Passing in a value of `canceled` will return all canceled subscriptions, including those belonging to deleted customers. Pass `ended` to find subscriptions that are canceled and subscriptions that are expired due to incomplete payment. Passing in a value of `all` will return subscriptions of all statuses. If no value is supplied, all subscriptions that have not been canceled are returned.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'active',
                        'all',
                        'canceled',
                        'ended',
                        'incomplete',
                        'incomplete_expired',
                        'past_due',
                        'trialing',
                        'unpaid',
                      ],
                    },
                  },
                  'test-clock': {
                    type: 'param',
                    description:
                      'Filter for subscriptions that are associated with the specified test clock. The response will not include subscriptions with test clocks if this and the customer parameter is not set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/subscriptions',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a new subscription on an existing customer. Each customer can have up to 500 active or scheduled subscriptions.  When you create a subscription with collection_method=charge_automatically, the first invoice is finalized as part of the request. The payment_behavior parameter determines the exact behavior of the initial payment.  To start subscriptions where the first invoice always begins in a draft status, use subscription schedules instead. Schedules provide the flexibility to model more complex billing configurations that change over time.',
                params: {
                  'add-invoice-items': {
                    type: 'param',
                    description:
                      'A list of prices and quantities that will generate invoice items appended to the first invoice for this subscription. You may pass up to 20 items.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'application-fee-percent': {
                    type: 'param',
                    description:
                      "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice subtotal that will be transferred to the application owner's Stripe account. The request must be made by a platform account on a connected account in order to set an application fee percentage. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'number',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Automatic tax settings for this subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'backdate-start-date': {
                    type: 'param',
                    description:
                      "For new subscriptions, a past timestamp to backdate the subscription's start date to. If set, the first invoice will contain a proration for the timespan between the start date and the current time. Can be combined with trials and the billing cycle anchor.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'billing-cycle-anchor': {
                    type: 'param',
                    description:
                      "A future timestamp to anchor the subscription's billing cycle. This is used to determine the date of the first full invoice, and, for plans with `month` or `year` intervals, the day of the month for subsequent invoices.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'billing-thresholds': {
                    type: 'param',
                    description:
                      'Define thresholds at which an invoice will be sent, and the subscription advanced to a new billing period. Pass an empty string to remove previously-defined thresholds.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'cancel-at': {
                    type: 'param',
                    description:
                      'A timestamp at which the subscription should cancel. If set to a date before the current period ends, this will cause a proration if prorations have been enabled using `proration_behavior`. If set during a future period, this will always cause a proration for that period.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'cancel-at-period-end': {
                    type: 'param',
                    description:
                      'Boolean indicating whether this subscription should cancel at the end of the current period.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically`, or `send_invoice`. When charging automatically, Stripe will attempt to pay this subscription at the end of the cycle using the default source attached to the customer. When sending an invoice, Stripe will email your customer an invoice with payment instructions. Defaults to `charge_automatically`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  coupon: {
                    type: 'param',
                    description:
                      'The ID of the coupon to apply to this subscription. A coupon applied to a subscription will only affect invoices created for that particular subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description: 'The identifier of the customer to subscribe.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'days-until-due': {
                    type: 'param',
                    description:
                      'Number of days a customer has to pay invoices generated by this subscription. Valid only for subscriptions where `collection_method` is set to `send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'default-payment-method': {
                    type: 'param',
                    description:
                      "ID of the default payment method for the subscription. It must belong to the customer associated with the subscription. This takes precedence over `default_source`. If neither are set, invoices will use the customer's invoice_settings.default-payment-method or default_source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-source': {
                    type: 'param',
                    description:
                      "ID of the default payment source for the subscription. It must belong to the customer associated with the subscription and be in a chargeable state. If `default_payment_method` is also set, `default_payment_method` will take precedence. If neither are set, invoices will use the customer's invoice_settings.default_payment_method or default-source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any subscription item that does not have `tax_rates` set. Invoices created will have their `default-tax-rates` populated from the subscription.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  items: {
                    type: 'param',
                    description:
                      'A list of up to 20 subscription items, each with an attached price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Indicates if a customer is on or off-session while an invoice payment is attempted.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'payment-behavior': {
                    type: 'param',
                    description:
                      "Use `allow_incomplete` to create subscriptions with `status=incomplete` if the first invoice cannot be paid. Creating subscriptions with this status allows you to manage scenarios where additional user actions are needed to pay a subscription's invoice. For example, SCA regulation may require 3DS authentication to complete payment. See the SCA Migration Guide for Billing to learn more. This is the default behavior.  Use `default_incomplete` to create Subscriptions with `status=incomplete` when the first invoice requires payment, otherwise start as active. Subscriptions transition to `status=active` when successfully confirming the payment intent on the first invoice. This allows simpler management of scenarios where additional user actions are needed to pay a subscription’s invoice. Such as failed payments, SCA regulation, or collecting a mandate for a bank debit payment method. If the payment intent is not confirmed within 23 hours subscriptions transition to `status=incomplete_expired`, which is a terminal state.  Use `error_if_incomplete` if you want Stripe to return an HTTP 402 status code if a subscription's first invoice cannot be paid. For example, if a payment method requires 3DS authentication due to SCA regulation and further user action is needed, this parameter does not create a subscription and returns an error instead. This was the default behavior for API versions prior to 2019-03-14. See the changelog to learn more.  `pending_if_incomplete` is only used with updates and cannot be passed when creating a subscription.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'allow_incomplete',
                        'default_incomplete',
                        'error_if_incomplete',
                        'pending_if_incomplete',
                      ],
                    },
                  },
                  'payment-settings': {
                    type: 'param',
                    description:
                      'Payment settings to pass to invoices created by the subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'pending-invoice-item-interval': {
                    type: 'param',
                    description:
                      'Specifies an interval for how often to bill for any pending invoice items. It is analogous to calling Create an invoice for the given subscription at the specified interval.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'promotion-code': {
                    type: 'param',
                    description:
                      'The API ID of a promotion code to apply to this subscription. A promotion code applied to a subscription will only affect invoices created for that particular subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      'Determines how to handle prorations resulting from the `billing_cycle_anchor`. Valid values are `create_prorations` or `none`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. Prorations can be disabled by passing `none`. If no value is passed, the default is `create_prorations`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      "If specified, the funds from the subscription's invoices will be transferred to the destination and the ID of the resulting transfers will be found on the resulting charges.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'trial-end': {
                    type: 'param',
                    description:
                      "Unix timestamp representing the end of the trial period the customer will get before being charged for the first time. This will always overwrite any trials that might apply via a subscribed plan. If set, trial-end will override the default trial period of the plan the customer is being subscribed to. The special value `now` can be provided to end the customer's trial immediately. Can be at most two years from `billing_cycle_anchor`. See Using trial periods on subscriptions to learn more.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'trial-from-plan': {
                    type: 'param',
                    description:
                      "Indicates if a plan's `trial_period_days` should be applied to the subscription. Setting `trial_end` per subscription is preferred, and this defaults to `false`. Setting this flag to `true` together with `trial_end` is not allowed. See Using trial periods on subscriptions to learn more.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'trial-period-days': {
                    type: 'param',
                    description:
                      'Integer representing the number of trial period days before the customer is charged for the first time. This will always overwrite any trials that might apply via a subscribed plan. See Using trial periods on subscriptions to learn more.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                },
                args: [],
                path: '/v1/subscriptions',
                method: 'post',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates an existing subscription on a customer to match the specified parameters. When changing plans or quantities, we will optionally prorate the price we charge next month to make up for any price changes. To preview how the proration will be calculated, use the upcoming invoice endpoint.',
                params: {
                  'add-invoice-items': {
                    type: 'param',
                    description:
                      'A list of prices and quantities that will generate invoice items appended to the first invoice for this subscription. You may pass up to 20 items.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'application-fee-percent': {
                    type: 'param',
                    description:
                      "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice subtotal that will be transferred to the application owner's Stripe account. The request must be made by a platform account on a connected account in order to set an application fee percentage. For more information, see the application fees documentation.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'number',
                    },
                  },
                  'automatic-tax': {
                    type: 'param',
                    description:
                      'Automatic tax settings for this subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'billing-cycle-anchor': {
                    type: 'param',
                    description:
                      "Either `now` or `unchanged`. Setting the value to `now` resets the subscription's billing cycle anchor to the current time. For more information, see the billing cycle documentation.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['now', 'unchanged'],
                    },
                  },
                  'billing-thresholds': {
                    type: 'param',
                    description:
                      'Define thresholds at which an invoice will be sent, and the subscription advanced to a new billing period. Pass an empty string to remove previously-defined thresholds.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'cancel-at': {
                    type: 'param',
                    description:
                      'A timestamp at which the subscription should cancel. If set to a date before the current period ends, this will cause a proration if prorations have been enabled using `proration_behavior`. If set during a future period, this will always cause a proration for that period.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'DateTime',
                      },
                    },
                  },
                  'cancel-at-period-end': {
                    type: 'param',
                    description:
                      'Boolean indicating whether this subscription should cancel at the end of the current period.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'collection-method': {
                    type: 'param',
                    description:
                      'Either `charge_automatically`, or `send_invoice`. When charging automatically, Stripe will attempt to pay this subscription at the end of the cycle using the default source attached to the customer. When sending an invoice, Stripe will email your customer an invoice with payment instructions. Defaults to `charge_automatically`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['charge_automatically', 'send_invoice'],
                    },
                  },
                  coupon: {
                    type: 'param',
                    description:
                      'The ID of the coupon to apply to this subscription. A coupon applied to a subscription will only affect invoices created for that particular subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'days-until-due': {
                    type: 'param',
                    description:
                      'Number of days a customer has to pay invoices generated by this subscription. Valid only for subscriptions where `collection_method` is set to `send_invoice`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'default-payment-method': {
                    type: 'param',
                    description:
                      "ID of the default payment method for the subscription. It must belong to the customer associated with the subscription. This takes precedence over `default_source`. If neither are set, invoices will use the customer's invoice_settings.default-payment-method or default_source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-source': {
                    type: 'param',
                    description:
                      "ID of the default payment source for the subscription. It must belong to the customer associated with the subscription and be in a chargeable state. If `default_payment_method` is also set, `default_payment_method` will take precedence. If neither are set, invoices will use the customer's invoice_settings.default_payment_method or default-source.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-tax-rates': {
                    type: 'param',
                    description:
                      'The tax rates that will apply to any subscription item that does not have `tax_rates` set. Invoices created will have their `default-tax-rates` populated from the subscription. Pass an empty string to remove previously-defined tax rates.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  items: {
                    type: 'param',
                    description:
                      'A list of up to 20 subscription items, each with an attached price.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Indicates if a customer is on or off-session while an invoice payment is attempted.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'pause-collection': {
                    type: 'param',
                    description:
                      'If specified, payment collection for this subscription will be paused.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'payment-behavior': {
                    type: 'param',
                    description:
                      "Use `allow_incomplete` to transition the subscription to `status=past_due` if a payment is required but cannot be paid. This allows you to manage scenarios where additional user actions are needed to pay a subscription's invoice. For example, SCA regulation may require 3DS authentication to complete payment. See the SCA Migration Guide for Billing to learn more. This is the default behavior.  Use `default_incomplete` to transition the subscription to `status=past_due` when payment is required and await explicit confirmation of the invoice's payment intent. This allows simpler management of scenarios where additional user actions are needed to pay a subscription’s invoice. Such as failed payments, SCA regulation, or collecting a mandate for a bank debit payment method.  Use `pending_if_incomplete` to update the subscription using pending updates. When you use `pending_if_incomplete` you can only pass the parameters supported by pending updates.  Use `error_if_incomplete` if you want Stripe to return an HTTP 402 status code if a subscription's invoice cannot be paid. For example, if a payment method requires 3DS authentication due to SCA regulation and further user action is needed, this parameter does not update the subscription and returns an error instead. This was the default behavior for API versions prior to 2019-03-14. See the changelog to learn more.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'allow_incomplete',
                        'default_incomplete',
                        'error_if_incomplete',
                        'pending_if_incomplete',
                      ],
                    },
                  },
                  'payment-settings': {
                    type: 'param',
                    description:
                      'Payment settings to pass to invoices created by the subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'pending-invoice-item-interval': {
                    type: 'param',
                    description:
                      'Specifies an interval for how often to bill for any pending invoice items. It is analogous to calling Create an invoice for the given subscription at the specified interval.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'promotion-code': {
                    type: 'param',
                    description:
                      'The promotion code to apply to this subscription. A promotion code applied to a subscription will only affect invoices created for that particular subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'proration-date': {
                    type: 'param',
                    description:
                      'If set, the proration will be calculated as though the subscription was updated at the given time. This can be used to apply exactly the same proration that was previewed with upcoming invoice endpoint. It can also be used to implement custom proration logic, such as prorating by day instead of by second, by providing the time that you wish to use for proration calculations.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  'transfer-data': {
                    type: 'param',
                    description:
                      "If specified, the funds from the subscription's invoices will be transferred to the destination and the ID of the resulting transfers will be found on the resulting charges. This will be unset if you POST an empty value.",
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  'trial-end': {
                    type: 'param',
                    description:
                      "Unix timestamp representing the end of the trial period the customer will get before being charged for the first time. This will always overwrite any trials that might apply via a subscribed plan. If set, trial-end will override the default trial period of the plan the customer is being subscribed to. The special value `now` can be provided to end the customer's trial immediately. Can be at most two years from `billing_cycle_anchor`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'trial-from-plan': {
                    type: 'param',
                    description:
                      "Indicates if a plan's `trial_period_days` should be applied to the subscription. Setting `trial_end` per subscription is preferred, and this defaults to `false`. Setting this flag to `true` together with `trial_end` is not allowed. See Using trial periods on subscriptions to learn more.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['subscription_exposed_id'],
                path: '/v1/subscriptions/{subscription_exposed_id}',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves the subscription with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['subscription_exposed_id'],
                path: '/v1/subscriptions/{subscription_exposed_id}',
                method: 'get',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'Cancels a customer’s subscription immediately. The customer will not be charged again for the subscription.  Note, however, that any pending invoice items that you’ve created will still be charged for at the end of the period, unless manually deleted. If you’ve set the subscription to cancel at the end of the period, any pending prorations will also be left in place and collected at the end of the period. But if the subscription is set to cancel immediately, pending prorations will be removed.  By default, upon subscription cancellation, Stripe will stop automatic collection of all finalized invoices for the customer. This is intended to prevent unexpected payment attempts after the customer has canceled a subscription. However, you can resume automatic collection of the invoices manually after subscription cancellation to have us proceed. Or, you could check for unpaid invoices before allowing the customer to cancel the subscription at all.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-now': {
                    type: 'param',
                    description:
                      'Will generate a final invoice that invoices for any un-invoiced metered usage and new/pending proration invoice items.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  prorate: {
                    type: 'param',
                    description:
                      'Will generate a proration invoice item that credits remaining unused time until the subscription period end.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['subscription_exposed_id'],
                path: '/v1/subscriptions/{subscription_exposed_id}',
                method: 'delete',
                run: true,
              },
              delete_discount: {
                type: 'command',
                description:
                  'Removes the currently applied discount on a subscription.',
                params: {},
                args: ['subscription_exposed_id'],
                path: '/v1/subscriptions/{subscription_exposed_id}/discount',
                method: 'delete',
                run: true,
              },
            },
          },
          subscription_items: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of your subscription items for a given subscription.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      'The ID of the subscription whose items will be retrieved.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/subscription_items',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Adds a new item to an existing subscription. No existing items will be changed or replaced.',
                params: {
                  'billing-thresholds': {
                    type: 'param',
                    description:
                      'Define thresholds at which an invoice will be sent, and the subscription advanced to a new billing period. When updating, pass an empty string to remove previously-defined thresholds.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'payment-behavior': {
                    type: 'param',
                    description:
                      "Use `allow_incomplete` to transition the subscription to `status=past_due` if a payment is required but cannot be paid. This allows you to manage scenarios where additional user actions are needed to pay a subscription's invoice. For example, SCA regulation may require 3DS authentication to complete payment. See the SCA Migration Guide for Billing to learn more. This is the default behavior.  Use `default_incomplete` to transition the subscription to `status=past_due` when payment is required and await explicit confirmation of the invoice's payment intent. This allows simpler management of scenarios where additional user actions are needed to pay a subscription’s invoice. Such as failed payments, SCA regulation, or collecting a mandate for a bank debit payment method.  Use `pending_if_incomplete` to update the subscription using pending updates. When you use `pending_if_incomplete` you can only pass the parameters supported by pending updates.  Use `error_if_incomplete` if you want Stripe to return an HTTP 402 status code if a subscription's invoice cannot be paid. For example, if a payment method requires 3DS authentication due to SCA regulation and further user action is needed, this parameter does not update the subscription and returns an error instead. This was the default behavior for API versions prior to 2019-03-14. See the changelog to learn more.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'allow_incomplete',
                        'default_incomplete',
                        'error_if_incomplete',
                        'pending_if_incomplete',
                      ],
                    },
                  },
                  plan: {
                    type: 'param',
                    description:
                      'The identifier of the plan to add to the subscription.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  price: {
                    type: 'param',
                    description: 'The ID of the price object.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'price-data': {
                    type: 'param',
                    description:
                      'Data used to generate a new Price object inline.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'proration-date': {
                    type: 'param',
                    description:
                      'If set, the proration will be calculated as though the subscription was updated at the given time. This can be used to apply the same proration that was previewed with the upcoming invoice endpoint.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  quantity: {
                    type: 'param',
                    description:
                      "The quantity you'd like to apply to the subscription item you're creating.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  subscription: {
                    type: 'param',
                    description:
                      'The identifier of the subscription to modify.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-rates': {
                    type: 'param',
                    description:
                      'A list of Tax Rate ids. These Tax Rates will override the `default_tax-rates` on the Subscription. When updating, pass an empty string to remove previously-defined tax rates.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: [],
                path: '/v1/subscription_items',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the subscription item with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['item'],
                path: '/v1/subscription_items/{item}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the plan or quantity of an item on a current subscription.',
                params: {
                  'billing-thresholds': {
                    type: 'param',
                    description:
                      'Define thresholds at which an invoice will be sent, and the subscription advanced to a new billing period. When updating, pass an empty string to remove previously-defined thresholds.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'primitive',
                        primitive: 'void',
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'off-session': {
                    type: 'param',
                    description:
                      'Indicates if a customer is on or off-session while an invoice payment is attempted.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'payment-behavior': {
                    type: 'param',
                    description:
                      "Use `allow_incomplete` to transition the subscription to `status=past_due` if a payment is required but cannot be paid. This allows you to manage scenarios where additional user actions are needed to pay a subscription's invoice. For example, SCA regulation may require 3DS authentication to complete payment. See the SCA Migration Guide for Billing to learn more. This is the default behavior.  Use `default_incomplete` to transition the subscription to `status=past_due` when payment is required and await explicit confirmation of the invoice's payment intent. This allows simpler management of scenarios where additional user actions are needed to pay a subscription’s invoice. Such as failed payments, SCA regulation, or collecting a mandate for a bank debit payment method.  Use `pending_if_incomplete` to update the subscription using pending updates. When you use `pending_if_incomplete` you can only pass the parameters supported by pending updates.  Use `error_if_incomplete` if you want Stripe to return an HTTP 402 status code if a subscription's invoice cannot be paid. For example, if a payment method requires 3DS authentication due to SCA regulation and further user action is needed, this parameter does not update the subscription and returns an error instead. This was the default behavior for API versions prior to 2019-03-14. See the changelog to learn more.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'allow_incomplete',
                        'default_incomplete',
                        'error_if_incomplete',
                        'pending_if_incomplete',
                      ],
                    },
                  },
                  plan: {
                    type: 'param',
                    description:
                      'The identifier of the new plan for this subscription item.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  price: {
                    type: 'param',
                    description:
                      "The ID of the price object. When changing a subscription item's price, `quantity` is set to 1 unless a `quantity` parameter is provided.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'price-data': {
                    type: 'param',
                    description:
                      'Data used to generate a new Price object inline.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'proration-date': {
                    type: 'param',
                    description:
                      'If set, the proration will be calculated as though the subscription was updated at the given time. This can be used to apply the same proration that was previewed with the upcoming invoice endpoint.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                  quantity: {
                    type: 'param',
                    description:
                      "The quantity you'd like to apply to the subscription item you're creating.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'tax-rates': {
                    type: 'param',
                    description:
                      'A list of Tax Rate ids. These Tax Rates will override the `default_tax-rates` on the Subscription. When updating, pass an empty string to remove previously-defined tax rates.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'array',
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['item'],
                path: '/v1/subscription_items/{item}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'Deletes an item from the subscription. Removing a subscription item from a subscription will not cancel the subscription.',
                params: {
                  'clear-usage': {
                    type: 'param',
                    description:
                      "Delete all usage for the given subscription item. Allowed only when the current plan's `usage_type` is `metered`.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      "Determines how to handle prorations when the billing cycle changes (e.g., when switching plans, resetting `billing_cycle_anchor=now`, or starting a trial), or if an item's `quantity` changes. Valid values are `create_prorations`, `none`, or `always_invoice`.  Passing `create_prorations` will cause proration invoice items to be created when applicable. These proration items will only be invoiced immediately under certain conditions. In order to always invoice immediately for prorations, pass `always_invoice`.  Prorations can be disabled by passing `none`.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                  'proration-date': {
                    type: 'param',
                    description:
                      'If set, the proration will be calculated as though the subscription was updated at the given time. This can be used to apply the same proration that was previewed with the upcoming invoice endpoint.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'DateTime',
                    },
                  },
                },
                args: ['item'],
                path: '/v1/subscription_items/{item}',
                method: 'delete',
                run: true,
              },
              usage_record_summaries: {
                type: 'command',
                description:
                  'For the specified subscription item, returns a list of summary objects. Each object in the list provides usage information that’s been summarized from multiple usage records and over a subscription billing period (e.g., 15 usage records in the month of September).  The list is sorted in reverse-chronological order (newest first). The first list item represents the most current usage period that hasn’t ended yet. Since new usage records can still be added, the returned summary information for the subscription item’s ID should be seen as unstable until the subscription billing period ends.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['subscription_item'],
                path:
                  '/v1/subscription_items/{subscription_item}/usage_record_summaries',
                method: 'get',
                run: true,
              },
            },
          },
          subscription_schedules: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Retrieves the list of your subscription schedules.',
                params: {
                  'canceled-at': {
                    type: 'param',
                    description:
                      'Only return subscription schedules that were created canceled the given date interval.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'completed-at': {
                    type: 'param',
                    description:
                      'Only return subscription schedules that completed during the given date interval.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'Only return subscription schedules that were created during the given date interval.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'Only return subscription schedules for the given customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'released-at': {
                    type: 'param',
                    description:
                      'Only return subscription schedules that were released during the given date interval.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  scheduled: {
                    type: 'param',
                    description:
                      'Only return subscription schedules that have not started yet.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/subscription_schedules',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a new subscription schedule object. Each customer can have up to 500 active or scheduled subscriptions.',
                params: {
                  customer: {
                    type: 'param',
                    description:
                      'The identifier of the customer to create the subscription schedule for.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'default-settings': {
                    type: 'param',
                    description:
                      "Object representing the subscription schedule's default settings.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'end-behavior': {
                    type: 'param',
                    description:
                      'Configures how the subscription schedule behaves when it ends. Possible values are `release` or `cancel` with the default being `release`. `release` will end the subscription schedule and keep the underlying subscription running.`cancel` will end the subscription schedule and cancel the underlying subscription.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['cancel', 'none', 'release', 'renew'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'from-subscription': {
                    type: 'param',
                    description:
                      "Migrate an existing subscription to be managed by a subscription schedule. If this parameter is set, a subscription schedule will be created using the subscription's item(s), set to auto-renew using the subscription's interval. When using this parameter, other parameters (such as phase values) cannot be set. To create a subscription schedule with other modifications, we recommend making two separate API calls.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  phases: {
                    type: 'param',
                    description:
                      'List representing phases of the subscription schedule. Each phase can be customized to have different durations, plans, and coupons. If there are multiple phases, the `end_date` of one phase will always equal the `start_date` of the next phase.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'start-date': {
                    type: 'param',
                    description:
                      'When the subscription schedule starts. We recommend using `now` so that it starts the subscription immediately. You can also use a Unix timestamp to backdate the subscription so that it starts on a past date, or set a future date for the subscription to start on.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/subscription_schedules',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing subscription schedule. You only need to supply the unique subscription schedule identifier that was returned upon subscription schedule creation.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['schedule'],
                path: '/v1/subscription_schedules/{schedule}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing subscription schedule.',
                params: {
                  'default-settings': {
                    type: 'param',
                    description:
                      "Object representing the subscription schedule's default settings.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'end-behavior': {
                    type: 'param',
                    description:
                      'Configures how the subscription schedule behaves when it ends. Possible values are `release` or `cancel` with the default being `release`. `release` will end the subscription schedule and keep the underlying subscription running.`cancel` will end the subscription schedule and cancel the underlying subscription.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['cancel', 'none', 'release', 'renew'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  phases: {
                    type: 'param',
                    description:
                      'List representing phases of the subscription schedule. Each phase can be customized to have different durations, plans, and coupons. If there are multiple phases, the `end_date` of one phase will always equal the `start_date` of the next phase. Note that past phases can be omitted.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'proration-behavior': {
                    type: 'param',
                    description:
                      'If the update changes the current phase, indicates if the changes should be prorated. Possible values are `create_prorations` or `none`, and the default value is `create_prorations`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['always_invoice', 'create_prorations', 'none'],
                    },
                  },
                },
                args: ['schedule'],
                path: '/v1/subscription_schedules/{schedule}',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'Cancels a subscription schedule and its associated subscription immediately (if the subscription schedule has an active subscription). A subscription schedule can only be canceled if its status is not_started or active.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'invoice-now': {
                    type: 'param',
                    description:
                      'If the subscription schedule is `active`, indicates if a final invoice will be generated that contains any un-invoiced metered usage and new/pending proration invoice items. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  prorate: {
                    type: 'param',
                    description:
                      'If the subscription schedule is `active`, indicates if the cancellation should be prorated. Defaults to `true`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['schedule'],
                path: '/v1/subscription_schedules/{schedule}/cancel',
                method: 'post',
                run: true,
              },
              release: {
                type: 'command',
                description:
                  'Releases the subscription schedule immediately, which will stop scheduling of its phases, but leave any existing subscription in place. A schedule can only be released if its status is not_started or active. If the subscription schedule is currently associated with a subscription, releasing it will remove its subscription property and set the subscription’s ID to the released_subscription property.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'preserve-cancel-date': {
                    type: 'param',
                    description:
                      'Keep any cancellation on the subscription that the schedule has set',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['schedule'],
                path: '/v1/subscription_schedules/{schedule}/release',
                method: 'post',
                run: true,
              },
            },
          },
          tax_codes: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'A list of all tax codes available to add to Products in order to allow specific tax calculations.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/tax_codes',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing tax code. Supply the unique tax code ID and Stripe will return the corresponding tax code information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id'],
                path: '/v1/tax_codes/{id}',
                method: 'get',
                run: true,
              },
            },
          },
          tax_ids: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description: 'Creates a new TaxID object for a customer.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  type: {
                    type: 'param',
                    description:
                      'Type of the tax ID, one of `ae_trn`, `au_abn`, `au_arn`, `bg_uic`, `br_cnpj`, `br_cpf`, `ca_bn`, `ca_gst_hst`, `ca_pst_bc`, `ca_pst_mb`, `ca_pst_sk`, `ca_qst`, `ch_vat`, `cl_tin`, `es_cif`, `eu_vat`, `gb_vat`, `ge_vat`, `hk_br`, `hu_tin`, `id_npwp`, `il_vat`, `in_gst`, `is_vat`, `jp_cn`, `jp_rn`, `kr_brn`, `li_uid`, `mx_rfc`, `my_frp`, `my_itn`, `my_sst`, `no_vat`, `nz_gst`, `ru_inn`, `ru_kpp`, `sa_vat`, `sg_gst`, `sg_uen`, `si_tin`, `th_vat`, `tw_vat`, `ua_vat`, `us_ein`, or `za_vat`',
                    required: true,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'ae_trn',
                        'au_abn',
                        'au_arn',
                        'bg_uic',
                        'br_cnpj',
                        'br_cpf',
                        'ca_bn',
                        'ca_gst_hst',
                        'ca_pst_bc',
                        'ca_pst_mb',
                        'ca_pst_sk',
                        'ca_qst',
                        'ch_vat',
                        'cl_tin',
                        'es_cif',
                        'eu_vat',
                        'gb_vat',
                        'ge_vat',
                        'hk_br',
                        'hu_tin',
                        'id_npwp',
                        'il_vat',
                        'in_gst',
                        'is_vat',
                        'jp_cn',
                        'jp_rn',
                        'kr_brn',
                        'li_uid',
                        'mx_rfc',
                        'my_frp',
                        'my_itn',
                        'my_sst',
                        'no_vat',
                        'nz_gst',
                        'ru_inn',
                        'ru_kpp',
                        'sa_vat',
                        'sg_gst',
                        'sg_uen',
                        'si_tin',
                        'th_vat',
                        'tw_vat',
                        'ua_vat',
                        'us_ein',
                        'za_vat',
                      ],
                    },
                  },
                  value: {
                    type: 'param',
                    description: 'Value of the tax ID.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/tax_ids',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of tax IDs for a customer.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['customer'],
                path: '/v1/customers/{customer}/tax_ids',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the TaxID object with the given identifier.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['customer', 'id'],
                path: '/v1/customers/{customer}/tax_ids/{id}',
                method: 'get',
                run: true,
              },
              delete: {
                type: 'command',
                description: 'Deletes an existing TaxID object.',
                params: {},
                args: ['customer', 'id'],
                path: '/v1/customers/{customer}/tax_ids/{id}',
                method: 'delete',
                run: true,
              },
            },
          },
          tax_rates: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'Returns a list of your tax rates. Tax rates are returned sorted by creation date, with the most recently created tax rates appearing first.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Optional flag to filter by tax rates that are either active or inactive (archived).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  created: {
                    type: 'param',
                    description: 'Optional range for filtering created date.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  inclusive: {
                    type: 'param',
                    description:
                      'Optional flag to filter by tax rates that are inclusive (or those that are not inclusive).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/tax_rates',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Creates a new tax rate.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Flag determining whether the tax rate is active or inactive (archived). Inactive tax rates cannot be used with new applications or Checkout Sessions, but will still work for subscriptions and invoices that already have it set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  country: {
                    type: 'param',
                    description:
                      'Two-letter country code (ISO 3166-1 alpha-2).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the tax rate for your internal use only. It will not be visible to your customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'display-name': {
                    type: 'param',
                    description:
                      'The display name of the tax rate, which will be shown to users.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  inclusive: {
                    type: 'param',
                    description:
                      'This specifies if the tax rate is inclusive or exclusive.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  jurisdiction: {
                    type: 'param',
                    description:
                      'The jurisdiction for the tax rate. You can use this label field for tax reporting purposes. It also appears on your customer’s invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  percentage: {
                    type: 'param',
                    description:
                      'This represents the tax rate percent out of 100.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'number',
                    },
                  },
                  state: {
                    type: 'param',
                    description:
                      'ISO 3166-2 subdivision code, without country prefix. For example, "NY" for New York, United States.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-type': {
                    type: 'param',
                    description:
                      'The high-level tax type, such as `vat` or `sales_tax`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'gst',
                        'hst',
                        'jct',
                        'pst',
                        'qst',
                        'rst',
                        'sales_tax',
                        'vat',
                      ],
                    },
                  },
                },
                args: [],
                path: '/v1/tax_rates',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description: 'Retrieves a tax rate with the given ID',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['tax_rate'],
                path: '/v1/tax_rates/{tax_rate}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description: 'Updates an existing tax rate.',
                params: {
                  active: {
                    type: 'param',
                    description:
                      'Flag determining whether the tax rate is active or inactive (archived). Inactive tax rates cannot be used with new applications or Checkout Sessions, but will still work for subscriptions and invoices that already have it set.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  country: {
                    type: 'param',
                    description:
                      'Two-letter country code (ISO 3166-1 alpha-2).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the tax rate for your internal use only. It will not be visible to your customers.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'display-name': {
                    type: 'param',
                    description:
                      'The display name of the tax rate, which will be shown to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  jurisdiction: {
                    type: 'param',
                    description:
                      'The jurisdiction for the tax rate. You can use this label field for tax reporting purposes. It also appears on your customer’s invoice.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  state: {
                    type: 'param',
                    description:
                      'ISO 3166-2 subdivision code, without country prefix. For example, "NY" for New York, United States.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'tax-type': {
                    type: 'param',
                    description:
                      'The high-level tax type, such as `vat` or `sales_tax`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        'gst',
                        'hst',
                        'jct',
                        'pst',
                        'qst',
                        'rst',
                        'sales_tax',
                        'vat',
                      ],
                    },
                  },
                },
                args: ['tax_rate'],
                path: '/v1/tax_rates/{tax_rate}',
                method: 'post',
                run: true,
              },
            },
          },
          terminal: {
            type: 'command',
            commands: {
              connection_tokens: {
                type: 'command',
                commands: {
                  create: {
                    type: 'command',
                    description:
                      'To connect to a reader the Stripe Terminal SDK needs to retrieve a short-lived connection token from Stripe, proxied through your server. On your backend, add an endpoint that creates and returns a connection token.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      location: {
                        type: 'param',
                        description:
                          'The id of the location that this connection token is scoped to. If specified the connection token will only be usable with readers assigned to that location, otherwise the connection token will be usable with all readers. Note that location scoping only applies to internet-connected readers. For more details, see the docs on scoping connection tokens.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/terminal/connection_tokens',
                    method: 'post',
                    run: true,
                  },
                },
              },
              locations: {
                type: 'command',
                commands: {
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a Location object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['location'],
                    path: '/v1/terminal/locations/{location}',
                    method: 'get',
                    run: true,
                  },
                  update: {
                    type: 'command',
                    description:
                      'Updates a Location object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      address: {
                        type: 'param',
                        description: 'The full address of the location.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'display-name': {
                        type: 'param',
                        description: 'A name for the location.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['location'],
                    path: '/v1/terminal/locations/{location}',
                    method: 'post',
                    run: true,
                  },
                  delete: {
                    type: 'command',
                    description: 'Deletes a Location object.',
                    params: {},
                    args: ['location'],
                    path: '/v1/terminal/locations/{location}',
                    method: 'delete',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new Location object. For further details, including which address fields are required in each country, see the Manage locations guide.',
                    params: {
                      address: {
                        type: 'param',
                        description: 'The full address of the location.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      'display-name': {
                        type: 'param',
                        description: 'A name for the location.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: [],
                    path: '/v1/terminal/locations',
                    method: 'post',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'Returns a list of Location objects.',
                    params: {
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/terminal/locations',
                    method: 'get',
                    run: true,
                  },
                },
              },
              readers: {
                type: 'command',
                commands: {
                  update: {
                    type: 'command',
                    description:
                      'Updates a Reader object by setting the values of the parameters passed. Any parameters not provided will be left unchanged.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      label: {
                        type: 'param',
                        description: 'The new label of the reader.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                    },
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}',
                    method: 'post',
                    run: true,
                  },
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a Reader object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}',
                    method: 'get',
                    run: true,
                  },
                  delete: {
                    type: 'command',
                    description: 'Deletes a Reader object.',
                    params: {},
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}',
                    method: 'delete',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description: 'Creates a new Reader object.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      label: {
                        type: 'param',
                        description:
                          'Custom label given to the reader for easier identification. If no label is specified, the registration code will be used.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      location: {
                        type: 'param',
                        description: 'The location to assign the reader to.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      metadata: {
                        type: 'param',
                        description:
                          'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                        required: false,
                        input_type: {
                          shape: 'emptyStringable',
                          type: {
                            shape: 'map',
                            keyType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                            elementType: {
                              shape: 'primitive',
                              primitive: 'string',
                            },
                          },
                        },
                      },
                      'registration-code': {
                        type: 'param',
                        description:
                          'A code generated by the reader used for registering to an account.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/terminal/readers',
                    method: 'post',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'Returns a list of Reader objects.',
                    params: {
                      'device-type': {
                        type: 'param',
                        description: 'Filters readers by device type',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: [
                            'bbpos_chipper2x',
                            'bbpos_wisepad3',
                            'bbpos_wisepos_e',
                            'stripe_m2',
                            'verifone_P400',
                          ],
                        },
                      },
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      location: {
                        type: 'param',
                        description:
                          'A location ID to filter the response list to only readers at the specific location',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      status: {
                        type: 'param',
                        description:
                          'A status filter to filter readers to only offline or online readers',
                        required: false,
                        input_type: {
                          shape: 'enum',
                          enum: ['offline', 'online'],
                        },
                      },
                    },
                    args: [],
                    path: '/v1/terminal/readers',
                    method: 'get',
                    run: true,
                  },
                  process_payment_intent: {
                    type: 'command',
                    description: 'Initiates a payment flow on a Reader.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'payment-intent': {
                        type: 'param',
                        description: 'PaymentIntent ID',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      'process-config': {
                        type: 'param',
                        description: 'Configuration overrides',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                    },
                    args: ['reader'],
                    path:
                      '/v1/terminal/readers/{reader}/process_payment_intent',
                    method: 'post',
                    run: true,
                  },
                  process_setup_intent: {
                    type: 'command',
                    description: 'Initiates a setup intent flow on a Reader.',
                    params: {
                      'customer-consent-collected': {
                        type: 'param',
                        description: 'Customer Consent Collected',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'boolean',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'setup-intent': {
                        type: 'param',
                        description: 'SetupIntent ID',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}/process_setup_intent',
                    method: 'post',
                    run: true,
                  },
                  cancel_action: {
                    type: 'command',
                    description: 'Cancels the current reader action.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}/cancel_action',
                    method: 'post',
                    run: true,
                  },
                  set_reader_display: {
                    type: 'command',
                    description: 'Sets reader display to show cart details.',
                    params: {
                      cart: {
                        type: 'param',
                        description: 'Cart',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'void',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      type: {
                        type: 'param',
                        description: 'Type',
                        required: true,
                        input_type: {
                          shape: 'literal',
                          literal: 'cart',
                        },
                      },
                    },
                    args: ['reader'],
                    path: '/v1/terminal/readers/{reader}/set_reader_display',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          test_helpers: {
            type: 'command',
            commands: {
              test_clocks: {
                type: 'command',
                commands: {
                  retrieve: {
                    type: 'command',
                    description: 'Retrieves a test clock.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                    },
                    args: ['test_clock'],
                    path: '/v1/test_helpers/test_clocks/{test_clock}',
                    method: 'get',
                    run: true,
                  },
                  delete: {
                    type: 'command',
                    description: 'Deletes a test clock.',
                    params: {},
                    args: ['test_clock'],
                    path: '/v1/test_helpers/test_clocks/{test_clock}',
                    method: 'delete',
                    run: true,
                  },
                  create: {
                    type: 'command',
                    description:
                      'Creates a new test clock that can be attached to new customers and quotes.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'frozen-time': {
                        type: 'param',
                        description:
                          'The initial frozen time for this test clock.',
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'DateTime',
                        },
                      },
                      name: {
                        type: 'param',
                        description: 'The name for this test clock.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/test_helpers/test_clocks',
                    method: 'post',
                    run: true,
                  },
                  list: {
                    type: 'command',
                    description: 'Returns a list of your test clocks.',
                    params: {
                      'ending-before': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      limit: {
                        type: 'param',
                        description:
                          'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'integer',
                        },
                      },
                      'starting-after': {
                        type: 'param',
                        description:
                          'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                        required: false,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                    args: [],
                    path: '/v1/test_helpers/test_clocks',
                    method: 'get',
                    run: true,
                  },
                  advance: {
                    type: 'command',
                    description:
                      'Starts advancing a test clock to a specified time in the future. Advancement is done when status changes to Ready.',
                    params: {
                      expand: {
                        type: 'param',
                        description:
                          'Specifies which fields in the response should be expanded.',
                        required: false,
                        input_type: {
                          shape: 'array',
                          elementType: {
                            shape: 'primitive',
                            primitive: 'string',
                          },
                        },
                      },
                      'frozen-time': {
                        type: 'param',
                        description:
                          "The time to advance the test clock. Must be after the test clock's current frozen time. Cannot be more than two intervals in the future from the shortest subscription in this test clock. If there are no subscriptions in this test clock, it cannot be more than two years in the future.",
                        required: true,
                        input_type: {
                          shape: 'primitive',
                          primitive: 'DateTime',
                        },
                      },
                    },
                    args: ['test_clock'],
                    path: '/v1/test_helpers/test_clocks/{test_clock}/advance',
                    method: 'post',
                    run: true,
                  },
                },
              },
            },
          },
          three_d_secures: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves a 3D Secure object.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['three_d_secure'],
                path: '/v1/3d_secure/{three_d_secure}',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description: 'Initiate 3D Secure authentication.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'Amount of the charge that you will create when authentication completes.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  card: {
                    type: 'param',
                    description:
                      'The ID of a card token, or the ID of a card belonging to the given customer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      'The customer associated with this 3D secure authentication.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'return-url': {
                    type: 'param',
                    description:
                      "The URL that the cardholder's browser will be returned to when authentication completes.",
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/3d_secure',
                method: 'post',
                run: true,
              },
            },
          },
          tokens: {
            type: 'command',
            commands: {
              retrieve: {
                type: 'command',
                description: 'Retrieves the token with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['token'],
                path: '/v1/tokens/{token}',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'Creates a single-use token that represents a bank account’s details. This token can be used with any API method in place of a bank account dictionary. This token can be used only once, by attaching it to a Custom account.',
                params: {
                  account: {
                    type: 'param',
                    description:
                      'Information for the account this token will represent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'bank-account': {
                    type: 'param',
                    description: 'The bank account this token will represent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  card: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  customer: {
                    type: 'param',
                    description:
                      "The customer (owned by the application's account) for which to create a token. This can be used only with an OAuth access token or Stripe-Account header. For more details, see Cloning Saved Payment Methods.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'cvc-update': {
                    type: 'param',
                    description:
                      'The updated CVC value this token will represent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  person: {
                    type: 'param',
                    description:
                      'Information for the person this token will represent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  pii: {
                    type: 'param',
                    description: 'The PII this token will represent.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: [],
                path: '/v1/tokens',
                method: 'post',
                run: true,
              },
            },
          },
          topups: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description: 'Top up the balance of an account',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer representing how much to transfer.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description:
                      'Three-letter ISO currency code, in lowercase. Must be a supported currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  source: {
                    type: 'param',
                    description:
                      'The ID of a source to transfer funds from. For most users, this should be left unspecified which will use the bank account that was set up in the dashboard for the specified currency. In test mode, this can be a test bank token (see Testing Top-ups).',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'statement-descriptor': {
                    type: 'param',
                    description:
                      "Extra information about a top-up for the source's bank statement. Limited to 15 ASCII characters.",
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies this top-up as part of a group.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/topups',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description: 'Returns a list of top-ups.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer representing how much to transfer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  created: {
                    type: 'param',
                    description:
                      'A filter on the list, based on the object `created` field. The value can be a string with an integer Unix timestamp, or it can be a dictionary with a number of different query options.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  status: {
                    type: 'param',
                    description:
                      'Only return top-ups that have the given status. One of `canceled`, `failed`, `pending` or `succeeded`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['canceled', 'failed', 'pending', 'succeeded'],
                    },
                  },
                },
                args: [],
                path: '/v1/topups',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of a top-up that has previously been created. Supply the unique top-up ID that was returned from your previous request, and Stripe will return the corresponding top-up information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['topup'],
                path: '/v1/topups/{topup}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the metadata of a top-up. Other top-up details are not editable by design.',
                params: {
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['topup'],
                path: '/v1/topups/{topup}',
                method: 'post',
                run: true,
              },
              cancel: {
                type: 'command',
                description:
                  'Cancels a top-up. Only pending top-ups can be canceled.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['topup'],
                path: '/v1/topups/{topup}/cancel',
                method: 'post',
                run: true,
              },
            },
          },
          transfers: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'To send funds from your Stripe account to a connected account, you create a new transfer object. Your Stripe balance must be able to cover the transfer amount, or you’ll receive an “Insufficient Funds” error.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer in %s representing how much to transfer.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  currency: {
                    type: 'param',
                    description: '3-letter ISO code for currency.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  destination: {
                    type: 'param',
                    description:
                      'The ID of a connected Stripe account. See the Connect documentation for details.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'map',
                      keyType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  'source-transaction': {
                    type: 'param',
                    description:
                      'You can use this parameter to transfer funds from a charge before they are added to your available balance. A pending balance will transfer immediately but the funds will not become available until the original charge becomes available. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'source-type': {
                    type: 'param',
                    description:
                      'The source balance to use for this transfer. One of `bank_account`, `card`, or `fpx`. For most users, this will default to `card`.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['bank_account', 'card', 'fpx'],
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'A string that identifies this transaction as part of a group. See the Connect documentation for details.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/transfers',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'Returns a list of existing transfers sent to connected accounts. The transfers are returned in sorted order, with the most recently created transfers appearing first.',
                params: {
                  created: {
                    type: 'param',
                    description: '',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                  destination: {
                    type: 'param',
                    description:
                      'Only return transfers for the destination specified by this account ID.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'transfer-group': {
                    type: 'param',
                    description:
                      'Only return transfers with the specified transfer group.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/transfers',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the details of an existing transfer. Supply the unique transfer ID from either a transfer creation request or the transfer list, and Stripe will return the corresponding transfer information.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['transfer'],
                path: '/v1/transfers/{transfer}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified transfer by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  This request accepts only metadata as an argument.',
                params: {
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string attached to the object. Often useful for displaying to users.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['transfer'],
                path: '/v1/transfers/{transfer}',
                method: 'post',
                run: true,
              },
            },
          },
          transfer_reversals: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'When you create a new reversal, you must specify a transfer to create it on.  When reversing transfers, you can optionally reverse part of the transfer. You can do so as many times as you wish until the entire transfer has been reversed.  Once entirely reversed, a transfer can’t be reversed again. This method will return an error when called on an already-reversed transfer, or when trying to reverse more money than is left on a transfer.',
                params: {
                  amount: {
                    type: 'param',
                    description:
                      'A positive integer in %s representing how much of this transfer to reverse. Can only reverse up to the unreversed amount remaining of the transfer. Partial transfer reversals are only allowed for transfers to Stripe Accounts. Defaults to the entire transfer amount.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An arbitrary string which you can attach to a reversal object. It is displayed alongside the reversal in the Dashboard. This will be unset if you POST an empty value.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  'refund-application-fee': {
                    type: 'param',
                    description:
                      'Boolean indicating whether the application fee should be refunded when reversing this transfer. If a full transfer reversal is given, the full application fee will be refunded. Otherwise, the application fee will be refunded with an amount proportional to the amount of the transfer reversed.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/transfers/{id}/reversals',
                method: 'post',
                run: true,
              },
              list: {
                type: 'command',
                description:
                  'You can see a list of the reversals belonging to a specific transfer. Note that the 10 most recent reversals are always available by default on the transfer object. If you need more than those 10, you can use this API method and the limit and starting_after parameters to page through additional reversals.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['id'],
                path: '/v1/transfers/{id}/reversals',
                method: 'get',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'By default, you can see the 10 most recent reversals stored directly on the transfer object, but you can also retrieve details about a specific reversal stored on the transfer.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['id', 'transfer'],
                path: '/v1/transfers/{transfer}/reversals/{id}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the specified reversal by setting the values of the parameters passed. Any parameters not provided will be left unchanged.  This request only accepts metadata and description as arguments.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                },
                args: ['id', 'transfer'],
                path: '/v1/transfers/{transfer}/reversals/{id}',
                method: 'post',
                run: true,
              },
            },
          },
          usage_records: {
            type: 'command',
            commands: {
              create: {
                type: 'command',
                description:
                  'Creates a usage record for a specified subscription item and date, and fills it with a quantity.  Usage records provide quantity information that Stripe uses to track how much a customer is using your service. With usage information and the pricing model set up by the metered billing plan, Stripe helps you send accurate invoices to your customers.  The default calculation for usage is to add up all the quantity values of the usage records within a billing period. You can change this default behavior with the billing plan’s aggregate_usage parameter. When there is more than one usage record with the same timestamp, Stripe adds the quantity values together. In most cases, this is the desired resolution, however, you can change this behavior with the action parameter.  The default pricing model for metered billing is per-unit pricing. For finer granularity, you can configure metered billing to have a tiered pricing model.',
                params: {
                  action: {
                    type: 'param',
                    description:
                      'Valid values are `increment` (default) or `set`. When using `increment` the specified `quantity` will be added to the usage at the specified timestamp. The `set` action will overwrite the usage quantity at that timestamp. If the subscription has billing thresholds, `increment` is the only allowed value.',
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: ['increment', 'set'],
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  quantity: {
                    type: 'param',
                    description:
                      'The usage quantity for the specified timestamp.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  timestamp: {
                    type: 'param',
                    description:
                      'The timestamp for the usage event. This timestamp must be within the current billing period of the subscription of the provided `subscription_item`, and must not be in the future. When passing `"now"`, Stripe records usage for the current time. Default is `"now"` if a value is not provided.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'void',
                    },
                  },
                },
                args: ['subscription_item'],
                path:
                  '/v1/subscription_items/{subscription_item}/usage_records',
                method: 'post',
                run: true,
              },
            },
          },
          usage_record_summaries: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description:
                  'For the specified subscription item, returns a list of summary objects. Each object in the list provides usage information that’s been summarized from multiple usage records and over a subscription billing period (e.g., 15 usage records in the month of September).  The list is sorted in reverse-chronological order (newest first). The first list item represents the most current usage period that hasn’t ended yet. Since new usage records can still be added, the returned summary information for the subscription item’s ID should be seen as unstable until the subscription billing period ends.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['subscription_item'],
                path:
                  '/v1/subscription_items/{subscription_item}/usage_record_summaries',
                method: 'get',
                run: true,
              },
            },
          },
          webhook_endpoints: {
            type: 'command',
            commands: {
              list: {
                type: 'command',
                description: 'Returns a list of your webhook endpoints.',
                params: {
                  'ending-before': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `ending-before` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, starting with `obj_bar`, your subsequent call can include `ending-before=obj_bar` in order to fetch the previous page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  limit: {
                    type: 'param',
                    description:
                      'A limit on the number of objects to be returned. Limit can range between 1 and 100, and the default is 10.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'integer',
                    },
                  },
                  'starting-after': {
                    type: 'param',
                    description:
                      'A cursor for use in pagination. `starting-after` is an object ID that defines your place in the list. For instance, if you make a list request and receive 100 objects, ending with `obj_foo`, your subsequent call can include `starting-after=obj_foo` in order to fetch the next page of the list.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/webhook_endpoints',
                method: 'get',
                run: true,
              },
              create: {
                type: 'command',
                description:
                  'A webhook endpoint must have a url and a list of enabled_events. You may optionally specify the Boolean connect parameter. If set to true, then a Connect webhook endpoint that notifies the specified url about events from all connected accounts is created; otherwise an account webhook endpoint that notifies the specified url only about events from your account is created. You can also create webhook endpoints in the webhooks settings section of the Dashboard.',
                params: {
                  'api-version': {
                    type: 'param',
                    description:
                      "Events sent to this endpoint will be generated with this Stripe Version instead of your account's default Stripe Version.",
                    required: false,
                    input_type: {
                      shape: 'enum',
                      enum: [
                        '2011-01-01',
                        '2011-06-21',
                        '2011-06-28',
                        '2011-08-01',
                        '2011-09-15',
                        '2011-11-17',
                        '2012-02-23',
                        '2012-03-25',
                        '2012-06-18',
                        '2012-06-28',
                        '2012-07-09',
                        '2012-09-24',
                        '2012-10-26',
                        '2012-11-07',
                        '2013-02-11',
                        '2013-02-13',
                        '2013-07-05',
                        '2013-08-12',
                        '2013-08-13',
                        '2013-10-29',
                        '2013-12-03',
                        '2014-01-31',
                        '2014-03-13',
                        '2014-03-28',
                        '2014-05-19',
                        '2014-06-13',
                        '2014-06-17',
                        '2014-07-22',
                        '2014-07-26',
                        '2014-08-04',
                        '2014-08-20',
                        '2014-09-08',
                        '2014-10-07',
                        '2014-11-05',
                        '2014-11-20',
                        '2014-12-08',
                        '2014-12-17',
                        '2014-12-22',
                        '2015-01-11',
                        '2015-01-26',
                        '2015-02-10',
                        '2015-02-16',
                        '2015-02-18',
                        '2015-03-24',
                        '2015-04-07',
                        '2015-06-15',
                        '2015-07-07',
                        '2015-07-13',
                        '2015-07-28',
                        '2015-08-07',
                        '2015-08-19',
                        '2015-09-03',
                        '2015-09-08',
                        '2015-09-23',
                        '2015-10-01',
                        '2015-10-12',
                        '2015-10-16',
                        '2016-02-03',
                        '2016-02-19',
                        '2016-02-22',
                        '2016-02-23',
                        '2016-02-29',
                        '2016-03-07',
                        '2016-06-15',
                        '2016-07-06',
                        '2016-10-19',
                        '2017-01-27',
                        '2017-02-14',
                        '2017-04-06',
                        '2017-05-25',
                        '2017-06-05',
                        '2017-08-15',
                        '2017-12-14',
                        '2018-01-23',
                        '2018-02-05',
                        '2018-02-06',
                        '2018-02-28',
                        '2018-05-21',
                        '2018-07-27',
                        '2018-08-23',
                        '2018-09-06',
                        '2018-09-24',
                        '2018-10-31',
                        '2018-11-08',
                        '2019-02-11',
                        '2019-02-19',
                        '2019-03-14',
                        '2019-05-16',
                        '2019-08-14',
                        '2019-09-09',
                        '2019-10-08',
                        '2019-10-17',
                        '2019-11-05',
                        '2019-12-03',
                        '2020-03-02',
                        '2020-08-27',
                      ],
                    },
                  },
                  connect: {
                    type: 'param',
                    description:
                      'Whether this endpoint should receive events from connected accounts (`true`), or from your account (`false`). Defaults to `false`.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  description: {
                    type: 'param',
                    description:
                      'An optional description of what the webhook is used for.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  'enabled-events': {
                    type: 'param',
                    description:
                      "The list of events to enable for this endpoint. You may specify `['*']` to enable all events, except those that require explicit selection.",
                    required: true,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'enum',
                        enum: [
                          '*',
                          'account.application.authorized',
                          'account.application.deauthorized',
                          'account.external_account.created',
                          'account.external_account.deleted',
                          'account.external_account.updated',
                          'account.updated',
                          'application_fee.created',
                          'application_fee.refund.updated',
                          'application_fee.refunded',
                          'balance.available',
                          'billing_portal.configuration.created',
                          'billing_portal.configuration.updated',
                          'capability.updated',
                          'charge.captured',
                          'charge.dispute.closed',
                          'charge.dispute.created',
                          'charge.dispute.funds_reinstated',
                          'charge.dispute.funds_withdrawn',
                          'charge.dispute.updated',
                          'charge.expired',
                          'charge.failed',
                          'charge.pending',
                          'charge.refund.updated',
                          'charge.refunded',
                          'charge.succeeded',
                          'charge.updated',
                          'checkout.session.async_payment_failed',
                          'checkout.session.async_payment_succeeded',
                          'checkout.session.completed',
                          'checkout.session.expired',
                          'coupon.created',
                          'coupon.deleted',
                          'coupon.updated',
                          'credit_note.created',
                          'credit_note.updated',
                          'credit_note.voided',
                          'customer.created',
                          'customer.deleted',
                          'customer.discount.created',
                          'customer.discount.deleted',
                          'customer.discount.updated',
                          'customer.source.created',
                          'customer.source.deleted',
                          'customer.source.expiring',
                          'customer.source.updated',
                          'customer.subscription.created',
                          'customer.subscription.deleted',
                          'customer.subscription.pending_update_applied',
                          'customer.subscription.pending_update_expired',
                          'customer.subscription.trial_will_end',
                          'customer.subscription.updated',
                          'customer.tax_id.created',
                          'customer.tax_id.deleted',
                          'customer.tax_id.updated',
                          'customer.updated',
                          'file.created',
                          'identity.verification_session.canceled',
                          'identity.verification_session.created',
                          'identity.verification_session.processing',
                          'identity.verification_session.redacted',
                          'identity.verification_session.requires_input',
                          'identity.verification_session.verified',
                          'invoice.created',
                          'invoice.deleted',
                          'invoice.finalization_failed',
                          'invoice.finalized',
                          'invoice.marked_uncollectible',
                          'invoice.paid',
                          'invoice.payment_action_required',
                          'invoice.payment_failed',
                          'invoice.payment_succeeded',
                          'invoice.sent',
                          'invoice.upcoming',
                          'invoice.updated',
                          'invoice.voided',
                          'invoiceitem.created',
                          'invoiceitem.deleted',
                          'invoiceitem.updated',
                          'issuing_authorization.created',
                          'issuing_authorization.request',
                          'issuing_authorization.updated',
                          'issuing_card.created',
                          'issuing_card.updated',
                          'issuing_cardholder.created',
                          'issuing_cardholder.updated',
                          'issuing_dispute.closed',
                          'issuing_dispute.created',
                          'issuing_dispute.funds_reinstated',
                          'issuing_dispute.submitted',
                          'issuing_dispute.updated',
                          'issuing_transaction.created',
                          'issuing_transaction.updated',
                          'mandate.updated',
                          'order.created',
                          'order.payment_failed',
                          'order.payment_succeeded',
                          'order.updated',
                          'order_return.created',
                          'payment_intent.amount_capturable_updated',
                          'payment_intent.canceled',
                          'payment_intent.created',
                          'payment_intent.partially_funded',
                          'payment_intent.payment_failed',
                          'payment_intent.processing',
                          'payment_intent.requires_action',
                          'payment_intent.succeeded',
                          'payment_link.created',
                          'payment_link.updated',
                          'payment_method.attached',
                          'payment_method.automatically_updated',
                          'payment_method.detached',
                          'payment_method.updated',
                          'payout.canceled',
                          'payout.created',
                          'payout.failed',
                          'payout.paid',
                          'payout.updated',
                          'person.created',
                          'person.deleted',
                          'person.updated',
                          'plan.created',
                          'plan.deleted',
                          'plan.updated',
                          'price.created',
                          'price.deleted',
                          'price.updated',
                          'product.created',
                          'product.deleted',
                          'product.updated',
                          'promotion_code.created',
                          'promotion_code.updated',
                          'quote.accepted',
                          'quote.canceled',
                          'quote.created',
                          'quote.finalized',
                          'radar.early_fraud_warning.created',
                          'radar.early_fraud_warning.updated',
                          'recipient.created',
                          'recipient.deleted',
                          'recipient.updated',
                          'reporting.report_run.failed',
                          'reporting.report_run.succeeded',
                          'reporting.report_type.updated',
                          'review.closed',
                          'review.opened',
                          'setup_intent.canceled',
                          'setup_intent.created',
                          'setup_intent.requires_action',
                          'setup_intent.setup_failed',
                          'setup_intent.succeeded',
                          'sigma.scheduled_query_run.created',
                          'sku.created',
                          'sku.deleted',
                          'sku.updated',
                          'source.canceled',
                          'source.chargeable',
                          'source.failed',
                          'source.mandate_notification',
                          'source.refund_attributes_required',
                          'source.transaction.created',
                          'source.transaction.updated',
                          'subscription_schedule.aborted',
                          'subscription_schedule.canceled',
                          'subscription_schedule.completed',
                          'subscription_schedule.created',
                          'subscription_schedule.expiring',
                          'subscription_schedule.released',
                          'subscription_schedule.updated',
                          'tax_rate.created',
                          'tax_rate.updated',
                          'terminal.reader.action_failed',
                          'terminal.reader.action_succeeded',
                          'test_helpers.test_clock.advancing',
                          'test_helpers.test_clock.created',
                          'test_helpers.test_clock.deleted',
                          'test_helpers.test_clock.internal_failure',
                          'test_helpers.test_clock.ready',
                          'topup.canceled',
                          'topup.created',
                          'topup.failed',
                          'topup.reversed',
                          'topup.succeeded',
                          'transfer.created',
                          'transfer.failed',
                          'transfer.paid',
                          'transfer.reversed',
                          'transfer.updated',
                        ],
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  url: {
                    type: 'param',
                    description: 'The URL of the webhook endpoint.',
                    required: true,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: [],
                path: '/v1/webhook_endpoints',
                method: 'post',
                run: true,
              },
              retrieve: {
                type: 'command',
                description:
                  'Retrieves the webhook endpoint with the given ID.',
                params: {
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                },
                args: ['webhook_endpoint'],
                path: '/v1/webhook_endpoints/{webhook_endpoint}',
                method: 'get',
                run: true,
              },
              update: {
                type: 'command',
                description:
                  'Updates the webhook endpoint. You may edit the url, the list of enabled_events, and the status of your endpoint.',
                params: {
                  description: {
                    type: 'param',
                    description:
                      'An optional description of what the webhook is used for.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                  disabled: {
                    type: 'param',
                    description: 'Disable the webhook endpoint if set to true.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'boolean',
                    },
                  },
                  'enabled-events': {
                    type: 'param',
                    description:
                      "The list of events to enable for this endpoint. You may specify `['*']` to enable all events, except those that require explicit selection.",
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'enum',
                        enum: [
                          '*',
                          'account.application.authorized',
                          'account.application.deauthorized',
                          'account.external_account.created',
                          'account.external_account.deleted',
                          'account.external_account.updated',
                          'account.updated',
                          'application_fee.created',
                          'application_fee.refund.updated',
                          'application_fee.refunded',
                          'balance.available',
                          'billing_portal.configuration.created',
                          'billing_portal.configuration.updated',
                          'capability.updated',
                          'charge.captured',
                          'charge.dispute.closed',
                          'charge.dispute.created',
                          'charge.dispute.funds_reinstated',
                          'charge.dispute.funds_withdrawn',
                          'charge.dispute.updated',
                          'charge.expired',
                          'charge.failed',
                          'charge.pending',
                          'charge.refund.updated',
                          'charge.refunded',
                          'charge.succeeded',
                          'charge.updated',
                          'checkout.session.async_payment_failed',
                          'checkout.session.async_payment_succeeded',
                          'checkout.session.completed',
                          'checkout.session.expired',
                          'coupon.created',
                          'coupon.deleted',
                          'coupon.updated',
                          'credit_note.created',
                          'credit_note.updated',
                          'credit_note.voided',
                          'customer.created',
                          'customer.deleted',
                          'customer.discount.created',
                          'customer.discount.deleted',
                          'customer.discount.updated',
                          'customer.source.created',
                          'customer.source.deleted',
                          'customer.source.expiring',
                          'customer.source.updated',
                          'customer.subscription.created',
                          'customer.subscription.deleted',
                          'customer.subscription.pending_update_applied',
                          'customer.subscription.pending_update_expired',
                          'customer.subscription.trial_will_end',
                          'customer.subscription.updated',
                          'customer.tax_id.created',
                          'customer.tax_id.deleted',
                          'customer.tax_id.updated',
                          'customer.updated',
                          'file.created',
                          'identity.verification_session.canceled',
                          'identity.verification_session.created',
                          'identity.verification_session.processing',
                          'identity.verification_session.redacted',
                          'identity.verification_session.requires_input',
                          'identity.verification_session.verified',
                          'invoice.created',
                          'invoice.deleted',
                          'invoice.finalization_failed',
                          'invoice.finalized',
                          'invoice.marked_uncollectible',
                          'invoice.paid',
                          'invoice.payment_action_required',
                          'invoice.payment_failed',
                          'invoice.payment_succeeded',
                          'invoice.sent',
                          'invoice.upcoming',
                          'invoice.updated',
                          'invoice.voided',
                          'invoiceitem.created',
                          'invoiceitem.deleted',
                          'invoiceitem.updated',
                          'issuing_authorization.created',
                          'issuing_authorization.request',
                          'issuing_authorization.updated',
                          'issuing_card.created',
                          'issuing_card.updated',
                          'issuing_cardholder.created',
                          'issuing_cardholder.updated',
                          'issuing_dispute.closed',
                          'issuing_dispute.created',
                          'issuing_dispute.funds_reinstated',
                          'issuing_dispute.submitted',
                          'issuing_dispute.updated',
                          'issuing_transaction.created',
                          'issuing_transaction.updated',
                          'mandate.updated',
                          'order.created',
                          'order.payment_failed',
                          'order.payment_succeeded',
                          'order.updated',
                          'order_return.created',
                          'payment_intent.amount_capturable_updated',
                          'payment_intent.canceled',
                          'payment_intent.created',
                          'payment_intent.partially_funded',
                          'payment_intent.payment_failed',
                          'payment_intent.processing',
                          'payment_intent.requires_action',
                          'payment_intent.succeeded',
                          'payment_link.created',
                          'payment_link.updated',
                          'payment_method.attached',
                          'payment_method.automatically_updated',
                          'payment_method.detached',
                          'payment_method.updated',
                          'payout.canceled',
                          'payout.created',
                          'payout.failed',
                          'payout.paid',
                          'payout.updated',
                          'person.created',
                          'person.deleted',
                          'person.updated',
                          'plan.created',
                          'plan.deleted',
                          'plan.updated',
                          'price.created',
                          'price.deleted',
                          'price.updated',
                          'product.created',
                          'product.deleted',
                          'product.updated',
                          'promotion_code.created',
                          'promotion_code.updated',
                          'quote.accepted',
                          'quote.canceled',
                          'quote.created',
                          'quote.finalized',
                          'radar.early_fraud_warning.created',
                          'radar.early_fraud_warning.updated',
                          'recipient.created',
                          'recipient.deleted',
                          'recipient.updated',
                          'reporting.report_run.failed',
                          'reporting.report_run.succeeded',
                          'reporting.report_type.updated',
                          'review.closed',
                          'review.opened',
                          'setup_intent.canceled',
                          'setup_intent.created',
                          'setup_intent.requires_action',
                          'setup_intent.setup_failed',
                          'setup_intent.succeeded',
                          'sigma.scheduled_query_run.created',
                          'sku.created',
                          'sku.deleted',
                          'sku.updated',
                          'source.canceled',
                          'source.chargeable',
                          'source.failed',
                          'source.mandate_notification',
                          'source.refund_attributes_required',
                          'source.transaction.created',
                          'source.transaction.updated',
                          'subscription_schedule.aborted',
                          'subscription_schedule.canceled',
                          'subscription_schedule.completed',
                          'subscription_schedule.created',
                          'subscription_schedule.expiring',
                          'subscription_schedule.released',
                          'subscription_schedule.updated',
                          'tax_rate.created',
                          'tax_rate.updated',
                          'terminal.reader.action_failed',
                          'terminal.reader.action_succeeded',
                          'test_helpers.test_clock.advancing',
                          'test_helpers.test_clock.created',
                          'test_helpers.test_clock.deleted',
                          'test_helpers.test_clock.internal_failure',
                          'test_helpers.test_clock.ready',
                          'topup.canceled',
                          'topup.created',
                          'topup.failed',
                          'topup.reversed',
                          'topup.succeeded',
                          'transfer.created',
                          'transfer.failed',
                          'transfer.paid',
                          'transfer.reversed',
                          'transfer.updated',
                        ],
                      },
                    },
                  },
                  expand: {
                    type: 'param',
                    description:
                      'Specifies which fields in the response should be expanded.',
                    required: false,
                    input_type: {
                      shape: 'array',
                      elementType: {
                        shape: 'primitive',
                        primitive: 'string',
                      },
                    },
                  },
                  metadata: {
                    type: 'param',
                    description:
                      'Set of key-value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format. Individual keys can be unset by posting an empty value to them. All keys can be unset by posting an empty value to `metadata`.',
                    required: false,
                    input_type: {
                      shape: 'emptyStringable',
                      type: {
                        shape: 'map',
                        keyType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                        elementType: {
                          shape: 'primitive',
                          primitive: 'string',
                        },
                      },
                    },
                  },
                  url: {
                    type: 'param',
                    description: 'The URL of the webhook endpoint.',
                    required: false,
                    input_type: {
                      shape: 'primitive',
                      primitive: 'string',
                    },
                  },
                },
                args: ['webhook_endpoint'],
                path: '/v1/webhook_endpoints/{webhook_endpoint}',
                method: 'post',
                run: true,
              },
              delete: {
                type: 'command',
                description:
                  'You can also delete webhook endpoints via the webhook endpoint management page of the Stripe dashboard.',
                params: {},
                args: ['webhook_endpoint'],
                path: '/v1/webhook_endpoints/{webhook_endpoint}',
                method: 'delete',
                run: true,
              },
            },
          },
        },
      },
    },
  },
};
